// @ts-check
// Área de trabajo (canvas) del editor de diagramas (TSK-0023).
//
// Objetivo (docs/01-roadmap.md, Fase 1): recibir bloques desde la paleta
// (arrastre O teclado), renderizarlos a partir del modelo lógico
// (`editor-model.js`, TSK-0021) y permitir seleccionar/reordenar un bloque
// ya colocado tanto con puntero como con teclado.
//
// --------------------------------------------------------------------------
// TSK-0060 (DEC-027, docs/generated/decisions.md): lista vertical secuencial
// --------------------------------------------------------------------------
// Por decisión de producto explícita del usuario (DEC-027), este módulo
// SUSTITUYE el posicionamiento libre 2D de TSK-0023 (ADR-0004, "Superado por
// ADR-0005" desde esta tarea: ver docs/adr/ADR-0004-canvas-dynamic-position.md
// y docs/adr/ADR-0005-canvas-vertical-sequential.md) por un modelo de LISTA
// VERTICAL: la posición visual de cada bloque de nivel superior es
// EXACTAMENTE su índice en `programaActual.cuerpo.sentencias`, de arriba
// hacia abajo, en flujo normal de documento (sin `left`/`top`, sin
// coordenadas x/y libres asignables a un punto arbitrario del lienzo).
//
// Regla de arquitectura ACTUALIZADA (sustituye la de TSK-0023, que exigía
// que "mover" tocase ÚNICAMENTE la capa visual): en este modelo, "posición"
// ES "orden en la Secuencia", así que reordenar un bloque (con flechas de
// teclado ArrowUp/ArrowDown, o arrastrándolo verticalmente con el puntero)
// SÍ modifica el modelo lógico -- vía `moverEnSecuencia`, ya existente en
// `editor-model.js` (TSK-0021), reutilizada aquí sin reimplementarla, nunca
// reinventada con una primitiva nueva. Solo COLOCAR un bloque nuevo
// (`colocarBloque`, vía `insertarEnSecuencia`) y REORDENAR (vía
// `moverEnSecuencia`) tocan el modelo lógico; NUNCA se escribe `x`/`y` en
// ningún elemento del DOM.
//
// Motivo del cambio (DEC-027, diagnóstico de causa raíz): el bug de flechas
// desalineadas de TSK-0056 era consecuencia directa de dos sistemas de
// coordenadas independientes coexistiendo -- bloques posicionados con
// `style.left/top` dentro de `.canvas-surface` (scroll propio,
// `overflow: auto`) mientras las flechas se recalculaban con
// `getBoundingClientRect()` solo en render/mover, nunca en un evento de
// `scroll` de esa superficie. Al eliminar el posicionamiento libre (los
// bloques ahora viven en flujo normal, apilados con `display: flex;
// flex-direction: column` en `canvas.css`), la causa estructural desaparece;
// además se añade explícitamente un listener de `scroll` sobre `superficie`
// que llama a `actualizarConexiones()` (ver más abajo), porque desplazar el
// contenido SIGUE cambiando la posición relativa de cada bloque respecto al
// `<svg>` superpuesto (que vive en `.canvas`, el contenedor EXTERIOR que NO
// hace scroll) -- eso no cambia con este modelo, solo cambia que ahora hay
// UNA única fuente de posición (el orden de la lista) y el recálculo
// dispara en TODOS los eventos relevantes (render, reordenar, arrastrar,
// scroll), no solo en algunos.
//
// `editor-visual-state.js` (capa de posición x/y, TSK-0021) sigue existiendo
// SIN cambios de API porque otros módulos ya aprobados y fuera de las rutas
// permitidas de esta tarea (`block-operations.js`, `editor-controller.js`,
// `editor-persistence.js`) siguen construyendo/pasando un `EstadoVisual`
// compartido y llamando a `establecerPosicion`/`obtenerPosicion`/
// `eliminarPosicion` sobre él (p.ej. `block-operations.js` al duplicar un
// bloque, para no dejar una posición colgante). Este módulo SIGUE aceptando
// la opción `estadoVisual` (es el mismo objeto que se pasa también a
// `block-operations.js`/`editor-persistence.js`, así que mutarlo aquí es lo
// que hace que la posición sobreviva al autoguardado sin necesitar un
// callback nuevo).
//
// ACTUALIZACIÓN (TSK-0081, DEC-028, cierre del Frente B): el párrafo de
// arriba ("YA NO LO USA para posicionar nada... no llama a
// establecerPosicion/obtenerPosicion en ningún punto de este archivo") DEJA
// DE SER CIERTO desde esta tarea. Ver la sección "TSK-0081" al final de este
// bloque de comentarios de cabecera (justo antes de los `import`) para el
// mecanismo completo de arrastre libre 2D que por fin consume
// `establecerPosicion`/`obtenerPosicion`/`eliminarPosicion` de verdad.
//
// Alcance de renderizado de ESTA tarea (nota explícita, no una decisión
// oculta, heredada de TSK-0023): se renderiza únicamente la lista plana de
// sentencias de nivel superior (`programa.cuerpo.sentencias`), no el
// contenido anidado de ramas de `Condicional`/cuerpos de bucle. Nada en el
// roadmap exige que esta tarea resuelva la edición de bloques anidados.
//
// Feedback perceptible para Conector (criterio de aceptación añadido tras el
// hallazgo P2 de accessibility-reviewer en TSK-0022): `palette-view.js` ya
// documenta que `crearNodo()` devuelve `null` para ese bloque (no tiene nodo
// IR propio). Este módulo NO hardcodea ese id: cualquier `nodo === null`
// recibido en `colocarBloque` (venga de donde venga) se trata de forma
// genérica como "intención sin nodo IR insertable" y produce un aviso
// visible + anunciado, NUNCA un silencio indistinguible de un fallo.
//
// ANCLAS FIJAS Inicio/Fin (TSK-0055, DEC-026, docs/generated/decisions.md):
// por decisión de producto explícita del usuario, Inicio/Fin son dos anclas
// SIEMPRE presentes en este canvas, desde el propio montaje, primer y último
// elemento visual de la lista. No participan en absoluto en
// `colocarBloque`/`renderizar` (la lista de sentencias): se construyen una
// única vez en `crearVistaCanvas` y se insertan directamente en `seccion`,
// antes/después de `superficie`. Con este cambio (TSK-0060) su
// posicionamiento pasa del modelo libre (ya de por sí NO tenían `left`/`top`
// propio, ver TSK-0055) al de flujo de lista: siguen sin tener listener de
// `keydown` (sin reordenar) ni de `mousedown` (sin arrastre) ni ningún
// control de eliminar/duplicar: intencionalmente de solo lectura salvo el
// foco/selección. Usan como `data-node-id` los mismos ids reservados
// 'inicio'/'fin' que ya declara `connectors.js`.
//
// Ambigüedad NO resuelta en silencio (reportada también en el historial de
// la tarea y en la respuesta final del agente ejecutor): este módulo expone
// `colocarBloque(nodo, definicion, indice?)` como el punto de entrada
// equivalente tanto para "soltar" (el propio `drop` interno lo invoca tras
// parsear `dataTransfer`, calculando el índice de inserción a partir de la
// coordenada vertical del cursor respecto a los bloques ya colocados) como
// para "colocar por teclado"/API directa (sin índice: se añade al final).
// No existe todavía ningún módulo de página/controlador en el árbol (fuera
// de las rutas permitidas de esta tarea) que conecte el
// `onSeleccionarBloque` de `palette-view.js` con este método para un usuario
// real de teclado que tabula hasta la paleta y pulsa Intro: esa composición
// queda pendiente de una tarea futura, igual que en TSK-0023.
//
// Dirección de dependencias (docs/03-frontend.md §4.2): este módulo importa
// `editor-model.js` y `editor-visual-state.js` (ya existentes, TSK-0021) y
// ningún `services/*`. La integración con la paleta se produce por contrato
// de datos (`dataTransfer` con los mismos MIME types que declara
// `palette-view.js`, documentados aquí de forma redundante e intencional) y
// por la firma de `colocarBloque`. De `palette-view.js` solo se importa,
// desde DEC-180, `PALETA_BLOQUES`, para nombrar un bloque que esta vista no
// colocó (la reconstruida no lo sabe). La paleta solo depende de
// `editor-model.js`: no hay ciclo.

// --------------------------------------------------------------------------
// TSK-0061 (DEC-027): papelera de eliminación + Supr/Backspace + "duplicar"
// mínimo, sustituyendo el botón "eliminar" del panel de operaciones
// --------------------------------------------------------------------------
// Contexto de la ola (ver historial de la ficha de la tarea): esta tarea
// llega justo después de TSK-0060 (lista vertical) y antes de TSK-0062 (que
// retirará el montaje de `block-operations.js` de `editor-controller.js`).
// El criterio de aceptación de ESTA tarea exige explícitamente, sin embargo,
// que "el panel de operaciones deja de montarse en editor-controller.js" —
// y `editor-controller.js` SÍ está en las rutas permitidas del frontmatter
// de esta ficha (`tasks/ready/TSK-0061-trash-can-delete.md`), aunque un
// mensaje de contexto recibido a mitad de tarea decía lo contrario
// ("NO toques editor-controller.js, está en rutas_prohibidas"). Se registra
// aquí la contradicción explícitamente (CLAUDE.md, "no decidas
// silenciosamente"): se ha resuelto siguiendo el frontmatter de la propia
// ficha (fuente de verdad primaria explícitamente indicada por el propio
// encargo: "Lee el archivo completo... para rutas permitidas/prohibidas"),
// que además es coherente con el criterio de aceptación textual ("el panel
// deja de montarse") y con la `excepcion_reapertura` (que solo protege el
// ARCHIVO `block-operations.js`, no `editor-controller.js`). Se retira el
// montaje de `block-operations.js` en `editor-controller.js` en esta misma
// tarea (ver ese archivo); el archivo `block-operations.js` en sí NO se
// toca, tal y como exige `rutas_prohibidas`.
//
// Mecanismo de eliminación (dos vías, misma primitiva `eliminarDeSecuencia`
// de `editor-model.js`, ya usada por `block-operations.js`, reutilizada sin
// reimplementar):
//   1) Arrastrar un bloque colocado (el mismo gesto de arrastre por puntero
//      que ya reordena, ver `manejarInicioArrastre`) hasta la papelera
//      (`.canvas-trash`, un `<button>` siempre presente al final del área de
//      trabajo): si se suelta con el puntero sobre su rectángulo, se elimina
//      en vez de reordenar.
//   2) Seleccionar un bloque (foco) y pulsar Supr/Backspace, o activar la
//      papelera con clic/Intro/Espacio (semántica nativa de `<button>`, sin
//      manejador de teclado a medida) actuando sobre el bloque con el foco
//      más reciente (`idBloqueSeleccionado`, actualizado por el evento
//      `focus` de cada bloque colocado).
//
// Protección contra borrado accidental (criterio de aceptación: "se preserva
// un mecanismo de confirmación accesible equivalente" al de dos pasos que
// tenía el botón "Eliminar" de `block-operations.js`): en vez de repetir ese
// mismo patrón de dos pasos (que encaja mal con un gesto de ARRASTRE — no
// hay una "fila" que sustituir a mitad de un `mousemove`), se implementa
// DESHACER INMEDIATO: al eliminar (por cualquiera de las 2 vías) el bloque
// desaparece de inmediato, pero el `<button>` "Deshacer" (oculto el resto
// del tiempo) aparece a continuación, recibe el foco YA (el usuario no tiene
// que buscarlo) y reinserta el nodo exacto en su índice original si se
// activa. El anuncio de la live region ya existente (`estado`, aria-live
// polite) menciona expresamente la posibilidad de deshacer. Se documenta
// como decisión de diseño explícita, no oculta: un modal de confirmación
// bloquearía el propio gesto de arrastre (habría que soltar el botón antes
// de que apareciese cualquier diálogo) y el patrón de dos pasos en la fila
// (como en `block-operations.js`) no tiene una "fila" equivalente para un
// `<button>` de papelera único y fijo. El deshacer se invalida (se oculta y
// se limpia) en cuanto ocurre CUALQUIER otra acción estructural (colocar,
// reordenar, duplicar u otro borrado): solo protege la ÚLTIMA eliminación,
// nunca dos gestos consecutivos, para que su índice de reinserción siga
// siendo significativo.
//
// "Duplicar" (criterio de aceptación de ESTA tarea histórica: sigue siendo
// posible de forma mínima y accesible, sin reintroducir el panel completo
// retirado): un único `<button>` adicional por bloque colocado ("Duplicar
// {etiqueta}", reutiliza `duplicarNodo` de `editor-model.js`, la misma
// primitiva que ya usaba `block-operations.js#duplicarBloque`, nunca
// reimplementada), NO un panel aparte. Se descarta deliberadamente
// reimplementar aquí "subir"/"bajar" (ya cubiertos por ArrowUp/ArrowDown y el
// arrastre vertical de TSK-0060) y el propio "eliminar" con confirmación en
// dos pasos (sustituido por la papelera+deshacer descritos arriba): el
// criterio de esta tarea era específicamente sobre eliminar y duplicar, no
// sobre reordenar.
//
// ACTUALIZACIÓN (TSK-0076, petición directa del usuario probando la app en
// vivo: "no quiero que aparezca duplicar al lado del bloque"): el botón
// "Duplicar {etiqueta}" descrito en el párrafo anterior SE RETIRA de la UI.
// No existía ningún atajo de teclado independiente de ese botón (se
// verificó antes de decidir, ver `crearItemBloque` más abajo): la función
// `duplicarBloque` de este módulo no tenía más llamador que el `click` de
// ese `<button>`, así que se elimina también, sin dejar código muerto. No
// queda ninguna vía de duplicado de bloques colocados en este módulo tras
// esta tarea.
//
// --------------------------------------------------------------------------
// TSK-0063 (docs/generated/decisions.md#DEC-027): forma y color por tipo de
// bloque, según el estándar de referencia consultado por el coordinador
// (smartdraw.com/flowchart/simbolos-de-diagramas-de-flujo, ver historial de
// la ficha de esta tarea). Cambio PURAMENTE visual: ni el nombre accesible
// (`textContent`, ya existente) ni el modelo lógico cambian por esto. La
// forma/color son un refuerzo, nunca la única señal (WCAG 2.2 SC 1.4.1): el
// texto del bloque sigue siendo la fuente primaria.
//
// Mecanismo: cada botón de bloque colocado (`renderizar()`, más abajo) gana
// un `dataset.blockType = nodo.nodo` (el propio valor de `TIPO_NODO`, p.ej.
// "Asignacion"/"Condicional"/"Comentario") ADEMÁS de `dataset.nodeId` (que
// ya existía, es el id único de instancia, no el tipo). `canvas.css` selecciona
// por `[data-block-type="..."]` para aplicar `clip-path`/`background-color`
// sin sustituir el `<button>` nativo por ningún `<div>` con ARIA
// reimplementado (docs/03-frontend.md §8.2).
//
// Mapa tipo -> símbolo elegido, y justificación explícita de los tipos SIN
// símbolo estándar directo en la referencia consultada (ninguno se inventa
// en silencio, CLAUDE.md "no decidas silenciosamente"):
//   Entrada/Salida    -> paralelogramo (estándar: entrada/salida de datos).
//   Asignacion        -> rectángulo (estándar: proceso).
//   Operacion         -> rectángulo, MISMA convención que Asignación: no es
//                        un tipo listado en el criterio de aceptación de
//                        esta tarea, pero SÍ es un bloque colocable real de
//                        la paleta (`palette-view.js`, id "operacion"); se
//                        trata como "proceso" genérico por ser también un
//                        paso de cálculo/transformación, no una decisión ni
//                        una E/S. Documentado aquí para no dejarlo sin
//                        forma por omisión.
//   Condicional       -> rombo (estándar: decisión). Cubre tanto "Decisión"
//                        (con rama sino) como "Condición" (sin ella): ambas
//                        comparten el mismo nodo IR `Condicional`
//                        (`palette-view.js`), así que comparten forma.
//   BucleMientras/
//   BucleFinPara/
//   RepetirHasta      -> hexágono. SIN símbolo estándar directo en la
//                        referencia consultada (que solo da "preparación/
//                        límite de bucle" como uso razonable de ese
//                        símbolo, ver historial de la ficha): se reutiliza
//                        para las 3 variantes de bucle de `editor-model.js`
//                        (aunque hoy la paleta solo coloca `BucleMientras`
//                        por defecto, ver `palette-view.js`), porque
//                        semánticamente las 3 son "el mismo símbolo de
//                        flujo repetido con límite/condición".
//   Llamada           -> rectángulo con doble borde vertical ("proceso
//                        predefinido"). SIN símbolo estándar directo en la
//                        referencia consultada: se elige porque el propio
//                        estándar (fuentes de diagramas de flujo genéricas)
//                        usa precisamente ese símbolo para "llamada a un
//                        subrutina/proceso ya definido en otro lugar", que
//                        es exactamente la semántica de `Llamada` (tanto
//                        "Llamada a función" como "Subproceso" comparten
//                        este mismo nodo IR, ver `palette-view.js`).
//   Retorno           -> rectángulo simple (mismo tratamiento visual que un
//                        proceso, pero con color propio para distinguirlo).
//                        SIN símbolo estándar directo en la referencia
//                        consultada: semánticamente es un tipo de salida de
//                        función/control de flujo, no una E/S de datos del
//                        programa (eso ya lo cubre el símbolo de
//                        paralelogramo de Entrada/Salida) ni una decisión;
//                        se descarta forzarlo a un símbolo que no le
//                        corresponde y se documenta como rectángulo llano.
//   Comentario        -> "nota/anotación": rectángulo con borde discontinuo
//                        y esquina superior derecha "doblada" (pseudo-
//                        elemento `::after`). SIN símbolo estándar directo
//                        en la referencia consultada, A PROPÓSITO: un
//                        comentario NO es un paso de flujo real (no se
//                        ejecuta, es metadata), así que se le da
//                        deliberadamente un símbolo que NO parece un paso
//                        de proceso normal, para no confundir al alumno
//                        haciéndole creer que es una instrucción ejecutable.
//   Conector          -> círculo pequeño (estándar: conector). Documentado
//                        aquí de forma EXPLÍCITA como una contradicción no
//                        resuelta en silencio (CLAUDE.md): el criterio de
//                        aceptación de esta tarea pide forma/color para
//                        "Conector", pero (ver comentario de cabecera más
//                        arriba, y `palette-view.js`) `crearNodo()` de
//                        "Conector" devuelve `null` y `colocarBloque` (más
//                        abajo) NUNCA inserta ningún elemento `.canvas-block`
//                        para un `nodo === null`: no existe, ni puede
//                        existir con la arquitectura actual (ADR-0005,
//                        `Secuencia`/adyacencia implícita), ningún botón de
//                        bloque colocado con `data-block-type="Conector"`
//                        que renderizar. La regla CSS para ese selector se
//                        define de todos modos en `canvas.css` (documentación
//                        viva de la convención elegida, y por si una tarea
//                        futura cambia esa arquitectura), pero es, hoy,
//                        inalcanzable en este módulo. No se fabrica ningún
//                        nodo/elemento artificial solo para "cumplir la
//                        letra" del criterio: sería exactamente el mismo
//                        error que architecture-reviewer ya rechazó en
//                        TSK-0022 (ver comentario de `palette-view.js` sobre
//                        "Conector").
//
// Anclas fijas Inicio/Fin (`.canvas-anchor`, `diagram-editor.css`, en rutas
// permitidas de esta tarea): óvalo/píldora (estándar: inicio/fin de
// diagrama), ya con `border-radius` muy alto en vez de `clip-path` (una
// píldora no necesita recortar esquinas con un polígono, un radio de borde
// mayor que la mitad de la altura ya basta).
//
// --------------------------------------------------------------------------
// TSK-0068 (petición directa del usuario, 2026-07-17): contenido real
// visible dentro del bloque + nombre identificador corto opcional
// --------------------------------------------------------------------------
// Hasta esta tarea, `renderizar()` (más abajo) mostraba SIEMPRE la etiqueta
// genérica fija del tipo (p.ej. "Asignación"), fijada una única vez al
// colocar el bloque (`etiquetasPorId`, ver más abajo) y nunca el valor real
// de ningún campo. El usuario, probando la app en vivo, pidió explícitamente
// ver el contenido real (p.ej. "edad" en vez de la palabra "variable").
//
// Alcance del "contenido real" (`contenidoRealDeNodo`, más abajo): en esta
// tarea (TSK-0068) cubría SOLO `Entrada`/`Asignacion` (nombre de variable) y
// `Condicional`/`BucleMientras`/`RepetirHasta` (condición, como texto corto
// vía `textoDeExpresion`) -- deliberadamente NO se extendía a `Comentario`/
// `Llamada`/`Salida`/`Retorno` aunque también tuvieran un valor real
// "mostrable", para no reescribir la suite de pruebas ya aprobada que
// localizaba esos bloques por su nombre accesible genérico
// (`getByRole(..., { name: 'Comentario' })`, docenas de veces), ampliando el
// alcance por encima del criterio de aceptación literal de aquella ficha.
//
// TSK-0213 (rechazo real y fundamentado de accessibility-reviewer,
// 2026-07-24, ver historial de la ficha): al eliminar `nombreCorto` (campo
// transversal, más abajo) desapareció la ÚNICA forma de distinguir por
// nombre accesible dos bloques del MISMO tipo sin contenido cubierto aquí
// (p.ej. dos "Comentario" con textos distintos, o dos "Llamada" a funciones
// distintas quedaban ambos con el nombre accesible genérico del tipo) --
// revierte sin registrarlo el propósito original de TSK-0068. Se extiende
// `contenidoRealDeNodo` a los 4 tipos restantes de `CAMPOS_POR_TIPO`
// (`block-content-panel.js`) que tienen contenido real editable:
// `Comentario.texto`, `Llamada.nombre_funcion` (texto plano, mismo criterio
// que `variable`) y `Salida.expresion`/`Retorno.expresion` (vía
// `textoDeExpresion`, mismo criterio que `Condicional.condicion`). Esto
// exige actualizar los tests que localizaban estos bloques por su etiqueta
// genérica cuando su campo real difiere de ella (ver tests actualizados);
// se acepta ese coste porque el criterio "un bloque nunca puede quedar
// indistinguible de otro del mismo tipo para un lector de pantalla" pesa
// más que evitar tocar tests. DEC-129 añade `BucleFinPara` («para i de 1 a
// 10»). Cualquier tipo fuera de este mapa (sin ningún campo editable en
// `CAMPOS_POR_TIPO`, p.ej. `Declaracion`) sigue mostrando su etiqueta
// genérica exactamente como antes.
//
// `textoDeExpresion` es una función PURA local, deliberadamente NO una
// reutilización de `expression-editor.js` (en `rutas_prohibidas` de esta
// ficha, y además ese módulo exporta solo un widget interactivo completo,
// no una función de texto plano): un renderizador de solo lectura mínimo
// (Literal/ReferenciaVariable/Operacion/AccesoIndice) suficiente para un
// texto corto tipo "edad > 18" dentro de un bloque.
//
// Truncado accesible (criterio de aceptación: "con truncado accesible si es
// larga" / "sin cortar contenido crítico sin indicación... de que hay más,
// p.ej. mediante title/aria-label con el valor completo") -- CONTRADICCIÓN
// REGISTRADA EXPLÍCITAMENTE (CLAUDE.md, "no decidas silenciosamente") frente
// a una decisión de producto previa y explícita (TSK-0071, comentario de
// cabecera de `canvas.css`, `.canvas-block`: "El texto sigue envolviendo en
// varias líneas si no cabe... no se trunca contenido, criterio de aceptación
// explícito"): se resuelve SIN pisar esa decisión -- `canvas.css` NO gana
// ningún `text-overflow`/`white-space: nowrap` nuevo (el texto que SÍ quepa
// sigue envolviendo en varias líneas, exactamente igual que antes). El
// "truncado" de ESTA tarea ocurre únicamente a nivel de STRING (JS, no CSS)
// y solo para expresiones realmente largas (`LIMITE_CARACTERES_CONTENIDO`,
// más abajo): se acorta el propio texto visible con una elipsis, y el
// `aria-label` del botón siempre lleva el texto COMPLETO sin truncar (no
// condicionado a si hubo truncado o no, para no depender de una rama de
// lógica adicional que pueda desincronizarse), así que un lector de pantalla
// nunca pierde información aunque el texto visible se acorte.
//
// TSK-0068 introdujo aquí un campo transversal `nombreCorto` (identificador
// corto opcional, guardado como campo EXTRA sobre el nodo del modelo) y su
// renderizado como un `<span>` discreto adicional dentro del bloque. TSK-
// 0213 (petición directa del usuario, 2026-07-24) lo elimina por completo:
// `crearItemBloque` (más abajo) ya NO lee ni renderiza `nodo.nombreCorto` --
// el nombre accesible/visible del bloque vuelve a ser únicamente
// `contenidoRealDeNodo(nodo) ?? etiqueta` (el mismo criterio de "contenido
// real" ya existente para `Entrada`/`Asignacion`/`Condicional`/bucles, ver
// más abajo), sin ningún span adicional. `ir-serialization.js` conserva su
// filtro de `nombreCorto` (con el comentario actualizado en ese archivo)
// solo como salvaguarda defensiva de compatibilidad con datos ya guardados
// en el `localStorage` de un usuario real antes de este cambio.
//
// Primer intento de TSK-0213 rechazado por accessibility-reviewer: dejar
// `contenidoRealDeNodo` limitado al alcance original de TSK-0068 (arriba)
// significaba que dos bloques del mismo tipo sin ese campo (p.ej. dos
// "Comentario") volvían a compartir el mismo nombre accesible genérico, sin
// forma de distinguirlos para un lector de pantalla -- regresión real del
// propósito de TSK-0068 al quitar `nombreCorto`. Arreglo: `contenidoRealDeNodo`
// se extiende (ver "Alcance del contenido real" arriba) a `Comentario`/
// `Llamada`/`Salida`/`Retorno`, cerrando el hueco sin reintroducir el campo
// transversal `nombreCorto`.
// --------------------------------------------------------------------------
// TSK-0072 (DEC-028, Frente B, primera tarea): colocar/reordenar/eliminar
// bloques DENTRO de las ramas de un Condicional (ramaVerdadero/ramaFalso) y
// del cuerpo de un Bucle, no solo en la Secuencia raíz.
// --------------------------------------------------------------------------
// Bug de origen (reportado por el usuario en vivo, 2026-07-17): un
// Condicional colocado en el canvas nunca ramificaba de verdad -- cualquier
// bloque colocado "después" se añadía como hermano en `programa.cuerpo`,
// nunca dentro de `rama_si`/`rama_sino` (que YA existían como nodos
// `Secuencia` propios desde que se coloca el Condicional, ver
// `palette-view.js#crearCondicional`/`crearBucleMientras`: siempre se crean
// con `ramaSi`/`ramaSino`/`cuerpo` ya como `crearSecuencia()` vacías, jamás
// `null` salvo `rama_sino` de una "Condición" sin sino). El motor de
// ejecución del backend ya recorría esas ramas recursivamente: el bug era
// puramente de la capa de colocación de este módulo, que solo sabía escribir
// en `programaActual.cuerpo`.
//
// Solución (reutilización, no reimplementación, docs/03-frontend.md,
// CLAUDE.md): este módulo ya NO llama a `insertarEnSecuencia`/
// `eliminarDeSecuencia`/`moverEnSecuencia` (de `editor-model.js`)
// directamente sobre `programaActual.cuerpo` -- las tres operaciones
// estructurales (colocar/eliminar/mover) pasan ahora, de forma uniforme
// tanto para la Secuencia raíz como para cualquier rama/cuerpo anidado, por
// `conectarBloque`/`desconectarBloque`/`moverBloqueEnContenedor` de
// `connectors.js` (ya existentes, TSK-0025/Ola 7 tras TSK-0062, no
// modificadas aquí: `connectors.js` está en `rutas_prohibidas` de esta
// tarea, se CONSUME por su API pública). La Secuencia raíz se trata como "un
// contenedor más" (`contenedorId = programaActual.cuerpo.id`, ver
// `idContenedorRaiz()`): esto UNIFICA el código de nivel superior y el
// anidado en una única implementación, en vez de duplicar la lógica de
// inserción/eliminación/reordenado una vez por nivel (que sería exactamente
// el tipo de "reimplementar su lógica en canvas-view.js" que el criterio de
// aceptación prohíbe explícitamente).
//
// Renderizado recursivo (`renderizar()`/`crearItemBloque()`/
// `crearZonaRama()`, más abajo): cada bloque colocado de tipo `Condicional`
// gana una o dos "zonas de rama" propias (`rama_si` siempre; `rama_sino`
// solo si no es `null`, ver "Condición" vs "Decisión" en `palette-view.js`);
// cada bloque de tipo bucle (`BucleMientras`/`BucleFinPara`/`RepetirHasta`)
// gana una zona de "cuerpo". Cada zona es un `<section aria-label="...">`
// con su propia lista `<ul>` (mismo mecanismo de colocar/reordenar/eliminar
// que la lista raíz: `dragover`/`drop` propios, botones con `keydown`/
// `mousedown` propios), anidada dentro del `<li>` del bloque contenedor --
// la recursión de `crearItemBloque` es exactamente lo que permite el
// anidamiento arbitrario (un Condicional dentro de la rama de otro
// Condicional, o un Bucle dentro de un Condicional, etc., criterio de
// aceptación 5): no hay ningún límite de profundidad codificado a mano,
// simplemente porque cada zona vuelve a invocar la misma función que ya
// renderiza el nivel raíz.
//
// Alcance visual DELIBERADAMENTE simple (frontmatter de la tarea: "aunque
// sea una lista indentada simple por ahora"): cada zona de rama es una lista
// indentada bajo el bloque que la contiene, apilada verticalmente (rama
// verdadero encima de rama falso si hay dos), NUNCA columnas divergentes con
// flechas -- ese diseño visual completo es una tarea posterior
// (CAP-EDITOR-ARROW-SYNC-FIX). La capa de flechas SVG existente
// (`actualizarConexiones()`) tampoco se extiende a las ramas: sigue
// dibujando únicamente Inicio -> secuencia raíz -> Fin, exactamente igual
// que antes de esta tarea (dibujar flechas dentro de una rama sin el diseño
// de columnas final produciría líneas visualmente engañosas que una tarea
// posterior tendría que rehacer de todos modos).
//
// Ambigüedad NO resuelta en silencio (CLAUDE.md, "no decidas
// silenciosamente"): esta tarea NO implementa mover un bloque YA colocado
// DESDE un contenedor HACIA otro distinto (p.ej. arrastrar un bloque de la
// raíz para soltarlo dentro de una rama). El arrastre de reordenado
// (`manejarInicioArrastre`) calcula el índice de destino y llama a
// `moverBloqueEnContenedor` siempre con el `contenedorId` ORIGINAL del
// bloque arrastrado (fijado al iniciar el gesto): soltarlo visualmente sobre
// la lista de otro contenedor no lo traslada allí, solo reordena dentro de
// su propio contenedor. El criterio de aceptación de esta ficha exige
// "colocar/reordenar/eliminar... dentro de una rama" y "un Condicional/Bucle
// puede colocarse DENTRO de la rama de otro", ambos ya cubiertos (colocar
// vía drag desde la paleta o vía la API directa vale para cualquier
// contenedor, incluidas ramas anidadas); no exige explícitamente mover un
// bloque YA CONECTADO de un contenedor a otro distinto arrastrándolo -- se
// deja fuera para no ampliar el alcance más allá de lo pedido, documentado
// aquí para una tarea futura que sí quiera cubrirlo explícitamente.
// --------------------------------------------------------------------------
// TSK-0073 (DEC-028, Frente B, segunda tarea): flechas V/F hacia las ramas +
// corrección de RAÍZ del desajuste de flechas al hacer scroll.
// --------------------------------------------------------------------------
// Motivo (frontmatter de la tarea, docs/generated/decisions.md#DEC-028): la
// capa `capaConexiones` (ver más abajo) vivía en `seccion` (`.canvas`, el
// contenedor EXTERIOR sin scroll propio), superpuesta a `superficie`
// (`.canvas-surface`, que SÍ tiene su propio `overflow-y: auto`), y solo se
// realineaba reaccionando a un evento `scroll` de `superficie` que
// recalculaba TODAS las coordenadas con `getBoundingClientRect()` -- el mismo
// patrón fragil que TSK-0066 (pendiente, nunca confirmada en un navegador
// real) sospechaba como causa de que las flechas no se vieran bien fuera de
// jsdom. Esta tarea aplica la corrección de raíz: `capaConexiones` pasa a ser
// HIJA directa de `superficie` (el mismo contenedor con scroll que los
// bloques), dimensionada al contenido COMPLETO (`superficie.scrollWidth`/
// `scrollHeight`, no el alto/ancho VISIBLE), con coordenadas calculadas en el
// sistema de referencia PROPIO de `superficie` (ver `puntoCentral`, más
// abajo: resta el `getBoundingClientRect` de `superficie` y SUMA de vuelta su
// `scrollTop`/`scrollLeft`, lo que da una coordenada estable
// independientemente del desplazamiento actual). Consecuencia directa: el
// listener de `scroll` que recalculaba en cada evento YA NO ES NECESARIO
// (criterio de aceptación explícito "se elimina... el listener... ya que deja
// de ser necesario") -- se ELIMINA por completo (no se deja un listener
// vacío ni comentado): el propio navegador desplaza `capaConexiones` junto
// con los bloques de forma nativa, porque ambos son ahora contenido real del
// MISMO elemento con scroll.
//
// Contradicción registrada y resuelta explícitamente (CLAUDE.md, "no decidas
// silenciosamente"): las anclas fijas Inicio/Fin (TSK-0055, DEC-026) vivían
// como hermanas de `superficie` dentro de `seccion` (fuera de su scroll,
// "siempre visibles"), no como hijas de `superficie`. Con `capaConexiones`
// ahora recortada por el `overflow-y: auto` de `superficie` (igual que
// cualquier otro contenido suyo), una línea hacia un ancla que siga viviendo
// FUERA de `superficie` quedaría recortada/invisible en cuanto excediera el
// área visible actual -- literalmente el mismo bug que esta tarea corrige,
// reintroducido solo para Inicio/Fin. Se resuelve moviendo Inicio/Fin DENTRO
// de `superficie` (cada una envuelta en su propio `<li>` persistente,
// `liAnclaInicio`/`liAnclaFin` más abajo, creados una única vez y
// reutilizados entre renders -- nunca recreados, para no romper la garantía
// ya aprobada en TSK-0055 de "misma referencia de nodo DOM tras
// renderizar()"): es coherente con la propia descripción original de DEC-026
// ("primer y último elemento visual de LA LISTA"), y es la única forma de que
// las anclas compartan el mismo sistema de coordenadas de scroll que el resto
// de la capa de flechas. Efecto colateral visible, documentado aquí a
// propósito: Inicio/Fin ya NO permanecen fijas en pantalla al hacer scroll
// (antes sí, por vivir fuera del contenedor con scroll) -- ahora se
// desplazan junto con el resto del diagrama, como cualquier otro elemento de
// la lista. Ninguna otra propiedad de las anclas cambia (siguen sin
// `left`/`top` propios, sin listeners de reordenar/eliminar/duplicar, ver
// TSK-0055 más arriba).
//
// Flechas nuevas V/F y de retorno (`dibujarCadena`, más abajo, recursiva):
// generaliza el mismo mecanismo que ya dibujaba Inicio -> secuencia raíz ->
// Fin (TSK-0056/0060) a CUALQUIER contenedor (rama_si/rama_sino/cuerpo,
// arbitrariamente anidado, mismo patrón de recursión sin límite de
// profundidad que `crearItemBloque`/`crearZonaRama` de TSK-0072): para un
// Condicional, una flecha etiquetada "V" hacia el primer bloque de rama_si (o
// hacia su indicador de rama vacía si no tiene contenido, ver
// `indicadoresVaciosPorContenedor`) y, si existe rama_sino, otra etiquetada
// "F" hacia ella; desde el último bloque de cada rama (o su indicador vacío),
// una flecha de convergencia hacia el bloque siguiente al Condicional EN SU
// PROPIO CONTENEDOR (nunca hacia un contenedor exterior: si el Condicional es
// la última sentencia de su contenedor, no hay flecha de convergencia, "si lo
// hay" del criterio de aceptación literal). Para un Bucle, una flecha (sin
// etiqueta) hacia el primer bloque de su cuerpo y, si el cuerpo tiene
// contenido, una flecha de retorno (`canvas-connection-line-loopback`, estilo
// discontinuo) desde su último bloque de vuelta al propio Bucle; un cuerpo
// vacío no dibuja ninguna flecha de bucle (no hay nada que recorrer ni a lo
// que volver).
//
// Decisión de alcance DELIBERADA, no oculta: esta tarea NO suprime la flecha
// "ordinaria" ya existente entre un Condicional/Bucle y su siguiente hermano
// en la MISMA secuencia (la que ya dibujaba `dibujarCadena` para cualquier
// par consecutivo, herencia de TSK-0056/0060/0072). Suprimirla exigiría
// decidir, para cada tipo de nodo, si su "salida ordinaria" debe reemplazarse
// SIEMPRE por las flechas de convergencia de sus ramas -- un rediseño mayor
// del recorrido de flechas fuera del criterio de aceptación literal de esta
// ficha (que solo pide AÑADIR las flechas V/F/retorno, nunca retirar las
// existentes). Consecuencia visual conocida: un Condicional con contenido en
// ambas ramas muestra, a la vez, la flecha ordinaria directa hacia su
// siguiente hermano Y las flechas de convergencia desde el final de cada
// rama hacia ese mismo destino -- una redundancia cosmética documentada
// aquí, no un error, que una tarea futura de diseño visual final (columnas
// divergentes reales, ya mencionada en el comentario de cabecera de
// TSK-0072) podría resolver rediseñando el recorrido por completo.
// --------------------------------------------------------------------------
// TSK-0074: fix de flechas invisibles (timing de conexión al DOM).
// --------------------------------------------------------------------------
// Bug real detectado por el usuario en un navegador de verdad (no en jsdom,
// ver comentario de cabecera "TSK-0073" sobre por qué jsdom nunca lo habría
// detectado): NINGUNA flecha se veía tras TSK-0073, pese a que su
// razonamiento de código era correcto. Causa raíz: `renderizar()` -> por
// tanto `actualizarConexiones()` -> por tanto `puntoCentral`/
// `dimensionarCapaConexiones` (más abajo, `getBoundingClientRect`/
// `scrollWidth`/`scrollHeight`) se ejecutan de forma SÍNCRONA aquí mismo,
// dentro de `crearVistaCanvas()`, ANTES de que `seccion` se inserte en el
// `document` real (eso ocurre después, en `editor-controller.js`, fuera de
// este módulo). Un subárbol desconectado del `document` no tiene layout: un
// navegador real devuelve 0 para todas esas medidas, así que la primera
// (y, sin este fix, ÚNICA) pasada de cálculo dibuja todo en (0,0)-(0,0).
// Se resuelve con un `ResizeObserver` sobre `superficie` (ver más abajo,
// junto a `renderizar()`) que repite `actualizarConexiones()` en cuanto el
// navegador calcula layout real (tamaño deja de ser 0x0) y en cualquier
// resize posterior -- sin depender de que ningún módulo externo llame a nada
// explícito tras el attach real, para no ampliar el acoplamiento entre
// módulos ni las rutas permitidas de esta tarea.
// --------------------------------------------------------------------------
// TSK-0081 (DEC-028, cierre del Frente B): arrastre libre 2D real de
// cualquier bloque colocado, cableando por fin `editor-visual-state.js`
// (`EstadoVisual`, construido en Ola 7, sin consumidor real desde que
// TSK-0060/DEC-027 lo dejó de usar, ver "ACTUALIZACIÓN (TSK-0081...)" más
// arriba en este mismo bloque de comentarios) con interacción real.
// --------------------------------------------------------------------------
//
// Separación estructura lógica / posición visual (DEC-028, síntesis ya
// resuelta, no reabierta aquí): arrastrar un bloque NUNCA llama a
// `conectarBloque`/`desconectarBloque`/`moverBloqueEnContenedor`
// (`connectors.js`) ni toca `programaActual` -- solo lee/escribe
// `establecerPosicion`/`obtenerPosicion`/`eliminarPosicion` sobre el
// `EstadoVisual` compartido. El ORDEN en `programaActual.cuerpo.sentencias`
// (o el de cualquier rama/cuerpo anidado) sigue siendo la ÚNICA fuente de
// verdad para qué conecta con qué (`actualizarConexiones`, sin cambios de
// arquitectura en esta tarea, solo de layout): una flecha se sigue dibujando
// entre dos nodos ESTRUCTURALMENTE conectados, usando la posición VISUAL
// actual de ambos extremos.
//
// Mecanismo elegido: TRANSFORM, no `left`/`top`/`position: absolute`. Cada
// `<li class="canvas-block-item">` (root o anidado dentro de una rama, ver
// `crearItemBloque`) sigue viviendo en el flujo normal de `display: flex;
// flex-direction: column` de su `.canvas-surface` contenedora (raíz o
// `.canvas-branch-surface`, TSK-0072) -- eso es lo que preserva, SIN
// reescribir ninguna otra parte de este módulo, el resto del layout ya
// aprobado (`ResizeObserver`/TSK-0074, indicador de rama vacía/TSK-0073,
// indentación de ramas/TSK-0072, formas por tipo/TSK-0063...). Encima de esa
// posición de flujo (su "posición por defecto", DEC-028 síntesis: "posición
// automática por defecto si el alumno no la mueve") se aplica un
// `transform: translate(Xpx, Ypx)` con el desplazamiento guardado en
// `EstadoVisual` para ese id (`aplicarTransformPosicion`, más abajo): un
// bloque sin posición guardada (o con `{x:0,y:0}`) se ve exactamente en su
// posición de flujo de siempre; arrastrarlo lo desplaza visualmente sin
// tocar el flujo/orden de sus hermanos. Como el `transform` es un efecto de
// PINTADO (no de layout), `getBoundingClientRect()` -- que ya usa
// `puntoCentral`/`dibujarCadena` para dibujar las flechas -- ya reflejaba
// siempre la posición REAL renderizada, incluida cualquier transformación
// CSS heredada de ancestros: las flechas siguen al bloque arrastrado SIN
// ningún cambio en `actualizarConexiones`/`puntoCentral`/`crearLinea` (ya
// existentes, TSK-0073), solo llamando a `actualizarConexiones()` de nuevo
// tras cada cambio de posición, exactamente como ya hacía cualquier
// reordenado.
//
// Limitación conocida y documentada, no oculta (trade-off deliberado dado el
// alcance/tiempo de esta tarea): al no retirar el bloque del flujo normal
// (evitar `position: absolute` evita tener que resolver aparte el tamaño
// mínimo de scroll/`scrollWidth`/`scrollHeight` de un contenedor cuyo
// contenido ya no aporta tamaño de layout), el bloque arrastrado deja un
// hueco vacío del tamaño de su caja en su posición de flujo original,
// mientras se ve pintado en otro punto vía `transform`. Es un compromiso de
// implementación conocido (no un error): el diseño visual "sin huecos" de
// columnas divergentes completas sigue siendo, como ya documentaba TSK-0072/
// TSK-0073, una tarea de diseño visual posterior (CAP-EDITOR-ARROW-SYNC-FIX).
//
// Reemplazo del arrastre por puntero anterior (contradicción con TSK-0060
// registrada y resuelta explícitamente, CLAUDE.md "no decidas
// silenciosamente"): antes de esta tarea, `mousedown` sobre un bloque
// colocado iniciaba un REORDENADO estructural vertical (`moverBloqueEnContenedor`
// vía `reordenarBloque`, ver la función `manejarInicioArrastre` que existía
// hasta esta tarea). El criterio de aceptación de ESTA tarea exige
// literalmente que el arrastre con el ratón mueva la posición VISUAL x/y
// libremente, y la nota de contexto recibida junto al encargo es explícita:
// "mover un bloque con arrastre libre NUNCA debe cambiar su posición en el
// árbol de Secuencias... reordenar/mover de rama ya existe por otro
// mecanismo... no lo dupliques ni lo mezcles con el arrastre libre de
// posición". Mezclar ambos gestos en el mismo `mousedown` (p.ej. con un
// modificador para elegir uno u otro) se descarta a propósito: complicaría
// el gesto principal sin necesidad, cuando el criterio de aceptación de esta
// ficha pide el arrastre LIBRE como comportamiento por defecto del ratón. Se
// resuelve por tanto retirando el reordenado-por-arrastre-de-ratón
// (`manejarInicioArrastre` original, con su índice de destino calculado por
// `calcularIndiceObjetivo`) y sustituyéndolo por `manejarInicioArrastreLibre`
// (más abajo): el reordenado estructural sigue disponible en su totalidad,
// sin perder ninguna capacidad, por teclado (`ArrowUp`/`ArrowDown` sin
// modificador, sin cambios de comportamiento en esta tarea) -- el criterio
// de aceptación de TSK-0060 nunca exigió que el reordenado tuviera que ser
// posible con AMBOS métodos de entrada a la vez, solo que hubiera un
// "equivalente de teclado" (WCAG 2.1.1), que ya existía y sigue existiendo
// sin cambios. `frontend/tests/unit/diagram-editor/canvas-interaction.spec.js`
// (en rutas permitidas de esta tarea) se actualiza en consecuencia: los
// tests que ejercitaban el reordenado-por-arrastre-de-ratón se sustituyen
// por tests equivalentes del nuevo arrastre libre 2D, sin perder cobertura
// del reordenado-por-teclado (intacto) ni de la papelera-por-arrastre
// (conservada, ver más abajo).
//
// Papelera por arrastre (TSK-0061) CONSERVADA sin cambios de comportamiento
// observable: soltar un bloque sobre `.canvas-trash` sigue eliminándolo
// (`eliminarBloque`, reutilizada sin cambios) en vez de reposicionarlo --
// eliminar no es "reordenar la Secuencia" en el sentido que DEC-028 protege
// (no cambia qué bloque ocupa qué índice de ningún hermano), así que no
// entra en conflicto con la regla "el arrastre libre no toca la estructura
// lógica": es la MISMA operación estructural explícita que ya podía
// activarse por teclado (Supr/Backspace) o clic en la papelera, solo con un
// tercer disparador (arrastrar-y-soltar-sobre-ella) que ya existía antes de
// esta tarea y se conserva intacto.
//
// Límites del área de arrastre (criterio de aceptación: "no se puede soltar
// un bloque fuera del `canvas-surface`"): `limitarDentroDelContenedor` (más
// abajo) usa `item.offsetLeft`/`offsetTop`/`offsetWidth`/`offsetHeight`
// (deliberadamente NO `getBoundingClientRect`, que SÍ refleja cualquier
// `transform` ya aplicado -- usar el rect transformado para calcular el
// límite del PRÓXIMO transform sería circular) junto con
// `item.offsetParent.scrollWidth`/`scrollHeight` (el contenedor `.canvas-
// surface` MÁS CERCANO, raíz o de una rama/cuerpo anidada: cada zona de rama
// ya declara su propio `position: relative` -- la misma clase `.canvas-
// surface` que la lista raíz, ver `crearZonaRama` -- así que ya es el
// `offsetParent` correcto sin ningún cambio adicional de CSS) para clampar
// el desplazamiento propuesto de modo que la caja de flujo NATURAL del
// bloque (offsetLeft/Top, invariante frente a `transform`, por especificación
// CSSOM View) más el desplazamiento propuesto siga cabiendo dentro de
// `[0, scrollWidth] x [0, scrollHeight]` de su propio contenedor. Un bloque
// dentro de una rama, por tanto, se limita al área de ESA rama (su propio
// `.canvas-branch-surface`), no al área de la lista raíz completa -- lectura
// razonable de "el área de scroll del canvas" para un contenedor que, como
// documenta TSK-0072/TSK-0073, es en sí mismo otra "superficie de
// colocación" (misma clase CSS, mismo mecanismo de colocar/reordenar/
// eliminar que la raíz).
//
// ACTUALIZACIÓN (TSK-0208, reporte directo del usuario tras probar TSK-0207:
// "se pueden mover, pero mínimamente, necesito más rango"): el párrafo de
// arriba seguía siendo cierto pero incompleto -- `[0, scrollWidth] x [0,
// scrollHeight]` es casi siempre `[0, tamaño natural del contenido]`, porque
// `.canvas-surface`/`.canvas-branch-surface` ya NO tienen scroll propio
// (retirado su `max-height`/`overflow-y: auto`, ver esa regla en canvas.css)
// y `transform` (el mecanismo de movimiento elegido arriba) nunca agranda el
// `scrollWidth`/`scrollHeight` de su padre por especificación CSSOM View. El
// margen real disponible era, por tanto, prácticamente nulo. Ver el
// comentario de cabecera de `limitarDentroDelContenedor`/
// `reservarEspacioHaciaDelante` (más abajo) para el margen añadido
// (`RANGO_EXTRA_ARRASTRE_LIBRE_PX`): hacia delante (`maxX`/`maxY`) respaldado
// por espacio real reservado (`reservarEspacioHaciaDelante`); hacia atrás
// (`minX`/`minY`) sin reserva real equivalente, pero ACOTADO (rechazo real de
// `accessibility-reviewer` a la primera versión de esta tarea, que lo ampliaba
// sin más y podía dejar un elemento en una coordenada de documento negativa e
// inalcanzable por scroll) al espacio de documento NATURALMENTE disponible
// antes del contenedor (`posicionAbsolutaDeDocumento`), de modo que la
// posición final nunca cae por debajo de la coordenada de documento 0.
//
// Rendimiento (criterio de aceptación: "documentar la estrategia elegida"):
// SIN `requestAnimationFrame`. Cada `mousemove` real (tras superar un umbral
// de 3px de movimiento total desde el `mousedown`, que también sirve para
// distinguir un arrastre real de un clic simple, mismo propósito que la
// bandera `seMovio` que ya existía en el arrastre anterior) recalcula el
// `transform` propuesto, lo clampa y llama a `actualizarConexiones()` de
// forma síncrona, una vez por evento. Se descarta `requestAnimationFrame` a
// propósito por dos motivos documentados, no una omisión: (1) el propio
// navegador ya limita la frecuencia de entrega de `mousemove` a su tasa de
// sondeo real (no hay un flujo de eventos "infinito" que desbordar), así que
// agrupar por frame con rAF no reduce de forma notable el trabajo total en
// la práctica para el volumen de bloques esperado en un programa educativo
// (decenas, no miles, de nodos); (2) introducir rAF complicaría
// significativamente el determinismo de los tests de este módulo en jsdom
// (que no implementa un scheduler de rAF real por defecto, a diferencia de
// `mousemove`/`mouseup`, que los tests ya despachan de forma síncrona y
// determinista, ver `canvas-interaction.spec.js`) sin una ganancia de
// rendimiento medible que lo justifique en este alcance. Si un futuro
// informe de rendimiento real demuestra que hace falta, es un cambio
// aislado a `manejarInicioArrastreLibre` (más abajo), no a la arquitectura
// de este módulo.
//
// Teclado (WCAG 2.5.7 "Dragging Movements", obligatorio, no opcional):
// `ArrowUp`/`ArrowDown` SIN modificador ya estaban ocupadas por el
// reordenado ESTRUCTURAL (TSK-0060, sin cambios en esta tarea, ver arriba).
// Reutilizar las mismas teclas para el movimiento VISUAL sería ambiguo (¿qué
// hace `ArrowDown` sobre un bloque: bajarlo un puesto en la Secuencia, o
// moverlo unos píxeles hacia abajo?) y mezclaría precisamente los dos
// conceptos que DEC-028 exige mantener separados. Se elige el modificador
// `Alt` (`Alt+ArrowUp/ArrowDown/ArrowLeft/ArrowRight`, las 4 direcciones,
// ver `TECLA_A_DELTA_XY` más abajo) para el movimiento visual libre en
// incrementos de `INCREMENTO_TECLADO_POSICION_LIBRE` píxeles, con el mismo
// clamp de límites que el arrastre por ratón (`limitarDentroDelContenedor`,
// reutilizada sin duplicar lógica) y el mismo anuncio perceptible por lector
// de pantalla (`anunciar`, live region `aria-live="polite"` ya existente,
// sin ninguna región nueva) que ya usa el resto de acciones de este módulo,
// mencionando explícitamente que es un movimiento VISUAL que no cambia el
// orden del programa (para no confundir a quien ya conoce `ArrowUp`/
// `ArrowDown` sin modificador como reordenado). `Ctrl`/`Cmd+Arrow` se
// descartan porque en varios navegadores/SO ya tienen un significado nativo
// de navegación (cambiar de pestaña, mover el cursor por palabras) que
// `preventDefault` no siempre puede interceptar de forma fiable en todas las
// plataformas; `Alt+Arrow` es el modificador que menos combinaciones nativas
// de navegador choca en la práctica y es el que sugería explícitamente la
// nota de contexto de esta tarea ("Alt/Ctrl+flechas si prefieres reservar
// las flechas solas para otra cosa").
// --------------------------------------------------------------------------
// TSK-0194 (DEC-069 fase 2, docs/generated/decisions.md#DEC-069/#DEC-070):
// capa interactiva de flechas (anclas de puerto + flechas arrastrables/
// reconectables/borrables, con equivalente completo de teclado).
// --------------------------------------------------------------------------
// Contexto: `arrow-connection-graph.js` (TSK-0191) modela el grafo EXPLÍCITO
// de arcos; `arrow-graph-derivation.js` (TSK-0192) deriva ese grafo a partir
// del árbol IR ya existente (`derivarGrafoDeIr`); `arrow-graph-compiler.js`
// (TSK-0193) compila de vuelta grafo -> IR, pero DEC-069 punto 6 fija que esa
// inversión de autoridad es TSK-0195, NO esta ficha. En consecuencia, esta
// tarea NO importa `compilarGrafoAIr`: el grafo interactivo de más abajo
// (`grafoInteractivo`/`nodosInteractivos`) es un ESTADO PROPIO de esta vista,
// re-derivado desde `programaActual` con `derivarGrafoDeIr` al final de cada
// `renderizar()` (colocar/reordenar/eliminar/deshacer, todo lo que ya toca el
// modelo lógico) -- la autoridad de conectividad SIGUE siendo el árbol IR, sin
// cambios en `renderizar()`/`actualizarConexiones()`/`colocarBloque`/
// `eliminarBloque` (criterio de aceptación 5). Las operaciones que SÍ ofrece
// esta capa (crear/reconectar/borrar una flecha, por puntero o teclado) mutan
// ÚNICAMENTE ese estado propio (`grafoInteractivo`) -- nunca `programaActual`
// -- y se redibujan con la función ligera `refrescarCapaInteractivaTrasEdicion`
// (sin pasar por `renderizar()`/`actualizarConexiones()`, para no tocar el
// render derivado ni la posición de los bloques). LIMITACIÓN CONOCIDA Y
// DOCUMENTADA de esta fase (no oculta): como `grafoInteractivo` se
// re-deriva DESDE CERO cada vez que ocurre una acción estructural ajena a
// esta capa (colocar/reordenar/eliminar/deshacer un bloque), cualquier
// creación/reconexión/borrado manual de flechas hecho por el alumno se
// pierde en cuanto ocurre una de esas acciones -- es el precio explícito de
// "coexistir sin invertir la autoridad todavía" (DEC-069 punto 6): mientras
// el árbol IR sea la única fuente de verdad, no hay ningún sitio donde
// persistir una edición de flecha que contradiga esa estructura. TSK-0195
// (inversión de autoridad) es la que hace que estas ediciones sobrevivan de
// verdad y decidan la ejecución/corrección.
//
// Modelo de puertos por tipo de nodo (DEC-069 punto 1, reexportado desde
// `arrow-connection-graph.js`, nunca reimplementado): cada bloque muestra una
// ancla `<button class="canvas-port">` por cada puerto que declara
// `puertosDeNodo` (1 `ENTRADA` si `entrada:true`, y una por cada valor de
// `salidas`) -- ver `descriptoresPuertos`/`renderizarCapaInteractiva`, más
// abajo. `Comentario` (`entrada:false, salidas:[]`) no gana ninguna ancla:
// coherente con que no participa del flujo (DEC-069 punto 1).
//
// Crear/reconectar (criterio de aceptación 1): una única función pura,
// `calcularAccionArco` (exportada para tests unitarios sin DOM), decide si un
// gesto puntero-suelta-en-otro-puerto (o su equivalente de teclado, ver más
// abajo) CREA un arco nuevo o RECONECTA uno ya existente -- sin que quien
// arrastra tenga que saber cuál de los dos ocurrirá: si el puerto de origen
// ya tenía un arco saliente, se reasigna su extremo `hasta`; si el puerto de
// destino ya tenía un arco entrante, se reasigna su extremo `desde`; en
// cualquier otro caso, se crea un arco nuevo. Esto cubre a la vez "crear desde
// un puerto de SALIDA a uno de ENTRADA" y "arrastrar un extremo de una flecha
// YA EXISTENTE para reconectarla", sin duplicar lógica entre ambos casos ni
// necesitar que el gesto "sepa" de antemano si está creando o reconectando.
//
// Snapping (criterio de aceptación 1): al soltar (puntero) se busca, con
// `puertoMasCercano` (pura, exportada), el puerto COMPATIBLE más próximo
// dentro de `RADIO_SNAP_PUERTO_PX`, más abajo -- nunca se exige soltar
// exactamente sobre el `<button>` de destino, que sería un objetivo demasiado
// pequeño para un gesto de arrastre en un diagrama con muchos bloques.
//
// Borrar una flecha suelta (criterio de aceptación 2, "misma afordancia que
// borrar un bloque"): cada flecha gana un `<button class="canvas-arrow-
// handle">` propio en su punto medio (el equivalente de "seleccionar la
// flecha") que se puede arrastrar hasta la papelera (`.canvas-trash`, ya
// existente, TSK-0061, reutilizada sin cambios) o borrar con Supr/Backspace
// mientras tiene el foco -- mismo mecanismo, misma papelera, mismo atajo de
// teclado que ya usa un bloque colocado (`manejarTeclaEnBloque`/
// `manejarActivarPapelera`, generalizados más abajo con `tipoSeleccionActual`
// para que la papelera actúe sobre lo que tenga el foco más reciente, bloque
// O flecha). Borrar un bloque retira sus flechas conectadas AUTOMÁTICAMENTE.
// sin ningún código dedicado: `eliminarBloque` ya llama a `renderizar()`, que
// re-deriva `grafoInteractivo` desde el `programaActual` YA SIN ese bloque --
// `derivarGrafoDeIr` nunca puede producir un arco hacia/desde un nodo que ya
// no existe en el árbol IR.
//
// Equivalente de teclado OBLIGATORIO (criterio de aceptación 3, WCAG 2.5.7
// "Dragging Movements" -- requisito duro, no opcional, igual que TSK-0081):
// cada ancla de puerto (`manejarTeclaEnPuerto`, más abajo) admite un "modo
// conexión" de 2 pasos, sin ratón: Intro/Espacio sobre el primer puerto lo
// marca como origen pendiente (`origenConexionTeclado`, anunciado por la
// live region ya existente); Tab hasta el puerto de destino e Intro/Espacio
// otra vez completa la conexión (misma función `calcularAccionArco` que usa
// el puntero, así que crea o reconecta exactamente con la misma lógica);
// Escape, o volver a activar el MISMO puerto, cancela sin aplicar ningún
// cambio. Delete/Backspace sobre un puerto borra la flecha conectada a ESE
// extremo exacto; Delete/Backspace sobre el asa de una flecha (o activar la
// papelera con ella enfocada) la borra igual que arrastrarla a la papelera --
// exactamente el mismo patrón de equivalencia ya aprobado para bloques
// (`manejarTeclaEnBloque`) y para el arrastre libre 2D (`manejarTeclaEnAncla`,
// TSK-0081), nunca un mecanismo nuevo inventado desde cero.
//
// Validación como PISTA no bloqueante (criterio de aceptación 4, DEC-069
// punto 3 / DEC-070 -- LEÍDO explícitamente antes de escribir este módulo):
// tras cada mutación de `grafoInteractivo` se recalcula
// `validarGrafoArcos(nodosInteractivos, grafoInteractivo)` (la misma función
// de TSK-0191, usada aquí EXACTAMENTE como "pista local de edición en vivo",
// nunca como puerta -- no se importa `compilarGrafoAIr`, y aunque se
// importara, "Ejecutar"/"Corrección" en este módulo siguen sin tocarse,
// criterio de aceptación 5). Las violaciones se traducen a señal NO
// CROMÁTICA (`aria-invalid="true"` + borde discontinuo + un icono "!" de
// texto, `canvas.css`, nunca solo color, 03-frontend §7.4/regla 17) sobre el
// puerto/flecha concreto que las provoca (`puertoAfectadoPorViolacion`/
// `arcoAfectadoPorViolacion`, puras, exportadas) y el recuento total se
// anuncia por la live region ya existente tras cada acción de esta capa
// (`mensajeConteoViolaciones`) y de forma persistente (no en vivo, para no
// abrir una segunda live region, ver comentario junto a
// `resumenValidacionInteractiva`) en un párrafo propio siempre presente. NUNCA
// se bloquea ninguna acción de esta capa por causa de una violación: crear/
// reconectar/borrar se aplican siempre, exactamente igual que
// `agregarArco`/`reconectarExtremo`/`eliminarArco` nunca lanzan por motivos de
// validez estructural (solo por argumentos malformados). DEC-070, punto 2:
// el `MULTIPLES_ENTRADAS` que `validarGrafoArcos` dispara SIEMPRE sobre el
// nodo de convergencia de un `Condicional` con continuación real es un FALSO
// POSITIVO CONOCIDO de ese módulo (congelado, ruta prohibida de esta ficha) --
// se muestra igual que cualquier otra violación (no se filtra en silencio,
// widget honesto), documentado aquí para que no se lea como un bug nuevo de
// esta tarea.
//
// LIMITACIÓN HONESTA registrada explícitamente (instrucción del encargo,
// CLAUDE.md "no decidas silenciosamente"): la verificación manual en un
// navegador real (tema claro y oscuro) del arrastre de extremos/snapping que
// pide el criterio de aceptación 5 de la ficha NO se ha podido realizar en
// este entorno (sin navegador real disponible para este agente) -- cubierta
// aquí únicamente con tests automatizados (unitarios de la lógica pura +
// axe-core sobre jsdom + lógica de teclado); queda PENDIENTE de acción humana,
// registrada también en el `historial:` de la ficha.
// --------------------------------------------------------------------------
// TSK-0196 (DEC-069 fase 3, docs/generated/decisions.md#DEC-069, "pulido:
// columnas divergentes, UX de reconexión, reconciliar el rol de
// connectors.js"): CIERRE de la cadena de flechas manuales.
// --------------------------------------------------------------------------
// 1. PULIDO VISUAL de ramas (criterio de aceptación 1): `crearZonaRama` gana
//    un 4º parámetro `tipoRama` (`'verdadero' | 'falso' | 'cuerpo'`), volcado
//    a `dataset.branchKind` de la propia zona -- `canvas.css` lo usa para
//    pintar `.canvas-block-branches` como COLUMNAS DIVERGENTES reales
//    (verdadero/falso lado a lado, `flex-direction: row` con `flex-wrap:
//    wrap` para no desbordar en viewports estrechos) en vez de la pila
//    vertical simple de TSK-0072/TSK-0077, con un acento de borde por tipo de
//    rama (refuerzo, nunca la única señal: el encabezado de texto "Si se
//    cumple"/"Si no se cumple"/"Cuerpo del bucle" ya identificaba cada zona
//    sin él). Cambio PURAMENTE de disposición visual (CSS) + un dataset
//    adicional: NO toca `contenedorId`/`dragover`/`drop` de cada zona, así
//    que el arrastre/snapping de extremos de TSK-0194 (que recalcula
//    coordenadas en tiempo real vía `getBoundingClientRect`, nunca asume una
//    disposición concreta) sigue funcionando sin cambios -- verificado
//    releyendo `puntoDePuerto`/`puntoCentral`, que no dependen de si un
//    contenedor está en fila o columna.
// 2. INFORMACIÓN VISUAL de la capa derivada PORTADA a la capa interactiva
//    (precondición del criterio de aceptación 2 para poder plantear retirar
//    código derivado): `renderizarCapaInteractiva` ahora dibuja también la
//    punta de flecha (un `<marker>` propio, reutilizando la clase ya
//    estilizada `canvas-connection-arrowhead` de `diagram-editor.css` --
//    SOLO se referencia esa clase ya existente desde este archivo, nunca se
//    edita ese fichero, fuera de `rutas_permitidas` de esta ficha) en cada
//    `.canvas-interactive-arrow-line`; los puertos `VERDADERO`/`FALSO`
//    (`.canvas-port-true`/`.canvas-port-false`) ganan un glifo "V"/"F" propio
//    vía `::before` en `canvas.css` (refuerzo visual, la relación real la
//    sigue anunciando el `aria-label` "Puerto de verdadero/falso de..." ya
//    existente, que un `::before` generado por CSS NUNCA sustituye en el
//    nombre accesible mientras `aria-label` siga presente).
// 3. LO QUE **NO** SE PORTA, A PROPÓSITO (decisión de alcance explícita, no
//    silenciosa): el "retorno" (loop-back) de un `Bucle` NO gana una línea
//    dibujada propia en la capa interactiva. DEC-069 punto 1 ya fija que ese
//    retorno es IMPLÍCITO ("NO un arco que el alumno dibuje"): en el modelo
//    nuevo, la contención visual real (el cuerpo vive DENTRO de su propia
//    `.canvas-branch`/`.canvas-block-branches`, con cabecera "Cuerpo del
//    bucle", TSK-0072) ya comunica "esto ocurre dentro del bucle y vuelve a
//    él" sin necesitar una flecha discontinua cruzando el lienzo -- ese
//    dibujo explícito era necesario en el modelo ANTERIOR (SVG plano sin
//    anidamiento visual real), no en este. Reproducir la línea punteada
//    exigiría además localizar el ÚLTIMO bloque efectivo de un cuerpo
//    arbitrariamente anidado (la misma recursión que ya hace
//    `actualizarConexiones`/`dibujarCadena`, más abajo) para un dato que hoy
//    NUNCA es un arco real del `grafoInteractivo` (el retorno no se deriva
//    como arco, ver `arrow-graph-derivation.js`): el coste/riesgo de esa
//    réplica no está justificado por el criterio de aceptación 1 (columnas
//    divergentes/UX de reconexión/rama vacía), así que se documenta como
//    decisión de alcance explícita en vez de intentarlo a medias.
// 4. RETIRADA DE CÓDIGO MUERTO (criterio de aceptación 2): `capaConexiones`/
//    `actualizarConexiones` (TSK-0056/0073/0074, render SVG derivado,
//    `aria-hidden`, ya SIN autoridad de conectividad desde TSK-0195) **NO se
//    retiran en esta ficha** -- decisión de alcance explícita, documentada
//    aquí y en el `historial:` de la ficha, no silenciosa:
//      a. Paridad visual INCOMPLETA incluso tras el punto 2 de arriba: el
//         punto 3 de arriba deja fuera, a propósito, el retorno de bucle.
//         Retirar `capaConexiones` hoy perdería esa señal visual sin
//         sustituto, una regresión real que el encargo prioriza evitar
//         explícitamente ("prioriza CERO regresión funcional... sobre el
//         alcance visual").
//      b. Coste de test real verificado: `tests/unit/diagram-editor/
//         canvas-connections.spec.js` (971 líneas) y su equivalente de
//         accesibilidad `tests/accessibility/diagram-editor/
//         canvas-connections.spec.js` (130 líneas) ejercitan ÚNICAMENTE
//         `capaConexiones`/`actualizarConexiones` (selectors `.canvas-
//         connection-line`/`.canvas-connections`/`.canvas-connection-
//         label`): retirar el render invalidaría esas ~1100 líneas de golpe,
//         exigiendo su reescritura completa contra la capa interactiva en la
//         MISMA tarea que además cambia el layout visual de ramas -- el tipo
//         de cambio grande y arriesgado que el encargo pide explícitamente
//         reducir ("si retirarlo íntegro es demasiado arriesgado en una sola
//         tarea, reduce lo que puedas de forma segura y documenta").
//      c. Bloqueo estructural de rutas: el estilo de esa capa
//         (`.canvas-connections`/`.canvas-connection-line`/`.canvas-
//         connection-label`/`.canvas-connection-arrowhead`) vive en
//         `diagram-editor.css`, que NO está en `rutas_permitidas` de esta
//         ficha (solo `canvas-view.js`/`canvas.css`/`connectors.js`/tests) --
//         retirar el JS que la puebla dejaría ese fichero con selectores
//         huérfanos que esta ficha no puede limpiar sin ampliar su propio
//         alcance autorizado.
//    Recomendación explícita para una tarea futura (no esta): con el punto 2
//    ya portado, cerrar el hueco del punto 3 (visualizar el retorno de bucle
//    en la capa interactiva, si se sigue considerando necesario) y entonces
//    sustituir `canvas-connections.spec.js`/su par de accesibilidad por
//    equivalentes contra `.canvas-interactive-arrow-line`/`.canvas-port-*`
//    ANTES de retirar `capaConexiones`/`actualizarConexiones` y sus estilos
//    en `diagram-editor.css` (ampliando entonces `rutas_permitidas` a ese
//    fichero).
// 5. RECONCILIACIÓN de `connectors.js` (criterio de aceptación 2): ver el
//    bloque de comentarios de cabecera de ese propio módulo -- se documenta
//    allí, no aquí, su rol reducido/coherente con que el grafo de arcos ya es
//    la autoridad de conectividad visible (TSK-0195).
import { crearIcono } from '../../components/base/iconos.js';
import {
  esNodo,
  TIPO_NODO,
  crearPrograma,
  buscarNodoPorId,
  buscarSecuenciaPadre,
} from './editor-model.js';
import { conectarBloque, desconectarBloque, moverBloqueEnContenedor } from './connectors.js';
import { definicionDeTipo } from './palette-view.js';
import {
  crearEstadoVisual,
  establecerPosicion,
  obtenerPosicion,
  eliminarPosicion,
} from './editor-visual-state.js';
// TSK-0194 (DEC-069 fase 2, docs/generated/decisions.md#DEC-069/#DEC-070):
// capa interactiva de flechas. Se consume ÚNICAMENTE la API pública ya
// aprobada de `arrow-connection-graph.js` (TSK-0191) y
// `arrow-graph-derivation.js` (TSK-0192) -- ambos módulos son de
// SOLO LECTURA para esta ficha (el primero explícitamente `rutas_prohibidas`;
// el segundo, aunque no está listado ahí, tampoco figura en
// `rutas_permitidas`, así que se trata con el mismo criterio: se importa su
// API pública, nunca se edita). Ver el bloque de comentarios "TSK-0194" más
// abajo, junto a `crearVistaCanvas`, para el diseño completo.
import {
  PUERTO,
  puertosDeNodo,
  crearArco,
  crearGrafoArcos,
  agregarArco,
  eliminarArco,
  reconectarExtremo,
  arcosDesde,
  arcosHacia,
  validarGrafoArcos,
} from './arrow-connection-graph.js';
import { derivarGrafoDeIr } from './arrow-graph-derivation.js';
// TSK-0195 (DEC-069 fase 2, docs/generated/decisions.md#DEC-069/#DEC-070):
// inversión de autoridad de conectividad al grafo de arcos. A diferencia de
// TSK-0194 (que deliberadamente NO importaba este módulo, ver su cabecera),
// esta ficha SÍ compila el grafo de vuelta a IR -- es exactamente el punto
// de la tarea. Se consume únicamente la API pública ya aprobada de
// `arrow-graph-compiler.js` (TSK-0193, ruta prohibida/no permitida de esta
// ficha: no se edita, solo se importa).
import { compilarGrafoAIr } from './arrow-graph-compiler.js';
import { crearRanuraTraducir } from '../../utils/ranura-traducir.js';

/**
 * @typedef {import('./editor-model.js').NodoModelo} NodoModelo
 * @typedef {import('./editor-visual-state.js').EstadoVisual} EstadoVisual
 */

/**
 * Forma mínima de metadatos de bloque que este módulo necesita para dar
 * feedback con texto (id + etiqueta). Se declara aquí en vez de importar el
 * `DefinicionBloque` de `palette-view.js`: `colocarBloque` solo necesita
 * estos dos campos (ver la cabecera).
 * @typedef {{ id?: string, etiqueta?: string }} DefinicionBloqueMinima
 */

/**
 * Contador de módulo para generar un `id` de encabezado único POR INSTANCIA
 * de `crearVistaCanvas` (hallazgo P2 de accessibility-reviewer en TSK-0055:
 * un `id` literal fijo produce varias `<section aria-labelledby="...">` con
 * el mismo `id` en el documento si conviven varias instancias, lo que viola
 * `landmark-unique`).
 */
let contadorInstanciasCanvas = 0;

/** MIME con el nodo IR serializado. DEBE coincidir con la constante privada
 * homónima de `palette-view.js`: es el contrato de datos entre ambos
 * módulos (vía `dataTransfer`), documentado en los dos sitios a propósito
 * porque no hay import directo que lo garantice en tiempo de compilación. */
export const MIME_BLOQUE = 'application/x-diagram-block+json';
/** MIME con el id de bloque, SIEMPRE presente (igual que en palette-view.js). */
export const MIME_BLOQUE_ID = 'application/x-diagram-block-id';

/** Id reservado de la ancla fija "Inicio" (TSK-0055, DEC-026). DEBE coincidir
 * con `ID_CONTENEDOR_INICIO` de `connectors.js`. */
export const ID_ANCLA_INICIO = 'inicio';
/** Id reservado de la ancla fija "Fin". Ver `ID_ANCLA_INICIO`. */
export const ID_ANCLA_FIN = 'fin';

/** Espacio de nombres SVG, requerido por `document.createElementNS` para
 * cualquier elemento gráfico de la capa de flechas (TSK-0056). */
const SVG_NS = 'http://www.w3.org/2000/svg';

/** Reordenar por teclado (TSK-0060, sustituye el movimiento libre x/y de
 * TSK-0023): ArrowUp sube un bloque una posición en la Secuencia, ArrowDown
 * lo baja una posición. Izquierda/derecha ya no tienen significado en una
 * lista vertical de un único eje y se ignoran deliberadamente (no capturan
 * el evento, no hacen nada). */
const TECLA_A_DELTA_INDICE = Object.freeze({
  ArrowUp: -1,
  ArrowDown: 1,
});

/** Incremento, en píxeles, de cada pulsación de `Alt+flecha` sobre el
 * movimiento VISUAL libre de un bloque (TSK-0081, ver comentario de
 * cabecera "Teclado"): un valor perceptible de un vistazo sin ser tan
 * grande que un ajuste fino resulte imposible por teclado. */
const INCREMENTO_TECLADO_POSICION_LIBRE = 12;

/** Movimiento VISUAL libre por teclado (TSK-0081): `Alt+flecha`, las 4
 * direcciones -- a diferencia de `TECLA_A_DELTA_INDICE` (reordenado
 * ESTRUCTURAL, sin modificador, sin cambios en esta tarea), este mapa nunca
 * toca `programaActual`, solo `EstadoVisual` (ver comentario de cabecera
 * "Teclado" para la justificación completa de por qué se separan). */
const TECLA_A_DELTA_XY = Object.freeze({
  ArrowUp: { x: 0, y: -INCREMENTO_TECLADO_POSICION_LIBRE },
  ArrowDown: { x: 0, y: INCREMENTO_TECLADO_POSICION_LIBRE },
  ArrowLeft: { x: -INCREMENTO_TECLADO_POSICION_LIBRE, y: 0 },
  ArrowRight: { x: INCREMENTO_TECLADO_POSICION_LIBRE, y: 0 },
});

/** Umbral, en píxeles de distancia total recorrida por el puntero desde el
 * `mousedown`, a partir del cual un gesto de arrastre se considera un
 * arrastre REAL (TSK-0081) y no un simple clic de foco/selección -- mismo
 * propósito que la bandera `seMovio` del arrastre-de-reordenado anterior a
 * esta tarea, expresado ahora como una distancia en vez de un cambio de
 * índice (que ya no existe en este gesto). */
const UMBRAL_ARRASTRE_LIBRE_PX = 3;

/** Margen REAL adicional (no el hueco de overflow que ya existiera antes de
 * mover nada) que `limitarDentroDelContenedor` añade en las 4 direcciones al
 * rango de movimiento libre de un bloque, ancla o zona de rama/cuerpo
 * (TSK-0208, reporte directo del usuario tras probar TSK-0207: "se pueden
 * mover, pero mínimamente, necesito más rango"). Causa raíz (ver el
 * comentario de cabecera de `limitarDentroDelContenedor` para el análisis
 * completo): el margen antes calculado dependía por completo de
 * `contenedor.scrollWidth`/`scrollHeight`, que normalmente coincide con el
 * tamaño NATURAL del contenido (sin ningún hueco de overflow propio), así
 * que el rango efectivo era casi cero. Valor elegido dentro del rango que
 * sugería el propio frontmatter de la tarea (400-600px): suficientemente
 * grande para reorganizar visualmente con comodidad, sin dejar de sentirse
 * un límite real (criterio de aceptación "no es movimiento infinito"). */
const RANGO_EXTRA_ARRASTRE_LIBRE_PX = 480;

/** Longitud máxima del texto de contenido MOSTRADO dentro de un bloque antes
 * de acortarlo con una elipsis (TSK-0068, ver comentario de cabecera
 * "Truncado accesible"): un límite de STRING, no de CSS, así que no reabre
 * la decisión de TSK-0071 de no truncar visualmente con `text-overflow`. */
const LIMITE_CARACTERES_CONTENIDO = 60;

/**
 * `true` si `nodo` es un tipo que tiene su propia "rama"/"cuerpo" renderizado
 * como zona de contenido anidada (TSK-0072): `Condicional` (rama_si/
 * rama_sino) o cualquiera de los 3 nodos de bucle (cuerpo). Comprobación de
 * tipo mínima y local, necesaria para decidir CÓMO renderizar un bloque
 * (`crearItemBloque`, más abajo): no es una reimplementación de ninguna
 * lógica de `connectors.js` (que resuelve un problema distinto: enumerar
 * contenedores para un `<select>`, no decidir la forma del DOM de un
 * bloque).
 * @param {NodoModelo} nodo
 * @returns {boolean}
 */
function esNodoConRamas(nodo) {
  return (
    nodo.nodo === TIPO_NODO.CONDICIONAL ||
    nodo.nodo === TIPO_NODO.BUCLE_MIENTRAS ||
    nodo.nodo === TIPO_NODO.BUCLE_FIN_PARA ||
    nodo.nodo === TIPO_NODO.REPETIR_HASTA
  );
}

/**
 * Acorta `texto` a `limite` caracteres con una elipsis final si hace falta.
 * Nunca corta a mitad de la propia elipsis (deja sitio para ella dentro del
 * límite).
 * @param {string} texto
 * @param {number} limite
 * @returns {string}
 */
function truncarTexto(texto, limite) {
  if (texto.length <= limite) return texto;
  return `${texto.slice(0, Math.max(0, limite - 1)).trimEnd()}…`;
}

/**
 * Renderizador de solo lectura, mínimo y PURO, de una expresión del modelo
 * a un texto corto de una línea (p.ej. "edad > 18"), TSK-0068. No reutiliza
 * `expression-editor.js` (widget interactivo completo, en `rutas_prohibidas`
 * de esta ficha): cubre únicamente los 4 tipos de nodo de expresión que
 * `editor-model.js` expone (`Literal`/`ReferenciaVariable`/`Operacion`/
 * `AccesoIndice`); cualquier otro valor cae al `default` (nombre de tipo),
 * nunca lanza ni deja el texto vacío en silencio.
 * @param {NodoModelo | null | undefined} nodo
 * @returns {string}
 */
function textoDeExpresion(nodo) {
  if (!nodo || typeof nodo !== 'object') return '';
  const registro = /** @type {Record<string, unknown>} */ (nodo);
  switch (registro.nodo) {
    case TIPO_NODO.LITERAL: {
      const valor = registro.valor;
      return typeof valor === 'string' ? `"${valor}"` : String(valor);
    }
    case TIPO_NODO.REFERENCIA_VARIABLE:
      return typeof registro.nombre === 'string' ? registro.nombre : '';
    case TIPO_NODO.OPERACION: {
      const operador = typeof registro.operador === 'string' ? registro.operador : '';
      const operandos = Array.isArray(registro.operandos) ? registro.operandos : [];
      if (operandos.length === 1) {
        return `${operador}${textoDeExpresion(/** @type {NodoModelo} */ (operandos[0]))}`;
      }
      const [izquierda, derecha] = operandos;
      return `${textoDeExpresion(/** @type {NodoModelo} */ (izquierda))} ${operador} ${textoDeExpresion(/** @type {NodoModelo} */ (derecha))}`;
    }
    case TIPO_NODO.ACCESO_INDICE: {
      const lista = /** @type {NodoModelo} */ (registro.lista);
      const indice = /** @type {NodoModelo} */ (registro.indice);
      return `${textoDeExpresion(lista)}[${textoDeExpresion(indice)}]`;
    }
    default:
      return typeof registro.nodo === 'string' ? registro.nodo : '';
  }
}

/**
 * Valor funcional real de `nodo`, listo para mostrar dentro del bloque
 * (TSK-0068), o `null` si este tipo no tiene ninguno cubierto por el
 * criterio de aceptación de esta ficha (en cuyo caso `renderizar()` sigue
 * mostrando la etiqueta genérica, exactamente igual que antes de esta
 * tarea). Ver "Alcance del contenido real" en el comentario de cabecera del
 * archivo para la justificación de qué tipos entran y cuáles no.
 * @param {NodoModelo} nodo
 * @returns {string | null}
 */
function contenidoRealDeNodo(nodo) {
  switch (nodo.nodo) {
    case TIPO_NODO.ENTRADA:
    case TIPO_NODO.ASIGNACION: {
      const variable = nodo.variable;
      return typeof variable === 'string' && variable.length > 0 ? variable : null;
    }
    case TIPO_NODO.CONDICIONAL:
    case TIPO_NODO.BUCLE_MIENTRAS:
    case TIPO_NODO.REPETIR_HASTA: {
      const texto = textoDeExpresion(/** @type {NodoModelo} */ (nodo.condicion));
      return texto.length > 0 ? texto : null;
    }
    // DEC-129: el bucle PARA enseña su contador («para i de 1 a 10»). Sin
    // esto caía a la etiqueta genérica, y uno traducido desde pseudocódigo
    // se leía literalmente «BucleFinPara».
    case TIPO_NODO.BUCLE_FIN_PARA: {
      const variable = typeof nodo.variable === 'string' ? nodo.variable : '';
      if (variable.length === 0) return null;
      const inicio = textoDeExpresion(/** @type {NodoModelo} */ (nodo.inicio));
      const fin = textoDeExpresion(/** @type {NodoModelo} */ (nodo.fin));
      const paso = textoDeExpresion(/** @type {NodoModelo} */ (nodo.paso));
      const texto = `para ${variable} de ${inicio} a ${fin}`;
      return paso.length > 0 && paso !== '1' ? `${texto}, de ${paso} en ${paso}` : texto;
    }
    // TSK-0213 (hallazgo real de accessibility-reviewer, ver "Alcance del
    // contenido real" más arriba): estos 4 tipos se añaden al mapa para
    // restaurar, sin `nombreCorto`, la capacidad de distinguir dos bloques
    // del MISMO tipo por su nombre accesible -- exactamente el mismo
    // criterio ya aplicado a Entrada/Asignación (variable) y
    // Condicional/Bucle (condición como texto), aplicado ahora a los otros
    // tipos de `CAMPOS_POR_TIPO` (`block-content-panel.js`) que también
    // tienen contenido real editable.
    case TIPO_NODO.COMENTARIO: {
      const texto = nodo.texto;
      return typeof texto === 'string' && texto.length > 0 ? texto : null;
    }
    case TIPO_NODO.LLAMADA: {
      const nombreFuncion = nodo.nombre_funcion;
      return typeof nombreFuncion === 'string' && nombreFuncion.length > 0 ? nombreFuncion : null;
    }
    case TIPO_NODO.SALIDA:
    case TIPO_NODO.RETORNO: {
      const expresion = nodo.expresion;
      if (expresion === null || expresion === undefined) return null;
      const texto = textoDeExpresion(/** @type {NodoModelo} */ (expresion));
      return texto.length > 0 ? texto : null;
    }
    default:
      return null;
  }
}

// --------------------------------------------------------------------------
// TSK-0194: lógica PURA de la capa interactiva de flechas (sin DOM, ver el
// comentario de cabecera "TSK-0194" para el diseño completo). Exportadas a
// propósito para poder testearlas sin montar ningún elemento
// (`frontend/tests/unit/diagram-editor/canvas-arrow-layer.spec.js`).
// --------------------------------------------------------------------------

/**
 * Radio de "snapping" (criterio de aceptación 1), en el mismo sistema de
 * coordenadas que `puntoDePuerto`/`puntoCentral` (píxeles sin escalar por
 * zoom): soltar un arrastre a menos de esta distancia de un puerto
 * compatible lo conecta a ÉL, sin exigir soltar exactamente encima del
 * `<button>` (un objetivo demasiado pequeño para un gesto de arrastre real).
 */
export const RADIO_SNAP_PUERTO_PX = 48;

/** Nombre legible de cada valor de `PUERTO` (DEC-069 punto 1), para el
 * nombre accesible de cada ancla/asa y para los mensajes de la live region. */
export const ETIQUETA_PUERTO = Object.freeze({
  [PUERTO.ENTRADA]: 'entrada',
  [PUERTO.SALIDA]: 'salida',
  [PUERTO.VERDADERO]: 'verdadero',
  [PUERTO.FALSO]: 'falso',
  [PUERTO.CUERPO]: 'cuerpo',
});

/**
 * `true` si `puerto` es un puerto "de salida" (cualquiera salvo `ENTRADA`,
 * DEC-069 punto 1: `SALIDA`/`VERDADERO`/`FALSO`/`CUERPO` son las 4 formas de
 * "salida" que puede tener un nodo; `ENTRADA` es la única "de entrada").
 * @param {string} puerto
 * @returns {boolean}
 */
export function esPuertoSalida(puerto) {
  return puerto !== PUERTO.ENTRADA;
}

/**
 * Punto de ancla LOCAL (relativo a la esquina superior izquierda de la caja
 * de un nodo, antes de sumar su posición real) de `puerto`, a partir del
 * tamaño `{ width, height }` de esa caja -- mismo estilo visual que un editor
 * de diagramas de flujo convencional: `ENTRADA` arriba, `SALIDA` abajo,
 * `VERDADERO`/`FALSO` abajo-izquierda/abajo-derecha (para no solaparse con
 * `SALIDA` en un `Condicional`, que nunca tiene puerto `SALIDA` ordinario,
 * DEC-069 punto 1), `CUERPO` a la derecha (para no solaparse con `SALIDA` de
 * un `Bucle*`, que sí tiene ambos puertos a la vez).
 * @param {{ width: number, height: number }} tamano
 * @param {string} puerto
 * @returns {{ x: number, y: number }}
 */
export function anclaLocalDePuerto(tamano, puerto) {
  const { width, height } = tamano;
  switch (puerto) {
    case PUERTO.ENTRADA:
      return { x: width / 2, y: 0 };
    case PUERTO.SALIDA:
      return { x: width / 2, y: height };
    case PUERTO.VERDADERO:
      return { x: width * 0.25, y: height };
    case PUERTO.FALSO:
      return { x: width * 0.75, y: height };
    case PUERTO.CUERPO:
      return { x: width, y: height / 2 };
    default:
      throw new TypeError(`anclaLocalDePuerto: puerto desconocido '${String(puerto)}'`);
  }
}

/**
 * Candidato de puerto con coordenadas ya resueltas (canvas-view.js las
 * calcula con `puntoDePuerto`, que depende del DOM real; esta función es
 * PURA: solo opera sobre la lista ya calculada).
 * @typedef {{ nodeId: string, puerto: string, x: number, y: number }} CandidatoPuerto
 */

/**
 * El candidato de `candidatos` más cercano a `punto`, dentro de `radioMax`
 * píxeles (criterio de aceptación 1, "snapping al puerto válido más
 * cercano"). `null` si ninguno cae dentro del radio (o la lista está vacía).
 * @param {CandidatoPuerto[]} candidatos
 * @param {{ x: number, y: number }} punto
 * @param {number} radioMax
 * @returns {CandidatoPuerto | null}
 */
export function puertoMasCercano(candidatos, punto, radioMax) {
  /** @type {CandidatoPuerto | null} */
  let mejor = null;
  let mejorDistancia = Infinity;
  for (const candidato of candidatos) {
    const distancia = Math.hypot(candidato.x - punto.x, candidato.y - punto.y);
    if (distancia <= radioMax && distancia < mejorDistancia) {
      mejorDistancia = distancia;
      mejor = candidato;
    }
  }
  return mejor;
}

/**
 * @typedef {{ nodeId: string, puerto: import('./arrow-connection-graph.js').ValorPuerto }} ExtremoPuerto
 */

/**
 * Decide y aplica, de forma PURA e INMUTABLE (nunca muta `grafo`), el efecto
 * de conectar `origen` con `destino` (en cualquier orden -- el propio puntero/
 * teclado no necesita saber de antemano si "creará" o "reconectará", ver el
 * comentario de cabecera "TSK-0194"): exactamente uno de los dos debe ser un
 * puerto "de salida" (`esPuertoSalida`) y el otro el puerto `ENTRADA` de otro
 * nodo, o se rechaza. Si el puerto de salida resuelto YA tenía un arco
 * saliente, se RECONECTA su extremo `hasta` (criterio de aceptación 1,
 * "extremos de una flecha existente arrastrables para reconectar"); si no,
 * pero el puerto de entrada YA tenía un arco entrante, se reconecta su
 * extremo `desde`; si ninguno de los dos, se CREA un arco nuevo. Nunca lanza
 * por causa de la VALIDEZ estructural del resultado (DEC-069 punto 3): un
 * `Condicional` con ya 1 arco `VERDADERO`, por ejemplo, puede legítimamente
 * reconectarse a otro destino sin que esta función lo impida -- la señal de
 * "esto viola una regla" es responsabilidad de `validarGrafoArcos`, ejecutada
 * aparte, como pista no bloqueante (DEC-069 punto 3 / DEC-070).
 * @param {import('./arrow-connection-graph.js').GrafoArcos} grafo
 * @param {ExtremoPuerto} origen
 * @param {ExtremoPuerto} destino
 * @returns {
 *   | { tipo: 'creado' | 'reconectado', grafo: import('./arrow-connection-graph.js').GrafoArcos, arcoId: string }
 *   | { tipo: 'rechazado', motivo: string }
 * }
 */
export function calcularAccionArco(grafo, origen, destino) {
  const origenEsSalida = esPuertoSalida(origen.puerto);
  const destinoEsSalida = esPuertoSalida(destino.puerto);

  /** @type {ExtremoPuerto} */
  let desde;
  /** @type {ExtremoPuerto} */
  let hasta;
  if (origenEsSalida && !destinoEsSalida) {
    desde = origen;
    hasta = destino;
  } else if (!origenEsSalida && destinoEsSalida) {
    desde = destino;
    hasta = origen;
  } else {
    return {
      tipo: 'rechazado',
      motivo: 'una flecha siempre conecta un puerto de salida con un puerto de entrada, nunca dos del mismo tipo',
    };
  }

  if (desde.nodeId === hasta.nodeId) {
    return { tipo: 'rechazado', motivo: 'un bloque no puede conectarse consigo mismo' };
  }

  const arcoExistenteDesde = arcosDesde(grafo, desde.nodeId, desde.puerto)[0];
  if (arcoExistenteDesde) {
    return {
      tipo: 'reconectado',
      grafo: reconectarExtremo(grafo, arcoExistenteDesde.id, 'hasta', hasta.nodeId, hasta.puerto),
      arcoId: arcoExistenteDesde.id,
    };
  }

  const arcoExistenteHasta = arcosHacia(grafo, hasta.nodeId, hasta.puerto)[0];
  if (arcoExistenteHasta) {
    return {
      tipo: 'reconectado',
      grafo: reconectarExtremo(grafo, arcoExistenteHasta.id, 'desde', desde.nodeId, desde.puerto),
      arcoId: arcoExistenteHasta.id,
    };
  }

  const arcoNuevo = crearArco({
    desdeNodoId: desde.nodeId,
    desdePuerto: desde.puerto,
    hastaNodoId: hasta.nodeId,
    hastaPuerto: hasta.puerto,
  });
  return { tipo: 'creado', grafo: agregarArco(grafo, arcoNuevo), arcoId: arcoNuevo.id };
}

/**
 * Elimina, de forma PURA e INMUTABLE, el arco (si existe) que toca el
 * extremo exacto `(nodeId, puerto)` -- como `desde` o como `hasta`,
 * cualquiera de los dos (criterio de aceptación 2/3: "borrar una flecha
 * suelta" arrastrando/activando ese mismo puerto). `null` si no había ningún
 * arco en ese extremo.
 * @param {import('./arrow-connection-graph.js').GrafoArcos} grafo
 * @param {ExtremoPuerto} extremo
 * @returns {{ grafo: import('./arrow-connection-graph.js').GrafoArcos, arcoId: string } | null}
 */
export function eliminarArcoDeExtremo(grafo, extremo) {
  const arco = arcosDesde(grafo, extremo.nodeId, extremo.puerto)[0] ?? arcosHacia(grafo, extremo.nodeId, extremo.puerto)[0];
  if (!arco) return null;
  return { grafo: eliminarArco(grafo, arco.id), arcoId: arco.id };
}

/** Tipos de violación de `validarGrafoArcos` (TSK-0191) que afectan a un
 * puerto FIJO, independiente del mensaje -- ver `puertoAfectadoPorViolacion`. */
const TIPO_VIOLACION_A_PUERTO_FIJO = Object.freeze({
  INICIO_SIN_SALIDA: PUERTO.SALIDA,
  FIN_SIN_ENTRADA: PUERTO.ENTRADA,
  MULTIPLES_ENTRADAS: PUERTO.ENTRADA,
  BUCLE_SIN_CUERPO: PUERTO.CUERPO,
  BUCLE_CUERPO_DUPLICADO: PUERTO.CUERPO,
});

/**
 * A qué `{ nodeId, puerto }` exacto afecta `violacion` (una de las que
 * devuelve `validarGrafoArcos`), o `null` si esta violación no señala un
 * puerto concreto (p.ej. `CICLO`/`NODO_DESCONOCIDO`, que señalan un ARCO, ver
 * `arcoAfectadoPorViolacion`). `MULTIPLES_SALIDAS` es ambiguo por tipo (puede
 * ser el puerto `SALIDA` de un bloque normal/Bucle, o el propio `SALIDA` de
 * `'inicio'`): como ambos casos usan el mismo valor de puerto (`PUERTO.
 * SALIDA`), no hace falta desambiguar por mensaje. `CONDICIONAL_RAMA_
 * FALTANTE`/`CONDICIONAL_RAMA_DUPLICADA` sí necesitan leer el mensaje (la
 * única forma disponible en la forma actual de `ViolacionGrafo` de saber si
 * la rama afectada es "verdadero" o "falso").
 * @param {{ tipo: string, nodoId?: string, arcoId?: string, mensaje: string }} violacion
 * @returns {{ nodeId: string, puerto: import('./arrow-connection-graph.js').ValorPuerto } | null}
 */
export function puertoAfectadoPorViolacion(violacion) {
  if (!violacion.nodoId) return null;
  if (violacion.tipo === 'CONDICIONAL_RAMA_FALTANTE' || violacion.tipo === 'CONDICIONAL_RAMA_DUPLICADA') {
    return { nodeId: violacion.nodoId, puerto: /falso/i.test(violacion.mensaje) ? PUERTO.FALSO : PUERTO.VERDADERO };
  }
  if (violacion.tipo === 'MULTIPLES_SALIDAS') {
    return { nodeId: violacion.nodoId, puerto: PUERTO.SALIDA };
  }
  const puertoFijo = /** @type {Record<string, import('./arrow-connection-graph.js').ValorPuerto>} */ (
    TIPO_VIOLACION_A_PUERTO_FIJO
  )[violacion.tipo];
  return puertoFijo ? { nodeId: violacion.nodoId, puerto: puertoFijo } : null;
}

/**
 * El `arcoId` afectado por `violacion`, o `null` si esta violación no está
 * ligada a un arco concreto (las de cardinalidad -- `MULTIPLES_ENTRADAS`,
 * etc. -- señalan un NODO/puerto, no un arco individual, ver
 * `puertoAfectadoPorViolacion`).
 * @param {{ tipo: string, nodoId?: string, arcoId?: string, mensaje: string }} violacion
 * @returns {string | null}
 */
export function arcoAfectadoPorViolacion(violacion) {
  return violacion.arcoId ?? null;
}

/**
 * @param {unknown} valor
 * @returns {NodoModelo}
 */
function requerirPrograma(valor) {
  if (!esNodo(valor) || valor.nodo !== TIPO_NODO.PROGRAMA) {
    throw new TypeError('crearVistaCanvas requiere "programa": un nodo Programa del modelo del editor');
  }
  const cuerpo = /** @type {Record<string, unknown>} */ (valor).cuerpo;
  if (!esNodo(cuerpo) || cuerpo.nodo !== TIPO_NODO.SECUENCIA) {
    throw new TypeError('el "programa" recibido debe tener un "cuerpo" de tipo Secuencia');
  }
  return valor;
}

/**
 * @param {unknown} valor
 * @returns {EstadoVisual}
 */
function requerirEstadoVisual(valor) {
  const candidato = /** @type {{ posiciones?: unknown } | null} */ (valor);
  if (!candidato || !(candidato.posiciones instanceof Map)) {
    throw new TypeError('crearVistaCanvas requiere "estadoVisual": un EstadoVisual de editor-visual-state.js');
  }
  return /** @type {EstadoVisual} */ (valor);
}

/**
 * Bloque recién eliminado que aún se puede deshacer al instante (TSK-0061).
 * @typedef {{ nodo: NodoModelo, contenedorId: string, indice: number, etiqueta: string, posicionVisual: import('./editor-visual-state.js').PosicionVisual | undefined }} EliminacionPendiente
 */

/**
 * Crea la vista del área de trabajo.
 *
 * @param {{
 *   programa?: NodoModelo,
 *   estadoVisual?: EstadoVisual,
 *   onCambioPrograma?: (programa: NodoModelo) => void,
 *   onDeshacer?: () => void,
 *   deshacerDisponible?: boolean,
 *   onCambioGrafoInteractivo?: (resultado: import('./arrow-graph-compiler.js').ResultadoCompilacion) => void,
 *   mensajeEstadoInicial?: string,
 *   eliminacionPendiente?: EliminacionPendiente | null,
 * }} [opciones]
 * @returns {{
 *   elemento: HTMLElement,
 *   colocarBloque: (nodo: NodoModelo | null, definicion?: DefinicionBloqueMinima, indice?: number) => NodoModelo | null,
 *   obtenerPrograma: () => NodoModelo,
 *   obtenerResultadoCompilacionGrafo: () => import('./arrow-graph-compiler.js').ResultadoCompilacion,
 *   mensajeEstado: () => string,
 *   eliminacionPendiente: () => EliminacionPendiente | null,
 *   deshacerEliminacionTieneFoco: () => boolean,
 *   enfocarDeshacerEliminacion: () => boolean,
 *   destruir: () => void,
 * }}
 */
export function crearVistaCanvas(opciones = {}) {
  const programaInicial = requerirPrograma(opciones.programa ?? crearPrograma());
  // TSK-0081: `estadoVisual` ahora se GUARDA (ya no se descarta tras
  // validar) -- es el mismo objeto compartido que otros módulos aprobados
  // (`block-operations.js`, `editor-persistence.js`) pasan/leen, así que
  // mutarlo aquí con `establecerPosicion`/`eliminarPosicion` (ver
  // `manejarInicioArrastreLibre`/`eliminarBloque` más abajo) es lo que hace
  // que la posición sobreviva al autoguardado sin necesitar un callback
  // nuevo (ver comentario de cabecera, arriba).
  const estadoVisual = requerirEstadoVisual(opciones.estadoVisual ?? crearEstadoVisual());
  const notificarCambioPrograma =
    typeof opciones.onCambioPrograma === 'function' ? opciones.onCambioPrograma : null;
  // TSK-0195: notificado tras CUALQUIER recálculo del resultado de
  // compilación del grafo interactivo (ver `recalcularGrafoInteractivo`/
  // `refrescarCapaInteractivaTrasEdicion`, más abajo). Opcional y aditivo:
  // ningún llamador existente que no lo pase ve ningún cambio de
  // comportamiento (mismo patrón que `onCambioPrograma`/`onDeshacer`).
  const notificarCambioGrafoInteractivo =
    typeof opciones.onCambioGrafoInteractivo === 'function' ? opciones.onCambioGrafoInteractivo : null;

  let programaActual = programaInicial;

  /** Zoom visual del área de trabajo (petición directa del usuario, mockup
   * aprobado): SOLO afecta la escala de PINTADO de `superficie`
   * (`transform: scale`), nunca su layout real ni `EstadoVisual`/el modelo
   * lógico -- mismo principio que el arrastre libre (DEC-028): el zoom es
   * puramente de presentación. Rango modesto (50%-150%, pasos de 10 puntos)
   * para mantener el contenido legible en ambos extremos. `transform` (a
   * diferencia de la propiedad `zoom`) deja intactos `offsetWidth`/
   * `offsetLeft`/`scrollWidth`/`scrollLeft` (documentado y ya explotado por
   * `limitarDentroDelContenedor`, ver su comentario de cabecera): el clamp
   * de arrastre sigue funcionando sin cambios a cualquier nivel de zoom. Lo
   * que SÍ hay que corregir explícitamente son los tres puntos donde este
   * módulo mezcla coordenadas de PANTALLA (`clientX/clientY`, ajenas al
   * `transform`) con coordenadas de LAYOUT (`offsetLeft`/`scrollLeft`,
   * ajenas también) o alimenta un SVG hijo (que, al vivir DENTRO del
   * elemento con `transform`, se reescala una SEGUNDA vez automáticamente):
   * `manejarMover` (delta de arrastre), `puntoCentral` y
   * `dimensionarCapaConexiones` (flechas SVG) -- ver los comentarios
   * puntuales en cada uno. */
  const NIVEL_ZOOM_MIN = 0.5;
  const NIVEL_ZOOM_MAX = 1.5;
  const PASO_ZOOM = 0.1;
  let nivelZoom = 1;

  /** Metadato SOLO de presentación de esta vista (id de nodo -> etiqueta
   * legible); ni el modelo lógico ni la capa visual conocen este dato.
   * Sembrado con Inicio/Fin desde el principio (nunca se les asigna en
   * `renderizar()`, que solo recorre `sentencias` -- las anclas no forman
   * parte de ese array): sin esto, `manejarInicioArrastreLibre` (arrastre
   * por puntero, ahora también enganchado a las anclas, ver
   * "Revisión posterior" en `crearAncla`) anunciaría "Bloque bloque movido…"
   * en vez de "Bloque Inicio movido…", al no encontrar su etiqueta y caer al
   * genérico `'bloque'`. */
  const etiquetasPorId = new Map([
    [ID_ANCLA_INICIO, 'Inicio'],
    [ID_ANCLA_FIN, 'Fin'],
  ]);

  /**
   * DEC-180: el nombre de un bloque para avisos y nombres accesibles. El que
   * apuntó esta vista al colocarlo; si no lo colocó ella (el controlador la
   * reconstruye tras cada cambio, o viene de un borrador), el de su tipo en
   * la paleta; si la paleta no lo tiene, el del modelo. Antes cada aviso
   * caía a algo distinto: «bloque», el id interno o «Asignacion».
   * @param {string} id
   * @returns {string}
   */
  function etiquetaDe(id) {
    const apuntada = etiquetasPorId.get(id);
    if (apuntada) return apuntada;
    const nodo = buscarNodoPorId(programaActual, id);
    if (!nodo) return 'bloque';
    return definicionDeTipo(nodo.nodo)?.etiqueta ?? nodo.nodo;
  }

  /**
   * Clave de POSICIÓN VISUAL (nunca de identidad/grafo, que sigue siendo
   * `ID_ANCLA_INICIO`/`ID_ANCLA_FIN` sin cambios) para el ancla Inicio/Fin de
   * ESTE `programaActual` (la Secuencia raíz mostrada por ESTA instancia de
   * canvas: el programa principal o el cuerpo de UNA función concreta).
   *
   * CORREGIDO (regresión real reportada por el usuario, "el canvas Principal
   * y el de las diferentes funciones... que los posicionamientos no se
   * afecten entre ellos"): `ID_ANCLA_INICIO`/`ID_ANCLA_FIN` son constantes
   * fijas (`'inicio'`/`'fin'`), pero `estadoVisual` (a diferencia de
   * `programaActual`) es UN ÚNICO objeto compartido por `editor-controller.js`
   * entre el programa principal y CADA función (mismo `Map` de posiciones
   * para todas las pestañas de "Cambiar de diagrama") -- ver
   * `editor-controller.js`, `const estadoVisual = ...` fuera de cualquier
   * función concreta. Usar la clave fija sin más hacía que arrastrar el
   * ancla Inicio en una función reposicionara TAMBIÉN el ancla Inicio del
   * programa principal (y de cualquier otra función), al escribir/leer la
   * MISMA entrada del Map. El resto de nodos (bloques, zonas de rama/cuerpo)
   * no sufren esto porque su `id` lo genera `generarId()` con un contador
   * único de toda la sesión (`editor-model.js`), nunca reutilizado entre
   * funciones -- solo Inicio/Fin usan una clave literal no namespaced.
   * `idContenedorRaiz()` (el id real de la Secuencia raíz de ESTE
   * `programaActual`) SÍ es distinto para el programa principal y cada
   * función, y estable mientras esta vista viva (no se regenera al
   * re-renderizar), así que namespacear con él basta para aislar la
   * posición sin tocar `ID_ANCLA_INICIO`/`ID_ANCLA_FIN` (seguirían
   * necesitándose sin cambios para `dataset.nodeId`/el grafo derivado/
   * `resolverElementoDePuerto`, todos ellos YA aislados por instancia de
   * canvas al vivir en closures separadas de `crearVistaCanvas`).
   * @param {string} idAncla `ID_ANCLA_INICIO` o `ID_ANCLA_FIN`
   * @returns {string}
   */
  function claveVisualAncla(idAncla) {
    return `${idContenedorRaiz()}::${idAncla}`;
  }

  const claveVisualAnclaInicio = claveVisualAncla(ID_ANCLA_INICIO);
  const claveVisualAnclaFin = claveVisualAncla(ID_ANCLA_FIN);
  etiquetasPorId.set(claveVisualAnclaInicio, 'Inicio');
  etiquetasPorId.set(claveVisualAnclaFin, 'Fin');

  /** Referencias vivas a los botones ya renderizados, para localizarlos por
   * id (medir su posición real, o recuperar el foco tras un reordenado). */
  const botonesPorId = new Map();

  /** Indicador visual "(rama vacía)" de cada zona de rama/cuerpo SIN
   * contenido (TSK-0073), indexado por el `id` de la Secuencia de esa zona
   * (`rama_si`/`rama_sino`/`cuerpo`): `dibujarCadena` lo usa como destino de
   * la flecha V/F o de entrada al cuerpo cuando la rama no tiene ningún
   * bloque colocado todavía (criterio de aceptación: "el primer bloque de
   * cada rama, o hacia un indicador de rama vacía si no tiene contenido").
   * Repoblado desde cero en cada `renderizar()` (`crearZonaRama`), igual que
   * `botonesPorId`.
   * @type {Map<string, HTMLElement>} */
  const indicadoresVaciosPorContenedor = new Map();

  /** Listeners de arrastre por puntero activos en `document`, para poder
   * desmontarlos desde `destruir()` si el componente se destruye a mitad de
   * un arrastre (docs/03-frontend.md §8.3).
   * @type {(() => void) | null} */
  let detenerArrastreActivo = null;

  /** Id del bloque colocado con el foco más reciente (TSK-0061): lo
   * actualiza el evento `focus` de cada botón de bloque en `renderizar()`.
   * Es lo que "selecciona" la papelera activada por teclado/clic (a
   * diferencia del arrastre, que ya lleva su propio id explícito). No se
   * limpia al perder el foco (p.ej. al tabular hacia la propia papelera):
   * conserva el último bloque enfocado como destino de la acción, igual que
   * el patrón ya usado por `block-operations.js` para "consumir la
   * selección del canvas" (contrato de foco, no un estado ARIA aparte).
   * @type {string | null} */
  let idBloqueSeleccionado = null;

  /** Último bloque eliminado, disponible para deshacer INMEDIATO (ver
   * comentario de cabecera "TSK-0061"): se limpia (a `null`) en cuanto
   * ocurre cualquier otra acción estructural (colocar/reordenar/duplicar/
   * otro borrado) o al deshacer, para que el índice de reinserción
   * guardado siga siendo significativo. `contenedorId` (TSK-0072): el id de
   * la Secuencia (raíz o rama/cuerpo anidado) de la que se eliminó, para que
   * `deshacerEliminacion` reinserte en el MISMO contenedor, no siempre en la
   * raíz. `posicionVisual` (TSK-0081): la posición VISUAL que tenía en
   * `EstadoVisual` justo antes de eliminarse (o `undefined` si no tenía
   * ninguna guardada, p.ej. nunca se arrastró), para que deshacer restaure
   * también DÓNDE se veía, no solo QUÉ era ni EN QUÉ índice/contenedor
   * lógico estaba.
   * @type {EliminacionPendiente | null} */
  let ultimoEliminado = null;

  // --------------------------------------------------------------------------
  // TSK-0194: estado de la capa interactiva de flechas (ver el comentario de
  // cabecera "TSK-0194" para el diseño completo).
  // --------------------------------------------------------------------------
  /** Grafo de arcos INTERACTIVO (DEC-069 fase 2): estado propio de esta
   * vista, re-derivado desde `programaActual` con `derivarGrafoDeIr` al
   * final de cada `renderizar()` -- NUNCA la autoridad de conectividad
   * (sigue siendo el árbol IR, criterio de aceptación 5). Empieza vacío;
   * `renderizar()` (llamada al final de `crearVistaCanvas`) lo puebla antes
   * de que cualquier consumidor externo pueda observarlo.
   * @type {import('./arrow-connection-graph.js').GrafoArcos} */
  let grafoInteractivo = crearGrafoArcos();
  /** Lista plana de nodos-bloque (+ `'inicio'`/`'fin'`) sobre la que se
   * calcularon `grafoInteractivo`/`violacionesInteractivas` -- la misma que
   * exige `validarGrafoArcos(nodos, grafo)`.
   * @type {import('./arrow-connection-graph.js').NodoOClave[]} */
  let nodosInteractivos = [];
  /** Violaciones de `validarGrafoArcos` sobre el grafo interactivo actual
   * (DEC-069 punto 3 / DEC-070: PISTA no bloqueante, nunca una puerta).
   * @type {ReturnType<typeof validarGrafoArcos>} */
  let violacionesInteractivas = [];
  /** TSK-0195 (DEC-069 punto 6/DEC-070): resultado de compilar
   * `grafoInteractivo` con `compilarGrafoAIr` -- la AUTORIDAD estructural
   * real (a diferencia de `violacionesInteractivas`, que es solo una pista
   * local de `validarGrafoArcos`, ver DEC-070). Se recalcula en los mismos
   * dos puntos que `violacionesInteractivas` (`recalcularGrafoInteractivo`/
   * `refrescarCapaInteractivaTrasEdicion`). Empieza en un valor neutro;
   * ambas funciones lo recalculan antes de que `renderizar()` (llamada al
   * final de `crearVistaCanvas`) devuelva el control.
   * @type {import('./arrow-graph-compiler.js').ResultadoCompilacion} */
  let resultadoCompilacionGrafo = { programa: null, errores: [] };
  /** Puerto marcado como origen pendiente de un gesto de conexión POR
   * TECLADO (criterio de aceptación 3, WCAG 2.5.7): `null` si no hay ninguno
   * en curso (ver `manejarTeclaEnPuerto`).
   * @type {{ nodeId: string, puerto: import('./arrow-connection-graph.js').ValorPuerto } | null} */
  let origenConexionTeclado = null;
  /** Qué tipo de elemento tiene la selección más reciente relevante para la
   * papelera/Supr (generalización de `idBloqueSeleccionado`, TSK-0061, para
   * que la papelera actúe también sobre una flecha con el foco, criterio de
   * aceptación 2/3): actualizado por el evento `focus` de cada bloque
   * colocado y de cada asa de flecha.
   * @type {'bloque' | 'arco' | null} */
  let tipoSeleccionActual = null;
  /** Id de la flecha con el foco más reciente (ver `tipoSeleccionActual`).
   * @type {string | null} */
  let idArcoSeleccionado = null;

  contadorInstanciasCanvas += 1;
  const idTitulo = `canvas-heading-${contadorInstanciasCanvas}`;

  /** Contador LOCAL a esta instancia de zonas de rama/cuerpo renderizadas
   * (TSK-0072): usado solo para generar `id`s de encabezado únicos por zona
   * dentro de esta misma instancia de canvas (mismo motivo que
   * `contadorInstanciasCanvas`, pero por zona en vez de por instancia
   * completa, ver `crearZonaRama`). Se reinicia a 0 en cada `renderizar()`
   * (el árbol completo de zonas se reconstruye desde cero en cada pasada,
   * así que reutilizar los mismos ids en el mismo orden es determinista y no
   * deja ids huérfanos). */
  let contadorZonasRama = 0;

  const seccion = document.createElement('section');
  seccion.className = 'canvas ui-panel';
  seccion.setAttribute('aria-labelledby', idTitulo);

  // --------------------------------------------------------------------------
  // TSK-0079: barra de herramientas FIJA en la parte superior del panel del
  // canvas (mockup aprobado por el usuario, Artifact 2026-07-18). Vive fuera
  // de `superficie` (igual que la papelera/deshacer/estado, ver más abajo),
  // así que nunca se desplaza con el scroll interno de la lista de bloques:
  // es "fija" en el sentido de "parte del marco exterior del panel, siempre
  // visible mientras el panel del canvas lo esté", sin necesitar
  // `position: fixed` (que la desligaría por completo del layout del resto
  // de la página). `canvas.css` añade además `position: sticky; top: 0`
  // sobre `.canvas-toolbar`, para que siga visible incluso si el propio
  // documento se desplaza verticalmente.
  //
  // CONTRADICCIÓN REGISTRADA Y RESUELTA (CLAUDE.md, "no decidas
  // silenciosamente"): el criterio de aceptación de esta tarea pide mover el
  // botón "Ejecutar" (hoy construido por `execution-panel.js`) a ESTA barra.
  // `editor-controller.js` -- el único módulo que hoy monta `canvas-view.js`
  // y `execution-panel.js` juntos, y por tanto el único sitio "natural" para
  // pasarle una referencia del uno al otro -- está en `rutas_prohibidas` del
  // frontmatter de esta ficha, así que esta tarea NO puede añadir ahí ningún
  // cableado nuevo (ni una opción `toolbar` a `crearPanelEjecucion`, ni
  // cambiar qué slot recibe qué elemento). Se resuelve con el MISMO patrón
  // que ya usa este propio módulo para comunicarse con `palette-view.js` sin
  // importarlo directamente (ver el comentario de cabecera sobre
  // `MIME_BLOQUE`/`MIME_BLOQUE_ID`: "por contrato de datos... nunca por un
  // import directo"): un contenedor con un atributo `data-*` ESTABLE y
  // documentado en los dos archivos (`data-canvas-toolbar-run`), que
  // `execution-panel.js` localiza por sí mismo (`document.querySelector`) en
  // vez de recibirlo por parámetro. Es un acoplamiento débil deliberado
  // (nunca un `import` cruzado entre paneles hermanos, docs/03-frontend.md
  // §4.2), documentado en ambos extremos del contrato, igual que el resto de
  // contratos de `dataset`/MIME ya existentes en este árbol. Si una tarea
  // futura reabre `editor-controller.js`, puede sustituir este mecanismo por
  // una inyección explícita sin cambiar el resultado visual.
  const barraHerramientas = document.createElement('div');
  barraHerramientas.className = 'canvas-toolbar';
  barraHerramientas.setAttribute('role', 'toolbar');
  barraHerramientas.setAttribute('aria-label', 'Herramientas del área de trabajo');

  // Hueco reservado para controles de zoom/deshacer futuros (mockup
  // aprobado): hoy este editor no tiene zoom, y "deshacer" ya vive como
  // `.canvas-undo` (más abajo, con su propio ciclo de vida ligado a
  // eliminar/reordenar) -- no se traslada aquí a propósito, para no romper
  // sus tests existentes ni su geometría de "objetivo de arrastre" con un
  // cambio de alcance no pedido por el criterio de aceptación de esta
  // tarea.
  // Zoom real del área de trabajo (petición directa del usuario, mockup
  // aprobado): grupo de controles nativos, mismo patrón de tamaño mínimo de
  // objetivo (WCAG 2.2 SC 2.5.8) que el resto de botones del canvas.
  const zonaZoom = document.createElement('div');
  zonaZoom.className = 'canvas-toolbar-zoom';
  zonaZoom.setAttribute('role', 'group');
  zonaZoom.setAttribute('aria-label', 'Zoom del área de trabajo');

  const botonZoomMenos = document.createElement('button');
  botonZoomMenos.type = 'button';
  botonZoomMenos.className = 'canvas-toolbar-zoom-button ui-button ui-button-terciario';
  botonZoomMenos.textContent = '−';
  botonZoomMenos.setAttribute('aria-label', 'Reducir zoom');

  // Sin `role="status"` propio a propósito: ya existe una única live region
  // canónica en este panel (`estado`, `.canvas-status`, más abajo) que los
  // tests localizan con `getByRole(..., 'status')` esperando un solo
  // resultado -- una segunda región de estado aquí la volvería ambigua. El
  // nivel de zoom es texto visible normal; `aria-label` en los propios
  // botones +/- (más abajo) ya deja claro por voz qué hace cada uno.
  const etiquetaZoom = document.createElement('span');
  etiquetaZoom.className = 'canvas-toolbar-zoom-level';
  etiquetaZoom.textContent = '100%';

  const botonZoomMas = document.createElement('button');
  botonZoomMas.type = 'button';
  botonZoomMas.className = 'canvas-toolbar-zoom-button ui-button ui-button-terciario';
  botonZoomMas.textContent = '+';
  botonZoomMas.setAttribute('aria-label', 'Aumentar zoom');

  zonaZoom.append(botonZoomMenos, etiquetaZoom, botonZoomMas);

  /** @param {number} nuevoNivel */
  function aplicarZoom(nuevoNivel) {
    const clamped = Math.min(NIVEL_ZOOM_MAX, Math.max(NIVEL_ZOOM_MIN, nuevoNivel));
    nivelZoom = Math.round(clamped * 100) / 100;
    superficie.style.transform = `scale(${nivelZoom})`;
    etiquetaZoom.textContent = `${Math.round(nivelZoom * 100)}%`;
    botonZoomMenos.disabled = nivelZoom <= NIVEL_ZOOM_MIN;
    botonZoomMas.disabled = nivelZoom >= NIVEL_ZOOM_MAX;
    // El viewport reserva el espacio de LAYOUT real del contenido ya
    // escalado (`transform` no reflowa por sí solo, ver comentario junto a
    // `nivelZoom`): sin esto, el contenido al 150% se pintaría por encima
    // del panel de ejecución en vez de empujarlo hacia abajo.
    viewportZoom.style.height = `${superficie.scrollHeight * nivelZoom}px`;
    dimensionarCapaConexiones();
    actualizarConexiones();
  }

  botonZoomMenos.addEventListener('click', () => aplicarZoom(nivelZoom - PASO_ZOOM));
  botonZoomMas.addEventListener('click', () => aplicarZoom(nivelZoom + PASO_ZOOM));

  // Deshacer GENERAL (petición directa del usuario, mockup aprobado):
  // distinto del `.canvas-undo` de más abajo (deshacer INMEDIATO, contextual,
  // solo tras una eliminación, con foco automático -- se conserva sin
  // cambios, sigue siendo el camino más rápido justo tras borrar). Este es
  // el botón de la barra de herramientas, siempre visible, que deshace la
  // ÚLTIMA mutación lógica de cualquier tipo (colocar/editar/eliminar/
  // reordenar/crear o eliminar función). Este módulo NO conoce el historial
  // en sí (vive en `editor-controller.js`, dueño de `programa`/
  // `estadoVisual`): se limita a invocar `opciones.onDeshacer()`, inyectado
  // explícitamente (a diferencia del contrato `data-canvas-toolbar-run` de
  // `execution-panel.js`, aquí SÍ es el mismo módulo -- `editor-controller.js`
  // -- quien crea esta vista, así que una inyección directa por parámetro es
  // el mecanismo más simple, sin necesidad de acoplamiento débil por
  // `dataset`). `opciones.deshacerDisponible` decide si arranca habilitado.
  const botonDeshacerGeneral = document.createElement('button');
  botonDeshacerGeneral.type = 'button';
  botonDeshacerGeneral.className = 'canvas-toolbar-undo ui-button ui-button-terciario';
  // Rediseño (Fase 4b): «↺ Deshacer» pasa a «Deshacer» con icono de trazo.
  botonDeshacerGeneral.append(crearIcono('deshacer'), document.createTextNode('Deshacer'));
  botonDeshacerGeneral.disabled = !opciones.deshacerDisponible;
  botonDeshacerGeneral.addEventListener('click', () => {
    if (typeof opciones.onDeshacer === 'function') opciones.onDeshacer();
  });

  const zonaEjecutar = document.createElement('div');
  zonaEjecutar.className = 'canvas-toolbar-run-slot';
  zonaEjecutar.dataset.canvasToolbarRun = 'true';
  // Hueco para el botón "Traducir a…", que lo crea la PÁGINA (ver
  // `utils/ranura-traducir.js`): mismo contrato por atributo que el de
  // "Ejecutar" de aquí al lado, por el mismo motivo -- este módulo no puede
  // importar el de otra feature. Vacío mientras nadie lo llene.
  barraHerramientas.append(
    zonaZoom,
    botonDeshacerGeneral,
    zonaEjecutar,
    crearRanuraTraducir(),
  );

  const titulo = document.createElement('h2');
  titulo.id = idTitulo;
  titulo.className = 'canvas-heading';
  titulo.textContent = 'Área de trabajo';

  const ayuda = document.createElement('p');
  ayuda.className = 'canvas-hint';
  ayuda.textContent =
    'Arrastra un bloque de la paleta y suéltalo aquí para añadirlo al final de la ' +
    'secuencia, de arriba hacia abajo. Para reordenar un bloque ya colocado: ' +
    'selecciónalo (clic o Tab) y usa las flechas arriba/abajo del teclado, o ' +
    'arrástralo con el puntero por encima o por debajo de otro bloque. Para ' +
    'eliminar un bloque: arrástralo hasta la papelera, o selecciónalo y pulsa ' +
    'Supr/Retroceso, o activa la papelera con Intro o Espacio; siempre aparece ' +
    'un botón "Deshacer" inmediatamente después.';

  const superficie = document.createElement('ul');
  superficie.className = 'canvas-surface';

  /**
   * Ancla fija Inicio/Fin (TSK-0055, DEC-026): un <button> nativo, igual que
   * un bloque colocado normal, sin ningún control de eliminar/duplicar (nunca
   * eliminables, ni participan del modelo lógico/`sentencias` -- siempre
   * primer/último elemento visual de `superficie`, ver comentario de cabecera
   * "Anclas fijas Inicio/Fin"). Focalizable/seleccionable, con nombre
   * accesible por texto.
   * Revisión posterior (petición directa del usuario, 2026-07-21): SÍ ganan
   * el mismo arrastre LIBRE 2D por puntero (`manejarInicioArrastreLibre`) y
   * el movimiento visual libre por teclado (`Alt+flecha`, rama de
   * `manejarTeclaEnAncla`) que ya tiene cualquier bloque colocado -- "no
   * fijos" en el sentido de que se pueden agarrar/mover como cualquier otro
   * bloque. Se listan explícitamente las diferencias que SÍ se mantienen,
   * ninguna oculta: sin Supr/Backspace (`manejarTeclaEnAncla` no reproduce
   * esa rama de `manejarTeclaEnBloque`) y sin flechas SIN modificador (que en
   * un bloque normal reordenan `sentencias` vía `moverBloqueEnContenedor`;
   * las anclas no están en ese array, así que no hay índice que mover) --
   * "fijos" solo en el sentido de que su posición en la LISTA (primero/
   * último) nunca cambia, exactamente igual que el arrastre por puntero de un
   * bloque normal tampoco reordena `sentencias` (TSK-0081, ver
   * `manejarInicioArrastreLibre`): mover a Inicio/Fin con el ratón es, para
   * cualquier bloque, un simple reposicionamiento VISUAL con `transform`, sin
   * tocar el modelo -- exactamente el comportamiento que ahora comparten.
   * @param {string} id
   * @param {string} etiqueta
   * @returns {HTMLButtonElement}
   */
  function crearAncla(id, etiqueta) {
    const boton = document.createElement('button');
    boton.type = 'button';
    boton.className = 'canvas-anchor';
    boton.dataset.nodeId = id;
    boton.textContent = etiqueta;
    return boton;
  }

  const anclaInicio = crearAncla(ID_ANCLA_INICIO, 'Inicio');
  const anclaFin = crearAncla(ID_ANCLA_FIN, 'Fin');

  /**
   * Envoltorio `<li>` persistente de una ancla (TSK-0073, ver comentario de
   * cabecera "Contradicción registrada y resuelta"): las anclas pasan a vivir
   * DENTRO de `superficie` (mismo contenedor con scroll que los bloques),
   * pero `superficie` es un `<ul>` -- necesitan su propio `<li>` para seguir
   * siendo semánticamente "un elemento de la lista", igual que
   * `.canvas-block-item` para un bloque colocado. Creado UNA ÚNICA VEZ (nunca
   * dentro de `renderizar()`) y reutilizado entre renders vía
   * `superficie.replaceChildren(...)`: conserva la garantía ya aprobada en
   * TSK-0055 de que la ancla es la MISMA referencia de nodo DOM tras
   * cualquier `renderizar()`.
   * @param {HTMLButtonElement} ancla
   * @returns {HTMLLIElement}
   */
  function envolverAncla(ancla) {
    const item = document.createElement('li');
    item.className = 'canvas-anchor-item';
    item.append(ancla);
    return item;
  }

  const liAnclaInicio = envolverAncla(anclaInicio);
  const liAnclaFin = envolverAncla(anclaFin);

  /**
   * Engancha a una ancla (Inicio/Fin) el mismo arrastre LIBRE 2D por puntero
   * y movimiento visual libre por teclado (`Alt+flecha`) que ya tiene
   * cualquier bloque colocado (ver "Revisión posterior" en el comentario de
   * cabecera de `crearAncla`). Se hace aquí, no en `crearAncla`, porque
   * `manejarInicioArrastreLibre` necesita el `<li>` envoltorio (`item`,
   * TSK-0081: mueve el `<li>` completo) -- que `envolverAncla` construye
   * DESPUÉS de crear el `<button>`, igual que `crearItemBloque` hace para un
   * bloque normal. También restaura, al montar, cualquier posición visual ya
   * guardada en `estadoVisual` (p.ej. de una sesión anterior con
   * autoguardado) -- un bloque normal la restaura en `crearItemBloque`/
   * `renderizar()`; las anclas nunca pasan por ahí, así que se hace aquí, una
   * única vez.
   * @param {HTMLButtonElement} boton
   * @param {HTMLLIElement} item
   * @param {string} id
   * @param {string} etiqueta
   */
  function configurarArrastreAncla(boton, item, id, etiqueta) {
    boton.addEventListener('mousedown', (evento) =>
      manejarInicioArrastreLibre(/** @type {MouseEvent} */ (evento), id, item, boton)
    );
    boton.addEventListener('keydown', (evento) =>
      manejarTeclaEnAncla(/** @type {KeyboardEvent} */ (evento), id, item, etiqueta)
    );
    aplicarTransformPosicion(item, id);
  }

  configurarArrastreAncla(anclaInicio, liAnclaInicio, claveVisualAnclaInicio, 'Inicio');
  configurarArrastreAncla(anclaFin, liAnclaFin, claveVisualAnclaFin, 'Fin');

  // --------------------------------------------------------------------------
  // Papelera + Deshacer (TSK-0061, DEC-027, ver comentario de cabecera)
  // --------------------------------------------------------------------------
  // Elemento SIEMPRE presente (criterio de aceptación: "una papelera visual
  // siempre presente en el área de trabajo"), no condicionado a que haya
  // algún bloque colocado ni a ninguna selección previa. Es un `<button>`
  // nativo (alcanzable por Tab, activable con Intro/Espacio sin manejador de
  // teclado a medida) Y, a la vez, el destino del gesto de arrastre (ver
  // `manejarInicioArrastre`/`estaSobrePapelera` más abajo): las dos vías que
  // exige el criterio de aceptación conviven en el mismo elemento.
  const papelera = document.createElement('button');
  papelera.type = 'button';
  papelera.className = 'canvas-trash ui-button ui-button-peligro';
  // Mockup aprobado (imagen de referencia, 2026-07-20): icono + etiqueta
  // corta en vez de una frase completa como único texto visible (que
  // obligaba a un botón a todo lo ancho). El nombre accesible completo, con
  // la explicación de qué hace, se conserva íntegro vía `aria-label` -- un
  // lector de pantalla sigue anunciando exactamente lo mismo que antes.
  papelera.setAttribute('aria-label', 'Papelera: eliminar el bloque seleccionado');
  // Rediseño (Fase 4b): icono de trazo en vez del emoji (ya `aria-hidden`).
  papelera.append(crearIcono('papelera'), document.createTextNode('Papelera'));

  // Petición directa del usuario (comparación en vivo contra la app real,
  // 2026-07-20): la papelera pasa a vivir en la barra de herramientas,
  // junto al botón "Ejecutar" (`zonaEjecutar`/`[data-canvas-toolbar-run]`,
  // ver arriba) -- "esto hará que el canvas se organice mucho mejor" -- en
  // vez de como un control suelto debajo del lienzo. `zonaEjecutar.append`
  // aquí, ANTES de que `execution-panel.js` traslade su propio botón
  // "Ejecutar" al mismo contenedor (ver `trasladarBotonEjecutarALaBarraDe
  // Herramientas`, que solo hace `append`, nunca `replaceChildren`): el
  // orden de inserción deja la papelera a la izquierda del botón Ejecutar
  // dentro del mismo grupo.
  zonaEjecutar.append(papelera);

  /** Botón "Deshacer" (TSK-0061): oculto salvo justo tras una eliminación.
   * Vive fuera de `superficie` (no es un bloque de la secuencia) para no
   * interferir con `calcularIndiceObjetivo`/`renderizar`. */
  const botonDeshacer = document.createElement('button');
  botonDeshacer.type = 'button';
  // DEC-180: la tecla secundaria de las piezas base, como el resto de la
  // barra (Fase 4b). Hasta DEC-180 no llegaba a verse nunca (nacía oculta).
  botonDeshacer.className = 'canvas-undo ui-button ui-button-secundario';
  botonDeshacer.hidden = true;

  // --------------------------------------------------------------------------
  // Capa de flechas visuales (TSK-0056, geometría simplificada por TSK-0060,
  // reubicada de raíz + extendida a ramas/cuerpo por TSK-0073, ver comentario
  // de cabecera "TSK-0073" para el detalle completo de la corrección).
  // --------------------------------------------------------------------------
  // Objetivo: dibujar una flecha real por cada conexión lógica. "Conectar A
  // con B" significa, en este modelo, que B ocupe la posición siguiente a A
  // en la lista vertical -- el ORDEN de `programaActual.cuerpo.sentencias`
  // (o de las sentencias de cualquier rama/cuerpo anidado, TSK-0073) es
  // exactamente esa información, la misma que usa `renderizar()`.
  //
  // Se dibuja una línea entre cada par de nodos CONSECUTIVOS de cada
  // contenedor: "Inicio, bloque 1, bloque 2, ..., bloque N, Fin" para la
  // Secuencia raíz (incluida, por tanto, Inicio -> Fin directamente cuando
  // N=0), y un patrón equivalente por cada rama/cuerpo (ver `dibujarCadena`,
  // más abajo).
  //
  // (TSK-0073) `capaConexiones` es ahora HIJA DIRECTA de `superficie`
  // (`.canvas-surface`, el mismo contenedor con `overflow-y: auto` que los
  // bloques -- antes vivía en `seccion`/`.canvas`, el contenedor EXTERIOR sin
  // scroll propio, causa raíz documentada del bug de desalineación de
  // DEC-027/TSK-0066): se dimensiona al contenido COMPLETO de `superficie`
  // (`dimensionarCapaConexiones`, más abajo) y sus coordenadas se calculan en
  // el sistema de referencia propio de `superficie` (`puntoCentral`, más
  // abajo), así que el navegador la desplaza de forma NATIVA junto con los
  // bloques al hacer scroll -- ya NO existe ningún listener de `scroll` que
  // recalcule nada (eliminado, ver comentario de cabecera "TSK-0073").
  const capaConexiones = document.createElementNS(SVG_NS, 'svg');
  capaConexiones.setAttribute('class', 'canvas-connections');
  capaConexiones.setAttribute('aria-hidden', 'true');
  capaConexiones.setAttribute('focusable', 'false');

  /** Id del marcador de punta de flecha, único por instancia. */
  const idMarcadorFlecha = `canvas-connection-arrow-${contadorInstanciasCanvas}`;
  const defsConexiones = document.createElementNS(SVG_NS, 'defs');
  const marcadorFlecha = document.createElementNS(SVG_NS, 'marker');
  marcadorFlecha.setAttribute('id', idMarcadorFlecha);
  marcadorFlecha.setAttribute('class', 'canvas-connection-arrowhead');
  marcadorFlecha.setAttribute('markerWidth', '8');
  marcadorFlecha.setAttribute('markerHeight', '8');
  marcadorFlecha.setAttribute('refX', '7');
  marcadorFlecha.setAttribute('refY', '4');
  marcadorFlecha.setAttribute('orient', 'auto-start-reverse');
  marcadorFlecha.setAttribute('markerUnits', 'userSpaceOnUse');
  const puntaFlecha = document.createElementNS(SVG_NS, 'path');
  puntaFlecha.setAttribute('d', 'M0,0 L8,4 L0,8 Z');
  marcadorFlecha.append(puntaFlecha);
  defsConexiones.append(marcadorFlecha);
  capaConexiones.append(defsConexiones);

  // --------------------------------------------------------------------------
  // TSK-0194: capa interactiva de flechas (ver comentario de cabecera
  // "TSK-0194"). Vive DENTRO de `superficie` (mismo contenedor con scroll,
  // mismo sistema de coordenadas que `capaConexiones`/`puntoCentral`,
  // TSK-0073), envuelta en su PROPIO `<li>` -- igual que las anclas
  // (`liAnclaInicio`/`liAnclaFin`), creado UNA ÚNICA VEZ y reutilizado entre
  // renders -- para que `renderizar()` pueda seguir reconstruyendo el resto
  // de la lista con `replaceChildren()` sin perder esta referencia.
  const capaFlechasInteractivas = document.createElementNS(SVG_NS, 'svg');
  capaFlechasInteractivas.setAttribute('class', 'canvas-interactive-arrows');
  // Decorativa (aria-hidden, igual que `capaConexiones`): la relación real
  // "flecha de A a B" la anuncia la propia live region tras cada acción
  // (`aplicarAccionArcoInteractivo`/`eliminarArcoDeExtremoInteractivo`, más
  // abajo) y el `aria-label` de cada `<button>` real de esta capa
  // (`.canvas-port`/`.canvas-arrow-handle`, en `capaPuertos`) -- las líneas
  // en sí nunca son la única fuente de esa información (WCAG 2.2 SC 1.4.1).
  capaFlechasInteractivas.setAttribute('aria-hidden', 'true');
  capaFlechasInteractivas.setAttribute('focusable', 'false');

  // TSK-0196 (DEC-069 fase 3, punto 2 del comentario de cabecera "TSK-0196"):
  // punta de flecha propia de esta capa, mismo patrón que `defsConexiones`/
  // `marcadorFlecha` de arriba (`capaConexiones`) -- reutiliza la clase YA
  // estilizada `canvas-connection-arrowhead` (`diagram-editor.css`, fuera de
  // `rutas_permitidas`: solo se REFERENCIA el selector ya existente, nunca se
  // edita ese fichero) para que ambas capas compartan idéntico lenguaje
  // visual sin duplicar declaraciones de color. Id único por instancia, igual
  // que `idMarcadorFlecha`.
  const idMarcadorFlechaInteractiva = `canvas-interactive-arrow-${contadorInstanciasCanvas}`;
  const defsFlechasInteractivas = document.createElementNS(SVG_NS, 'defs');
  const marcadorFlechaInteractiva = document.createElementNS(SVG_NS, 'marker');
  marcadorFlechaInteractiva.setAttribute('id', idMarcadorFlechaInteractiva);
  marcadorFlechaInteractiva.setAttribute('class', 'canvas-connection-arrowhead');
  marcadorFlechaInteractiva.setAttribute('markerWidth', '8');
  marcadorFlechaInteractiva.setAttribute('markerHeight', '8');
  marcadorFlechaInteractiva.setAttribute('refX', '7');
  marcadorFlechaInteractiva.setAttribute('refY', '4');
  marcadorFlechaInteractiva.setAttribute('orient', 'auto-start-reverse');
  marcadorFlechaInteractiva.setAttribute('markerUnits', 'userSpaceOnUse');
  const puntaFlechaInteractiva = document.createElementNS(SVG_NS, 'path');
  puntaFlechaInteractiva.setAttribute('d', 'M0,0 L8,4 L0,8 Z');
  marcadorFlechaInteractiva.append(puntaFlechaInteractiva);
  defsFlechasInteractivas.append(marcadorFlechaInteractiva);
  capaFlechasInteractivas.append(defsFlechasInteractivas);

  /** Contenedor de los controles REALES (`<button>` nativos) de esta capa:
   * un ancla por puerto (`.canvas-port`) y un asa por flecha
   * (`.canvas-arrow-handle`), ver `renderizarCapaInteractiva`. Separado del
   * `<svg>` (que solo pinta líneas decorativas) porque un `<button>` dentro
   * de un `<svg>` foreignObject complicaría innecesariamente el DOM seguro
   * (docs/03-frontend.md §8.2) sin ninguna ventaja real aquí. */
  const capaPuertos = document.createElement('div');
  capaPuertos.className = 'canvas-port-layer';

  const liCapaInteractiva = document.createElement('li');
  liCapaInteractiva.className = 'canvas-arrow-layer-item';
  liCapaInteractiva.append(capaFlechasInteractivas, capaPuertos);

  /** Resumen PERSISTENTE (no en vivo, criterio de aceptación 4: "el nº de
   * problemas se anuncia") del recuento de violaciones actuales de
   * `validarGrafoArcos` sobre `grafoInteractivo` -- DELIBERADAMENTE no una
   * segunda `aria-live` (ya existe una única live region canónica en este
   * panel, `estado`, más abajo, que varios tests localizan esperando un
   * único resultado, ver comentario ya existente junto a `etiquetaZoom`):
   * el recuento CONCRETO de cada acción de esta capa (crear/reconectar/
   * borrar una flecha) SÍ se anuncia por esa misma `estado`
   * (`mensajeConteoViolaciones`, más abajo); este párrafo es la forma de
   * consultar el recuento en cualquier momento, no solo justo tras una
   * acción, para un usuario de lector de pantalla que navegue por el
   * documento en vez de esperar un anuncio. */
  const resumenValidacionInteractiva = document.createElement('p');
  resumenValidacionInteractiva.className = 'canvas-arrow-validation-summary';
  resumenValidacionInteractiva.textContent = 'Capa de flechas interactiva: sin avisos de validación.';

  /**
   * TSK-0195 (DEC-069 punto 3/DEC-070): región ASERTIVA (`role="alert"`)
   * con la lista CONCRETA de errores de `compilarGrafoAIr` cuando el grafo
   * NO resuelve a un programa válido -- distinta, a propósito, de
   * `resumenValidacionInteractiva` (que es solo la pista NO bloqueante de
   * `validarGrafoArcos`, DEC-070): esta región refleja la AUTORIDAD
   * estructural real (la puerta de "Ejecutar"/"Corrección", DEC-070) y
   * es la respuesta a "muestra la lista concreta de errores al alumno" de
   * la ficha, ya que `execution-panel.js`/`autocorrection-panel.js` (fuera
   * de las rutas permitidas de esta tarea) solo exponen un mensaje
   * genérico cuando no reciben un `Programa` válido (ver
   * `editor-controller.js#manejarCambioGrafoInteractivo`). Oculta cuando no
   * hay errores (DOM seguro: `textContent` en cada `<li>`, nunca `innerHTML`
   * con datos externos, docs/03-frontend.md §8.2).
   */
  const tituloErroresCompilacion = document.createElement('p');
  tituloErroresCompilacion.className = 'canvas-graph-compile-errors-heading';
  tituloErroresCompilacion.textContent =
    'El diagrama no se puede ejecutar ni corregir todavía: revisa estos problemas de conexión.';
  const listaErroresCompilacion = document.createElement('ul');
  listaErroresCompilacion.className = 'canvas-graph-compile-errors-list';
  const regionErroresCompilacion = document.createElement('div');
  regionErroresCompilacion.className = 'canvas-graph-compile-errors';
  regionErroresCompilacion.setAttribute('role', 'alert');
  regionErroresCompilacion.hidden = true;
  regionErroresCompilacion.append(tituloErroresCompilacion, listaErroresCompilacion);

  /** Panel de estado: es a la vez el "indicio visual" y la "live region". */
  const estado = document.createElement('p');
  // Rediseño (Fase 4b): la consola de estado de las piezas base, al pie
  // del lienzo. El «>» y el cursor son decorativos (no se leen).
  estado.className = 'canvas-status ui-consola ui-consola-linea';
  estado.dataset.cursor = '';
  estado.setAttribute('role', 'status');
  estado.setAttribute('aria-live', 'polite');
  estado.setAttribute('aria-atomic', 'true');
  // `mensajeEstadoInicial` (opcional y aditivo: sin él, texto por defecto y
  // comportamiento idéntico al anterior) existe porque este módulo se
  // DESTRUYE y se RECONSTRUYE entero en cada cambio de programa
  // (`editor-controller.js#reconstruirDependientes`). Como `estado` es un
  // elemento NUEVO en cada reconstrucción, nacía siempre con el texto por
  // defecto y borraba el anuncio que `anunciar()` acababa de emitir una
  // microtarea antes: "Bloque Salida de datos colocado…", "Bloque X
  // eliminado…", "Bloque X movido a la posición 2 de 3…". Es decir, la live
  // region quedaba muerta justo para las acciones que más importa anunciar, y
  // un lector de pantalla se quedaba con "Todavía no se ha colocado ningún
  // bloque" con el diagrama ya lleno. El controlador lee el mensaje vigente
  // antes de destruir (`mensajeEstado()`) y lo pasa aquí al reconstruir.
  estado.textContent =
    typeof opciones.mensajeEstadoInicial === 'string' && opciones.mensajeEstadoInicial.length > 0
      ? opciones.mensajeEstadoInicial
      : 'Área de trabajo lista. Todavía no se ha colocado ningún bloque.';

  // Orden de lectura/tabulación: Inicio antes de los bloques colocados, Fin
  // después -- refleja "todo diagrama tiene un inicio y un fin". `superficie`
  // (TSK-0073) ahora contiene, en este orden, `capaConexiones` + `liAnclaInicio`
  // + un `<li>` por bloque colocado + `liAnclaFin` (reconstruidos por
  // `renderizar()`, más abajo); `seccion` solo agrupa `superficie` junto al
  // resto de controles del canvas que SÍ quedan fuera de su scroll (papelera,
  // deshacer, estado). La capa de flechas se inserta ANTES del resto del
  // contenido de `superficie` a propósito: en ausencia de `z-index`
  // explícito, el orden en el DOM decide el orden de pintado, así que anclas
  // y bloques quedan siempre visualmente por encima de las líneas (que además
  // tienen `pointer-events: none`).
  // Envoltorio del área escalable (petición directa del usuario, zoom real):
  // `transform: scale`, a diferencia de la propiedad `zoom`, NUNCA reflowa
  // el layout de sus hermanos (papelera/deshacer/estado, ni el panel de
  // ejecución que viene después en la página) -- sin este envoltorio, hacer
  // zoom por encima del 100% pintaría el contenido escalado POR ENCIMA de
  // esos elementos en vez de empujarlos. `aplicarZoom` (arriba) fija su
  // `height` real en cada cambio de nivel para que el resto de la página
  // reserve el espacio correcto.
  const viewportZoom = document.createElement('div');
  viewportZoom.className = 'canvas-zoom-viewport';
  viewportZoom.append(superficie);
  superficie.style.transformOrigin = 'top left';

  // La papelera ya no es hija directa de `seccion`: vive dentro de
  // `zonaEjecutar` (barra de herramientas, ver más arriba). TSK-0194:
  // `resumenValidacionInteractiva` se inserta ANTES de `estado` -- un párrafo
  // más, sin `aria-live` propio (ver su comentario de creación, más arriba),
  // así que no compite con la única live region de este panel.
  seccion.append(
    barraHerramientas,
    titulo,
    ayuda,
    viewportZoom,
    botonDeshacer,
    resumenValidacionInteractiva,
    regionErroresCompilacion,
    estado
  );

  // `superficie` (`.canvas-surface`) es ahora el contenedor de referencia
  // ("position: relative", ver canvas.css) para que `capaConexiones` -- HIJA
  // suya, TSK-0073 -- se posicione en su mismo sistema de coordenadas de
  // contenido/scroll. Se expresa como regla CSS estática (permitido en esta
  // tarea), no vía `element.style.*` aquí: es un valor constante, no depende
  // de ninguna posición dinámica.

  /**
   * @param {string} mensaje
   * @param {{ aviso?: boolean }} [opcionesAnuncio]
   */
  function anunciar(mensaje, { aviso = false } = {}) {
    estado.textContent = mensaje;
    estado.classList.toggle('canvas-status-aviso', aviso);
  }

  /**
   * @returns {NodoModelo[]}
   */
  function sentenciasActuales() {
    return /** @type {NodoModelo[]} */ (
      /** @type {Record<string, unknown>} */ (programaActual.cuerpo).sentencias
    );
  }

  /** Id de la Secuencia raíz de `programaActual` (TSK-0072): tratada como
   * "un contenedor más" para `conectarBloque`/`desconectarBloque`/
   * `moverBloqueEnContenedor` de `connectors.js`, exactamente igual que
   * cualquier `rama_si`/`rama_sino`/`cuerpo` anidado -- es lo que permite
   * unificar la colocación/eliminación/reordenado de nivel superior y
   * anidado en una única implementación (ver comentario de cabecera
   * "TSK-0072"). Función (no una constante fijada una vez) porque, aunque en
   * la práctica el `id` de la Secuencia raíz nunca cambia entre operaciones
   * (todas la reconstruyen preservando su `id`), leerlo de
   * `programaActual` en cada llamada evita depender de esa invariante de
   * forma implícita.
   * @returns {string}
   */
  function idContenedorRaiz() {
    return /** @type {string} */ (/** @type {Record<string, unknown>} */ (programaActual.cuerpo).id);
  }

  /**
   * Sentencias actuales del contenedor (Secuencia raíz o cualquier rama/
   * cuerpo anidado) identificado por `contenedorId` (TSK-0072): generalización
   * de `sentenciasActuales()` para operar sobre cualquier nivel de anidamiento,
   * localizando el nodo `Secuencia` real en `programaActual` mediante
   * `buscarNodoPorId` (`editor-model.js`, ya importada, nunca reimplementada).
   * @param {string} contenedorId
   * @returns {NodoModelo[]}
   */
  function sentenciasDe(contenedorId) {
    const contenedor = buscarNodoPorId(programaActual, contenedorId);
    if (!contenedor || contenedor.nodo !== TIPO_NODO.SECUENCIA) {
      throw new TypeError(`'${contenedorId}' no identifica una Secuencia existente en el programa`);
    }
    return /** @type {NodoModelo[]} */ (/** @type {Record<string, unknown>} */ (contenedor).sentencias);
  }

  /**
   * Centro de `elemento` en el sistema de coordenadas de CONTENIDO de
   * `superficie` (TSK-0073; antes, en `seccion`/`.canvas`, ver comentario de
   * cabecera "TSK-0073"): una coordenada ESTABLE sea cual sea el `scrollTop`/
   * `scrollLeft` actual de `superficie`, porque resta su
   * `getBoundingClientRect` (que NO cambia al desplazar SU PROPIO contenido:
   * `superficie` es el elemento que hace scroll, no algo que se desplaza
   * dentro de un ancestro) y vuelve a SUMAR su `scrollTop`/`scrollLeft`
   * actual -- normalizando así cualquier desplazamiento visible del momento
   * en que se llama. Es esta invarianza frente al scroll la que permite
   * eliminar el listener de `scroll` (ver comentario de cabecera): una vez
   * calculada, la coordenada no necesita recalcularse solo porque el usuario
   * haya desplazado `superficie` -- es el propio navegador quien mueve
   * `capaConexiones` (hija de `superficie`) junto con el resto de su
   * contenido.
   * @param {HTMLElement} elemento
   * @returns {{ x: number, y: number }}
   */
  function puntoCentral(elemento) {
    const base = superficie.getBoundingClientRect();
    const rect = elemento.getBoundingClientRect();
    // Corrección de zoom (ver comentario junto a `nivelZoom`): `rect`/`base`
    // vienen de `getBoundingClientRect`, que SÍ refleja el `transform: scale`
    // de `superficie` -- la diferencia entre ambos ya está en píxeles DE
    // PANTALLA (escalados). `capaConexiones` (el `<svg>` que consume este
    // valor) vive DENTRO de `superficie`, así que el propio `transform` la
    // reescala una SEGUNDA vez al pintarla: hay que devolver la coordenada
    // en el sistema de referencia SIN escalar (dividiendo por `nivelZoom`)
    // para que, tras esa segunda escalada automática del navegador, la
    // flecha quede exactamente donde toca a cualquier nivel de zoom.
    return {
      x: (rect.left - base.left) / nivelZoom + superficie.scrollLeft + rect.width / nivelZoom / 2,
      y: (rect.top - base.top) / nivelZoom + superficie.scrollTop + rect.height / nivelZoom / 2,
    };
  }

  /**
   * Dimensiona `capaConexiones` al contenido COMPLETO de `superficie`
   * (`scrollWidth`/`scrollHeight`, no `clientWidth`/`clientHeight`, que solo
   * cubrirían el área VISIBLE actual, TSK-0073): con `width`/`height`
   * establecidos como atributos SVG (no CSS), 1 unidad de usuario del `<svg>`
   * equivale a 1px, el mismo sistema de coordenadas que usa `puntoCentral`.
   * Sin esto, cualquier línea con una coordenada más allá del alto/ancho
   * VISIBLE actual de `superficie` quedaría recortada por su propio
   * `overflow-y: auto` en cuanto se desplazara fuera de la vista.
   */
  function dimensionarCapaConexiones() {
    // Corrección de zoom (ver `puntoCentral`, mismo motivo: este `<svg>`
    // vive DENTRO del elemento con `transform: scale`, así que su propio
    // tamaño se reescala una segunda vez al pintarse -- se divide por
    // `nivelZoom` para que el tamaño EFECTIVO en pantalla siga cubriendo
    // exactamente `scrollWidth`/`scrollHeight` (el contenido real, ya
    // escalado) sea cual sea el zoom actual.
    capaConexiones.setAttribute('width', String(superficie.scrollWidth / nivelZoom));
    capaConexiones.setAttribute('height', String(superficie.scrollHeight / nivelZoom));
  }

  /**
   * CORREGIDO (regresión real reportada por el usuario: las flechas dentro
   * de una rama/cuerpo de bucle no se veían al añadir bloques nuevos --
   * causa raíz encontrada por inspección en un navegador real, no supuesta):
   * `capaFlechasInteractivas` (el `<svg class="canvas-interactive-arrows">`
   * de esta capa) se dimensiona en CSS con `width: 100%; height: 100%`
   * (canvas.css) relativas a su contenedor inmediato, `liCapaInteractiva`
   * (`<li class="canvas-arrow-layer-item">`), que a su vez es
   * DELIBERADAMENTE `width: 0; height: 0` (para no interceptar clic/arrastre
   * sobre el resto del canvas con una caja invisible del tamaño completo,
   * mismo patrón que `capaPuertos`/`.canvas-port-layer`, cuyos botones se
   * posicionan cada uno por separado). El resultado, en CUALQUIER navegador:
   * el `<svg>` raíz de esta capa queda con viewport 0×0 -- y un `<svg>` raíz
   * con viewport 0×0 NO PINTA NINGÚN contenido, ni el suyo propio ni el de
   * sus hijos, aunque tengan `overflow: visible` (verificado directamente:
   * un `<line>`/`<rect>` de prueba con coordenadas y color explícitos dentro
   * de este `<svg>` no se pintaba en pantalla pese a que sus atributos y
   * estilos computados eran correctos). Mismo arreglo que ya usa
   * `capaConexiones` (`dimensionarCapaConexiones`, arriba): fijar `width`/
   * `height` como ATRIBUTOS SVG explícitos (que SÍ ganan a la regla CSS
   * `100%`/`100%` de un contenedor 0×0, al no depender de resolver ningún
   * porcentaje) en vez de dejar que el `<svg>` intente heredar el tamaño de
   * un `<li>` que nunca debe tenerlo.
   */
  function dimensionarCapaFlechasInteractivas() {
    capaFlechasInteractivas.setAttribute('width', String(superficie.scrollWidth / nivelZoom));
    capaFlechasInteractivas.setAttribute('height', String(superficie.scrollHeight / nivelZoom));
  }

  /**
   * Crea una `<line>` (más, opcionalmente, un `<text>` de etiqueta) entre
   * `origen.elemento` y `destino.elemento` (TSK-0073, extraído del cuerpo
   * previamente en línea de `actualizarConexiones` para reutilizarlo tanto en
   * la cadena raíz como en cualquier rama/cuerpo). DOM seguro
   * (docs/03-frontend.md §8.2): `createElementNS`/`textContent`, nunca HTML
   * de cadena.
   * @param {{ id: string, elemento: HTMLElement }} origen
   * @param {{ id: string, elemento: HTMLElement }} destino
   * @param {{ etiqueta?: string, claseExtra?: string }} [opciones]
   * @returns {SVGElement[]}
   */
  function crearLinea(origen, destino, { etiqueta, claseExtra } = {}) {
    const p1 = puntoCentral(origen.elemento);
    const p2 = puntoCentral(destino.elemento);

    const linea = document.createElementNS(SVG_NS, 'line');
    linea.setAttribute('class', claseExtra ? `canvas-connection-line ${claseExtra}` : 'canvas-connection-line');
    linea.setAttribute('aria-hidden', 'true');
    linea.setAttribute('x1', String(p1.x));
    linea.setAttribute('y1', String(p1.y));
    linea.setAttribute('x2', String(p2.x));
    linea.setAttribute('y2', String(p2.y));
    linea.setAttribute('marker-end', `url(#${idMarcadorFlecha})`);
    // Datos estructurales (no de presentación): permiten a los tests
    // comprobar QUÉ conexión representa cada línea sin depender de layout
    // real (jsdom no calcula geometría por defecto).
    linea.dataset.connectionFrom = origen.id;
    linea.dataset.connectionTo = destino.id;

    const elementos = [/** @type {SVGElement} */ (linea)];

    // TSK-0073 (criterio de aceptación: "dos flechas etiquetadas V/F"):
    // etiqueta VISIBLE decorativa, `aria-hidden` como el resto de la capa --
    // la relación real ("Si se cumple"/"Si no se cumple") ya la anuncia el
    // encabezado accesible de `crearZonaRama` (`.canvas-branch-heading`), así
    // que esta etiqueta nunca es la ÚNICA fuente de esa información (WCAG 2.2
    // SC 1.4.1, mismo criterio ya aplicado al resto de la capa de flechas).
    if (etiqueta) {
      const texto = document.createElementNS(SVG_NS, 'text');
      texto.setAttribute('class', 'canvas-connection-label');
      texto.setAttribute('aria-hidden', 'true');
      texto.setAttribute('x', String((p1.x + p2.x) / 2 + 6));
      texto.setAttribute('y', String((p1.y + p2.y) / 2));
      texto.textContent = etiqueta;
      elementos.push(/** @type {SVGElement} */ (texto));
    }

    return elementos;
  }

  /**
   * Recalcula y redibuja TODAS las flechas visuales, de forma RECURSIVA
   * (TSK-0073, sin límite de profundidad, mismo patrón de recursión que
   * `crearItemBloque`/`crearZonaRama` de TSK-0072): la Secuencia raíz
   * (Inicio -> primer bloque -> ... -> último bloque -> Fin, o Inicio -> Fin
   * directamente si está vacía) y, además, para cada Condicional/Bucle de
   * CUALQUIER contenedor (raíz o rama/cuerpo anidado), sus propias flechas
   * V/F o de retorno (ver comentario de cabecera "TSK-0073" para el detalle
   * completo). Se llama tras `renderizar()` y tras cualquier reordenado
   * (teclado o arrastre por puntero); YA NO hace falta llamarla en `scroll`
   * (ver comentario de cabecera "TSK-0073": la corrección de raíz hace que
   * las coordenadas calculadas aquí sean estables frente al scroll).
   */
  function actualizarConexiones() {
    /** @type {SVGElement[]} */
    const elementosSvg = [];

    /**
     * Dibuja la cadena secuencial de `sentencias` desde `entrada` hasta
     * `salida` (si se indica; se omite la última flecha si no hay salida,
     * p.ej. un Condicional que es la última sentencia de su contenedor), y
     * RECURSIVAMENTE las flechas propias de cualquier Condicional/Bucle
     * dentro de `sentencias`.
     * @param {NodoModelo[]} sentencias
     * @param {{ id: string, elemento: HTMLElement }} entrada
     * @param {{ id: string, elemento: HTMLElement } | null} salida
     * @param {{ etiquetaPrimeraLinea?: string, indicadorVacio?: HTMLElement | null, claseUltimaLinea?: string }} [opciones]
     */
    function dibujarCadena(sentencias, entrada, salida, opciones = {}) {
      /** @type {{ id: string, elemento: HTMLElement }[]} */
      const nodosEnOrden = [entrada];
      if (sentencias.length === 0) {
        // Rama/cuerpo sin contenido (criterio de aceptación: "hacia un
        // indicador de rama vacía si no tiene contenido"): si esta cadena
        // tiene un indicador propio (siempre el caso salvo la Secuencia
        // raíz, que no lo necesita -- Inicio -> Fin ya es un destino real),
        // se usa como único nodo intermedio.
        if (opciones.indicadorVacio) {
          nodosEnOrden.push({ id: `${entrada.id}-vacio`, elemento: opciones.indicadorVacio });
        }
      } else {
        for (const nodo of sentencias) {
          const boton = botonesPorId.get(nodo.id);
          // Defensivo: en teoría todo `nodo.id` de `sentencias` tiene su
          // botón, pero si `actualizarConexiones` se llamara antes de que
          // `renderizar()` haya terminado de poblar `botonesPorId`, se omite
          // ese nodo en vez de dibujar una flecha hacia un elemento
          // inexistente.
          if (boton) nodosEnOrden.push({ id: nodo.id, elemento: boton });
        }
      }
      if (salida) nodosEnOrden.push(salida);

      for (let indice = 0; indice < nodosEnOrden.length - 1; indice += 1) {
        const origen = nodosEnOrden[indice];
        const destino = nodosEnOrden[indice + 1];
        const etiqueta = indice === 0 ? opciones.etiquetaPrimeraLinea : undefined;
        const claseExtra =
          opciones.claseUltimaLinea && indice === nodosEnOrden.length - 2 ? opciones.claseUltimaLinea : undefined;
        elementosSvg.push(...crearLinea(origen, destino, { etiqueta, claseExtra }));
      }

      // Recursión (TSK-0073): cada Condicional/Bucle de ESTA MISMA lista gana
      // sus propias flechas V/F (Condicional) o de entrada+retorno (Bucle),
      // sin límite de profundidad -- exactamente el mismo patrón que permite
      // el anidamiento arbitrario en `crearItemBloque`/`crearZonaRama`
      // (TSK-0072).
      for (let indice = 0; indice < sentencias.length; indice += 1) {
        const nodo = sentencias[indice];
        if (!esNodoConRamas(nodo)) continue;
        const boton = botonesPorId.get(nodo.id);
        if (!boton) continue;
        const nodoEntrada = { id: nodo.id, elemento: boton };

        // Convergencia (criterio de aceptación: "hacia el bloque siguiente al
        // Condicional en SU CONTENEDOR PADRE (si lo hay)"): el siguiente
        // hermano en esta MISMA `sentencias` -- nunca en un contenedor
        // exterior, ver comentario de cabecera "Decisión de alcance
        // DELIBERADA" sobre por qué no se sustituye la flecha ordinaria ya
        // existente.
        const siguiente = sentencias[indice + 1];
        const botonSiguiente = siguiente ? botonesPorId.get(siguiente.id) : undefined;
        const convergencia = botonSiguiente ? { id: siguiente.id, elemento: botonSiguiente } : null;

        if (nodo.nodo === TIPO_NODO.CONDICIONAL) {
          const ramaSi = /** @type {NodoModelo} */ (nodo.rama_si);
          const sentenciasSi = /** @type {NodoModelo[]} */ (
            /** @type {Record<string, unknown>} */ (ramaSi).sentencias
          );
          dibujarCadena(sentenciasSi, nodoEntrada, convergencia, {
            etiquetaPrimeraLinea: 'V',
            indicadorVacio: indicadoresVaciosPorContenedor.get(ramaSi.id) ?? null,
          });

          const ramaSino = /** @type {NodoModelo | null} */ (nodo.rama_sino);
          if (ramaSino) {
            const sentenciasSino = /** @type {NodoModelo[]} */ (
              /** @type {Record<string, unknown>} */ (ramaSino).sentencias
            );
            dibujarCadena(sentenciasSino, nodoEntrada, convergencia, {
              etiquetaPrimeraLinea: 'F',
              indicadorVacio: indicadoresVaciosPorContenedor.get(ramaSino.id) ?? null,
            });
          }
        } else {
          // Bucle (BucleMientras/BucleFinPara/RepetirHasta): entrada al
          // cuerpo sin etiqueta + flecha de retorno (loop-back) desde su
          // último bloque de vuelta al propio Bucle -- el mismo nodo hace de
          // "entrada" y de "salida" de esta cadena. Un cuerpo vacío no
          // dibuja nada (no hay nada que recorrer ni a lo que volver,
          // decisión de alcance documentada en la cabecera del archivo).
          const cuerpo = /** @type {NodoModelo} */ (nodo.cuerpo);
          const sentenciasCuerpo = /** @type {NodoModelo[]} */ (
            /** @type {Record<string, unknown>} */ (cuerpo).sentencias
          );
          if (sentenciasCuerpo.length > 0) {
            dibujarCadena(sentenciasCuerpo, nodoEntrada, nodoEntrada, {
              claseUltimaLinea: 'canvas-connection-line-loopback',
            });
          }
        }
      }
    }

    dibujarCadena(sentenciasActuales(), { id: ID_ANCLA_INICIO, elemento: anclaInicio }, { id: ID_ANCLA_FIN, elemento: anclaFin });

    // `defsConexiones` (el marcador de punta de flecha) es el único hijo que
    // sobrevive entre redibujados: no se recrea en cada llamada, solo las
    // líneas/etiquetas.
    capaConexiones.replaceChildren(defsConexiones, ...elementosSvg);
    dimensionarCapaConexiones();
  }

  // --------------------------------------------------------------------------
  // TSK-0194: capa interactiva de flechas -- lógica que SÍ toca el DOM (la
  // lógica pura vive antes de `crearVistaCanvas`, ver comentario de cabecera
  // "TSK-0194"). Diseño: `estaSobrePapelera`/`limitarDentroDelContenedor` NO
  // se reimplementan; la primera se reutiliza tal cual (más abajo), la
  // segunda no aplica aquí (los puertos/asas no tienen posición propia que
  // clampar, solo se posicionan sobre el punto ya calculado de un puerto).
  // --------------------------------------------------------------------------

  /**
   * El elemento DOM real que representa `nodeId` (un bloque colocado, o una
   * de las dos anclas fijas `'inicio'`/`'fin'`) -- la misma referencia que ya
   * usa `actualizarConexiones`/`dibujarCadena` para calcular `puntoCentral`.
   * `null` si `nodeId` no tiene, todavía, ningún elemento renderizado (no
   * debería ocurrir tras un `renderizar()` completo, pero se comprueba de
   * forma defensiva, mismo criterio que `dibujarCadena`).
   * @param {string} nodeId
   * @returns {HTMLElement | null}
   */
  function elementoDeNodo(nodeId) {
    if (nodeId === ID_ANCLA_INICIO) return anclaInicio;
    if (nodeId === ID_ANCLA_FIN) return anclaFin;
    return botonesPorId.get(nodeId) ?? null;
  }

  /**
   * Punto de un puerto concreto de `nodeId`, en el MISMO sistema de
   * coordenadas de contenido de `superficie` que usa `puntoCentral`
   * (estable frente al scroll, corregido por el zoom actual, TSK-0073/
   * mockup de zoom) -- pero anclado al BORDE de la caja del nodo
   * (`anclaLocalDePuerto`, pura) en vez de a su centro. `null` si `nodeId` no
   * tiene elemento renderizado todavía.
   * @param {string} nodeId
   * @param {import('./arrow-connection-graph.js').ValorPuerto} puerto
   * @returns {{ x: number, y: number } | null}
   */
  function puntoDePuerto(nodeId, puerto) {
    const elemento = elementoDeNodo(nodeId);
    if (!elemento) return null;
    const base = superficie.getBoundingClientRect();
    const rect = elemento.getBoundingClientRect();
    const local = anclaLocalDePuerto({ width: rect.width, height: rect.height }, puerto);
    return {
      x: (rect.left - base.left) / nivelZoom + superficie.scrollLeft + local.x / nivelZoom,
      y: (rect.top - base.top) / nivelZoom + superficie.scrollTop + local.y / nivelZoom,
    };
  }

  /**
   * Nombre legible de un extremo `(nodeId, puerto)`, para el `aria-label` de
   * cada ancla/asa y para los mensajes de la live region (criterio de
   * aceptación 3/4): reutiliza `etiquetasPorId` (la misma etiqueta visible
   * que ya usa el resto de este módulo para bloques/anclas), nunca un id
   * técnico interno.
   * @param {string} nodeId
   * @param {import('./arrow-connection-graph.js').ValorPuerto} puerto
   * @returns {string}
   */
  function etiquetaDeExtremo(nodeId, puerto) {
    const etiquetaNodo = etiquetaDe(nodeId);
    return `${etiquetaNodo} (puerto de ${ETIQUETA_PUERTO[puerto]})`;
  }

  /**
   * Todos los descriptores de puerto `{ nodeId, puerto }` declarados por
   * `nodosInteractivos` (criterio de aceptación 1: "cada bloque muestra
   * anclas de puerto según su tipo"). `Comentario`/`'inicio'`/`'fin'` con
   * `puertosDeNodo` sin excepciones ya resuelven correctamente sus propios
   * casos (0 puertos, o solo `SALIDA`/`ENTRADA`, DEC-069 punto 1); el
   * `try/catch` es puramente defensivo (ningún tipo de `nodosInteractivos`
   * debería quedar fuera de `PUERTOS_POR_TIPO`, ver `arrow-connection-
   * graph.js`).
   * @returns {ExtremoPuerto[]}
   */
  function descriptoresPuertos() {
    /** @type {ExtremoPuerto[]} */
    const descriptores = [];
    for (const entrada of nodosInteractivos) {
      const id = typeof entrada === 'string' ? entrada : entrada.id;
      let puertos;
      try {
        puertos = puertosDeNodo(entrada);
      } catch {
        continue;
      }
      if (puertos.entrada) descriptores.push({ nodeId: id, puerto: PUERTO.ENTRADA });
      for (const puerto of puertos.salidas) descriptores.push({ nodeId: id, puerto });
    }
    return descriptores;
  }

  /**
   * Traduce `violacionesInteractivas` (DEC-069 punto 3 / DEC-070: pista no
   * bloqueante) a los dos conjuntos que necesita `renderizarCapaInteractiva`
   * para marcar `aria-invalid`/la señal no cromática (criterio de aceptación
   * 4): claves `"<nodeId>::<puerto>"` de puertos afectados, e ids de arcos
   * afectados.
   * @returns {{ puertosInvalidos: Set<string>, arcosInvalidos: Set<string> }}
   */
  function calcularConjuntosInvalidos() {
    const puertosInvalidos = new Set();
    const arcosInvalidos = new Set();
    for (const violacion of violacionesInteractivas) {
      const puertoAfectado = puertoAfectadoPorViolacion(violacion);
      if (puertoAfectado) puertosInvalidos.add(`${puertoAfectado.nodeId}::${puertoAfectado.puerto}`);
      const arcoAfectado = arcoAfectadoPorViolacion(violacion);
      if (arcoAfectado) arcosInvalidos.add(arcoAfectado);
    }
    return { puertosInvalidos, arcosInvalidos };
  }

  /**
   * Mensaje de recuento de violaciones actuales (criterio de aceptación 4:
   * "el nº de problemas se anuncia... la edición NO se bloquea", DEC-069
   * punto 3 / DEC-070): se añade al final de cada anuncio de esta capa
   * (crear/reconectar/borrar una flecha).
   * @returns {string}
   */
  function mensajeConteoViolaciones() {
    const total = violacionesInteractivas.length;
    return total === 0
      ? 'Sin avisos de validación en la capa de flechas.'
      : `${total} aviso(s) de validación en la capa de flechas (pista, no bloquea la edición ni la ejecución).`;
  }

  /** Actualiza el párrafo persistente de recuento (`resumenValidacionInteractiva`,
   * ver su comentario de creación): se llama en cada pasada de
   * `renderizarCapaInteractiva`, nunca dispara la live region por sí solo. */
  function actualizarResumenValidacionInteractiva() {
    const total = violacionesInteractivas.length;
    resumenValidacionInteractiva.textContent =
      total === 0
        ? 'Capa de flechas interactiva: sin avisos de validación.'
        : `Capa de flechas interactiva: ${total} aviso(s) de validación (pista, no bloquea la edición ni la ejecución).`;
    resumenValidacionInteractiva.classList.toggle('canvas-arrow-validation-summary-aviso', total > 0);
  }

  /**
   * TSK-0195 (DEC-069 punto 3/DEC-070): repinta `regionErroresCompilacion`
   * a partir de `resultadoCompilacionGrafo.errores` -- DOM seguro
   * (`textContent` por cada `<li>`, nunca `innerHTML`, docs/03-frontend.md
   * §8.2). Oculta la región por completo cuando no hay ningún error (grafo
   * que SÍ resuelve a programa válido).
   */
  function repintarErroresCompilacionGrafo() {
    const errores = resultadoCompilacionGrafo.errores;
    if (errores.length === 0) {
      regionErroresCompilacion.hidden = true;
      listaErroresCompilacion.replaceChildren();
      return;
    }
    const items = errores.map((error) => {
      const item = document.createElement('li');
      item.textContent = error.mensaje;
      return item;
    });
    listaErroresCompilacion.replaceChildren(...items);
    regionErroresCompilacion.hidden = false;
  }

  /**
   * TSK-0195 (DEC-069 punto 6/DEC-070): recompila `grafoInteractivo` con
   * `compilarGrafoAIr` -- la AUTORIDAD estructural real -- y repinta la
   * región de errores concretos. Puro recálculo/repintado: NO decide por sí
   * solo si hay que commitear nada a `programaActual` (eso lo deciden, cada
   * una a su manera, `recalcularGrafoInteractivo`/
   * `refrescarCapaInteractivaTrasEdicion`, más abajo).
   */
  function recalcularResultadoCompilacionGrafo() {
    resultadoCompilacionGrafo = compilarGrafoAIr(nodosInteractivos, grafoInteractivo);
    repintarErroresCompilacionGrafo();
  }

  /**
   * Re-deriva `grafoInteractivo`/`nodosInteractivos`/`violacionesInteractivas`
   * desde `programaActual`. Se llama SIEMPRE al final de `renderizar()` --
   * es decir, en cualquier acción que ya tocaba el modelo lógico (colocar/
   * reordenar/eliminar/deshacer). TSK-0195 (DEC-069 punto 6): a diferencia
   * de TSK-0194, esto YA NO es la única fuente de verdad de conectividad --
   * el grafo interactivo lo es -- pero re-derivar aquí sigue siendo
   * necesario y correcto: por la propiedad de round-trip
   * (`compilarGrafoAIr(derivarGrafoDeIr(p)) ≡ p`, ver
   * `arrow-graph-compiler.spec.js`), el grafo derivado de un árbol IR ya
   * válido siempre vuelve a compilar con éxito, así que esto nunca pierde
   * ninguna edición interactiva CONFIRMADA (ya commiteada en
   * `programaActual` por `refrescarCapaInteractivaTrasEdicion`, más abajo).
   * Cancela también cualquier "modo conexión" de teclado pendiente (un
   * origen marcado sobre un puerto que puede haber dejado de existir).
   */
  function recalcularGrafoInteractivo() {
    const derivado = derivarGrafoDeIr(programaActual);
    grafoInteractivo = derivado.grafo;
    nodosInteractivos = derivado.nodos;
    violacionesInteractivas = validarGrafoArcos(nodosInteractivos, grafoInteractivo);
    recalcularResultadoCompilacionGrafo();
    origenConexionTeclado = null;
  }

  /**
   * Recalcula `violacionesInteractivas`/`resultadoCompilacionGrafo` y vuelve
   * a pintar SOLO la capa interactiva (`renderizarCapaInteractiva`) tras una
   * edición hecha DESDE esta misma capa (crear/reconectar/borrar una
   * flecha): a propósito NO llama a `renderizar()`/`actualizarConexiones()`
   * completos -- el render derivado/la posición de los bloques no cambian
   * por esto.
   *
   * TSK-0195 (DEC-069 punto 6/DEC-070, INVERSIÓN DE AUTORIDAD -- a
   * diferencia de TSK-0194, que documentaba aquí mismo que estas ediciones
   * "nunca tocan programaActual"): si el grafo resultante SÍ compila a un
   * programa válido (`resultadoCompilacionGrafo.programa !== null`), esa
   * edición se COMMITEA de inmediato como el nuevo `programaActual` --
   * preservando `declaraciones`/`funciones` del envoltorio (el compilador
   * solo reconstruye `cuerpo`, ver la ambigüedad 2 de la cabecera de
   * `arrow-graph-compiler.js`) -- y se notifica por el mismo
   * `notificarCambioPrograma` que ya usan `colocarEnContenedor`/
   * `eliminarBloque`/`moverBloqueEnContenedor` (ninguna lógica nueva de
   * reconciliación: `editor-controller.js#manejarCambioPrograma` ya sabe
   * reconciliar el envoltorio de una función activa). Si el grafo NO
   * compila (temporalmente inválido, DEC-069 punto 3), NO se commitea nada
   * -- `programaActual` conserva el último estado lógico válido y la
   * edición sigue viva ÚNICAMENTE en `grafoInteractivo`, exactamente como
   * documentaba TSK-0194 -- y los errores concretos quedan visibles en
   * `regionErroresCompilacion`.
   *
   * LIMITACIÓN CONOCIDA Y DOCUMENTADA (no oculta, coherente con CLAUDE.md):
   * un commit con éxito notifica `notificarCambioPrograma`, que en
   * `editor-controller.js` dispara la MISMA reconstrucción completa del
   * canvas que cualquier otro cambio de origen `'canvas'` (destruye y
   * vuelve a crear `canvasVista` en el siguiente microtask) -- el foco que
   * esta función ya restaura sobre el puerto/asa recién tocado (ver
   * `aplicarAccionArcoInteractivo`/`eliminarArcoInteractivoDesdeExtremo`/
   * `eliminarArcoInteractivo`, más abajo) se pierde de inmediato en ese
   * microtask posterior, cayendo al mismo destino de foco de respaldo que
   * ya usa cualquier otra acción de origen `'canvas'` (el encabezado del
   * canvas, nunca `<body>`). Se documenta explícitamente para
   * accessibility-reviewer: es el mismo tipo de trade-off ya aceptado por
   * el resto de este controlador (colocar/eliminar/mover bloque) extendido
   * ahora a las ediciones de flecha que SÍ cambian la conectividad real;
   * no hay pérdida de foco a `<body>` en ningún caso.
   */
  function refrescarCapaInteractivaTrasEdicion() {
    violacionesInteractivas = validarGrafoArcos(nodosInteractivos, grafoInteractivo);
    recalcularResultadoCompilacionGrafo();
    renderizarCapaInteractiva();

    if (resultadoCompilacionGrafo.programa !== null) {
      programaActual = {
        ...programaActual,
        cuerpo: /** @type {Record<string, unknown>} */ (resultadoCompilacionGrafo.programa).cuerpo,
      };
      if (notificarCambioPrograma) notificarCambioPrograma(programaActual);
    }

    if (notificarCambioGrafoInteractivo) notificarCambioGrafoInteractivo(resultadoCompilacionGrafo);
  }

  /**
   * Localiza el botón `.canvas-port` de `{nodeId, puerto}` DESPUÉS de un
   * `renderizarCapaInteractiva()` (que reconstruye todos los botones desde
   * cero con `replaceChildren`, ver su comentario de cabecera) y le devuelve
   * el foco -- mismo patrón ya aprobado en este módulo por `eliminarBloque`
   * (re-enfoca `botonDeshacer` tras su propio re-render) y `reordenarBloque`
   * (re-enfoca el bloque movido): sin esto, cada crear/reconectar/borrar de
   * esta capa deja caer el foco a `<body>` (defecto real señalado por
   * accessibility-reviewer en TSK-0194, corregido aquí).
   * @param {string} nodeId
   * @param {string} puerto
   * @returns {boolean} `true` si el puerto existía en el DOM recién pintado
   *   y se ha enfocado; `false` si ya no existe (p.ej. el bloque se ha
   *   eliminado mientras tanto), para que la persona que llama pueda decidir
   *   un objetivo de foco alternativo.
   */
  function enfocarPuertoInteractivo(nodeId, puerto) {
    const boton = /** @type {HTMLButtonElement | null} */ (
      capaPuertos.querySelector(`.canvas-port[data-port-node="${nodeId}"][data-port-value="${puerto}"]`)
    );
    if (!boton) return false;
    boton.focus();
    return true;
  }

  /**
   * Análogo de `enfocarPuertoInteractivo` para el asa de una flecha
   * (`.canvas-arrow-handle`, `data-arc-id`): localiza el asa `arcoId` DESPUÉS
   * de un `renderizarCapaInteractiva()` (que reconstruye TODAS las asas desde
   * cero con `replaceChildren`) y le devuelve el foco. Necesario cuando el
   * re-render NO borra el arco (solo lo reposiciona) y hay que conservar el
   * foco sobre el mismo asa -- p.ej. el callback del `ResizeObserver`, que
   * repinta la capa por un cambio de layout sin editar la conectividad.
   * @param {string} arcoId
   * @returns {boolean} `true` si el asa existía en el DOM recién pintado y se
   *   ha enfocado; `false` si ya no existe.
   */
  function enfocarAsaInteractiva(arcoId) {
    const asa = /** @type {HTMLButtonElement | null} */ (
      capaPuertos.querySelector(`.canvas-arrow-handle[data-arc-id="${arcoId}"]`)
    );
    if (!asa) return false;
    asa.focus();
    return true;
  }

  /**
   * Análogo de `enfocarAsaInteractiva` para una de las DOS asas de extremo de
   * una flecha (`.canvas-arrow-endpoint-handle`, TSK-0205), identificada por
   * `arcoId` + `extremo` (`'origen'`/`'destino'`, `data-arc-endpoint`): tras
   * un `renderizarCapaInteractiva()` que reconstruye TODAS las asas desde cero
   * (`replaceChildren`) sin borrar el arco (p.ej. el callback del
   * `ResizeObserver`, o un Escape de teclado que solo repinta), devuelve el
   * foco al asa de extremo equivalente para no dejarlo caer a `<body>` (mismo
   * invariante de foco de TSK-0204). Es el TERCER tipo de elemento focable de
   * esta capa (puerto/asa central/asa de extremo), por eso necesita su propio
   * helper: su identidad es `arcoId` + `extremo`, no un `{nodeId, puerto}` ni
   * un `arcoId` a secas.
   * @param {string} arcoId
   * @param {'origen' | 'destino'} extremo
   * @returns {boolean} `true` si el asa de extremo existía en el DOM recién
   *   pintado y se ha enfocado; `false` si ya no existe.
   */
  function enfocarAsaExtremoInteractiva(arcoId, extremo) {
    const asa = /** @type {HTMLButtonElement | null} */ (
      capaPuertos.querySelector(
        `.canvas-arrow-endpoint-handle[data-arc-id="${arcoId}"][data-arc-endpoint="${extremo}"]`
      )
    );
    if (!asa) return false;
    asa.focus();
    return true;
  }

  /**
   * Reconstruye por completo `capaPuertos`/`capaFlechasInteractivas` a partir
   * de `grafoInteractivo`/`nodosInteractivos`/`violacionesInteractivas`
   * (criterios de aceptación 1/2/4). DOM seguro (docs/03-frontend.md §8.2):
   * solo `createElement`/`createElementNS`/`textContent`/`dataset`.
   */
  function renderizarCapaInteractiva() {
    capaPuertos.replaceChildren();
    const { puertosInvalidos, arcosInvalidos } = calcularConjuntosInvalidos();

    // Anclas de puerto (criterio de aceptación 1): una por cada
    // `{ nodeId, puerto }` que declara `descriptoresPuertos`.
    for (const descriptor of descriptoresPuertos()) {
      const punto = puntoDePuerto(descriptor.nodeId, descriptor.puerto);
      if (!punto) continue;

      const boton = document.createElement('button');
      boton.type = 'button';
      boton.className = 'canvas-port';
      boton.classList.add(`canvas-port-${descriptor.puerto}`);
      boton.dataset.portNode = descriptor.nodeId;
      boton.dataset.portValue = descriptor.puerto;
      boton.style.left = `${punto.x}px`;
      boton.style.top = `${punto.y}px`;
      boton.setAttribute('aria-label', `Puerto de ${ETIQUETA_PUERTO[descriptor.puerto]} de ${etiquetaDe(descriptor.nodeId)}`);

      if (puertosInvalidos.has(`${descriptor.nodeId}::${descriptor.puerto}`)) {
        boton.setAttribute('aria-invalid', 'true');
        boton.classList.add('canvas-port-invalido');
      }
      if (
        origenConexionTeclado &&
        origenConexionTeclado.nodeId === descriptor.nodeId &&
        origenConexionTeclado.puerto === descriptor.puerto
      ) {
        boton.classList.add('canvas-port-origen-activo');
      }

      boton.addEventListener('mousedown', (evento) =>
        manejarInicioArrastrePuerto(/** @type {MouseEvent} */ (evento), descriptor)
      );
      boton.addEventListener('keydown', (evento) => manejarTeclaEnPuerto(/** @type {KeyboardEvent} */ (evento), descriptor));
      capaPuertos.append(boton);
    }

    /**
     * Crea un asa de EXTREMO de flecha (TSK-0205, modelo draw.io: flecha
     * seleccionable con un círculo de asa en cada punta, reconectables por
     * separado). `.canvas-arrow-endpoint-handle` es una clase DISTINTA de
     * `.canvas-arrow-handle` (el asa central de selección/borrado, que se
     * conserva intacta): así ningún selector `.canvas-arrow-handle` existente
     * cambia de conteo.
     *
     * CABLEADO CRUZADO (decisión registrada en el historial de la ficha, y en
     * CLAUDE.md como contradicción resuelta): para que arrastrar el asa de un
     * extremo mueva ESE extremo dejando el contrario FIJO -- lo que pide el
     * criterio de aceptación #2 y el modelo draw.io -- hay que iniciar el
     * gesto de puerto ANCLADO en el puerto del OTRO extremo (`anclaFija`),
     * porque en `calcularAccionArco` el puerto que se agarra queda fijo y el
     * arco reconecta su extremo CONTRARIO. Ejemplo: para el asa de DESTINO
     * (punta) de A(out)->B(in), se pasa el descriptor de ORIGEN `{A, out}`:
     * arrastrar a C reconecta `hasta` -> C (A->C), origen A intacto. Reusa
     * `manejarInicioArrastrePuerto` (puntero: reconectar, o soltar en la
     * papelera para borrar la flecha entera) y `manejarTeclaEnPuerto`
     * (teclado: Enter/Espacio inicia el modo de 2 pasos, Escape cancela,
     * Supr/Backspace borra la flecha de ese extremo), SIN reimplementar su
     * lógica.
     * @param {'origen' | 'destino'} extremo Punta donde se dibuja el asa.
     * @param {{ x: number, y: number }} punto Coordenada del asa (p1/p2).
     * @param {ExtremoPuerto} anclaFija Descriptor del puerto del OTRO extremo
     *   (el que queda fijo): el que se pasa a los manejadores de puerto.
     * @param {import('./arrow-connection-graph.js').Arco} arco
     * @param {boolean} invalida
     */
    function crearAsaExtremo(extremo, punto, anclaFija, arco, invalida) {
      const asaExtremo = document.createElement('button');
      asaExtremo.type = 'button';
      asaExtremo.className = 'canvas-arrow-endpoint-handle';
      asaExtremo.classList.add(`canvas-arrow-endpoint-handle-${extremo}`);
      asaExtremo.dataset.arcId = arco.id;
      asaExtremo.dataset.arcEndpoint = extremo;
      asaExtremo.style.left = `${punto.x}px`;
      asaExtremo.style.top = `${punto.y}px`;
      asaExtremo.setAttribute(
        'aria-label',
        `Extremo de ${extremo} de la flecha de ${etiquetaDe(arco.desdeNodoId)} a ` +
          `${etiquetaDe(arco.hastaNodoId)}`
      );
      if (invalida) {
        asaExtremo.setAttribute('aria-invalid', 'true');
        asaExtremo.classList.add('canvas-arrow-endpoint-handle-invalido');
      }
      // Enfocar un asa de extremo selecciona la MISMA flecha (criterio de
      // aceptación #3: activar la papelera con un asa de extremo enfocada
      // borra la flecha entera, igual que con el asa central).
      asaExtremo.addEventListener('focus', () => {
        tipoSeleccionActual = 'arco';
        idArcoSeleccionado = arco.id;
      });
      asaExtremo.addEventListener('keydown', (evento) =>
        manejarTeclaEnPuerto(/** @type {KeyboardEvent} */ (evento), anclaFija)
      );
      asaExtremo.addEventListener('mousedown', (evento) =>
        manejarInicioArrastrePuerto(/** @type {MouseEvent} */ (evento), anclaFija)
      );
      capaPuertos.append(asaExtremo);
    }

    // Flechas + asa de borrado (criterio de aceptación 2): una `<line>`
    // decorativa (capaFlechasInteractivas) + un `<button>` real en el punto
    // medio (capaPuertos) por cada arco de `grafoInteractivo`.
    /** @type {SVGElement[]} */
    const lineas = [];
    for (const arco of grafoInteractivo.arcos) {
      const p1 = puntoDePuerto(arco.desdeNodoId, arco.desdePuerto);
      const p2 = puntoDePuerto(arco.hastaNodoId, arco.hastaPuerto);
      if (!p1 || !p2) continue;

      const invalida = arcosInvalidos.has(arco.id);

      const linea = document.createElementNS(SVG_NS, 'line');
      linea.setAttribute('class', invalida ? 'canvas-interactive-arrow-line canvas-interactive-arrow-line-invalida' : 'canvas-interactive-arrow-line');
      linea.setAttribute('x1', String(p1.x));
      linea.setAttribute('y1', String(p1.y));
      linea.setAttribute('x2', String(p2.x));
      linea.setAttribute('y2', String(p2.y));
      // TSK-0196 (punto 2 del comentario de cabecera "TSK-0196"): punta de
      // flecha, misma técnica ya aprobada de `capaConexiones`/`crearLinea`
      // (`marker-end` apuntando al `<marker>` propio de esta capa).
      linea.setAttribute('marker-end', `url(#${idMarcadorFlechaInteractiva})`);
      linea.dataset.arcId = arco.id;
      lineas.push(/** @type {SVGElement} */ (linea));

      const asa = document.createElement('button');
      asa.type = 'button';
      asa.className = 'canvas-arrow-handle';
      asa.dataset.arcId = arco.id;
      asa.style.left = `${(p1.x + p2.x) / 2}px`;
      asa.style.top = `${(p1.y + p2.y) / 2}px`;
      asa.setAttribute(
        'aria-label',
        `Flecha de ${etiquetaDe(arco.desdeNodoId)} a ${etiquetaDe(arco.hastaNodoId)}`
      );
      if (invalida) {
        asa.setAttribute('aria-invalid', 'true');
        asa.classList.add('canvas-arrow-handle-invalido');
      }
      asa.addEventListener('focus', () => {
        tipoSeleccionActual = 'arco';
        idArcoSeleccionado = arco.id;
      });
      asa.addEventListener('keydown', (evento) => manejarTeclaEnArco(/** @type {KeyboardEvent} */ (evento), arco.id));
      asa.addEventListener('mousedown', (evento) =>
        manejarInicioArrastreAsaArco(/** @type {MouseEvent} */ (evento), arco.id)
      );
      capaPuertos.append(asa);

      // TSK-0205: DOS asas de extremo por flecha (una en cada punta),
      // reconectables por separado (modelo draw.io). Cableado CRUZADO (ver
      // `crearAsaExtremo`): el asa de ORIGEN (en p1) ancla en el puerto de
      // DESTINO, y viceversa, para que arrastrar cada asa mueva SU propia
      // punta dejando la contraria fija.
      /** @type {ExtremoPuerto} */
      const descriptorOrigen = {
        nodeId: arco.desdeNodoId,
        puerto: /** @type {import('./arrow-connection-graph.js').ValorPuerto} */ (arco.desdePuerto),
      };
      /** @type {ExtremoPuerto} */
      const descriptorDestino = {
        nodeId: arco.hastaNodoId,
        puerto: /** @type {import('./arrow-connection-graph.js').ValorPuerto} */ (arco.hastaPuerto),
      };
      crearAsaExtremo('origen', p1, descriptorDestino, arco, invalida);
      crearAsaExtremo('destino', p2, descriptorOrigen, arco, invalida);
    }
    // TSK-0196: `defsFlechasInteractivas` (la punta de flecha) es, igual que
    // `defsConexiones` en `capaConexiones`, el único hijo que sobrevive entre
    // redibujados -- se reincluye explícitamente en cada `replaceChildren`.
    capaFlechasInteractivas.replaceChildren(defsFlechasInteractivas, ...lineas);
    dimensionarCapaFlechasInteractivas();

    actualizarResumenValidacionInteractiva();
  }

  /**
   * TSK-0206 (fix P0, defecto de producción confirmado con Playwright en
   * navegador real): refresca a la vez las líneas SVG derivadas
   * (`actualizarConexiones`) Y la capa de puertos/asas de flecha
   * (`renderizarCapaInteractiva`) -- las DOS piezas que dependen de la
   * posición VISUAL de un bloque/ancla, ver comentario de cabecera de
   * `aplicarTransformPosicion`. Antes de esta ficha, `manejarInicioArrastreLibre`
   * (arrastre por puntero), `manejarTeclaEnAncla` y la rama `Alt+flecha` de
   * `manejarTeclaEnBloque` llamaban solo a `actualizarConexiones()`: el bloque
   * y las líneas SVG se movían, pero `capaPuertos`/`capaFlechasInteractivas`
   * (que solo se repintaban por `ResizeObserver`/edición de flecha, ninguno de
   * los cuales dispara un `transform: translate(...)`, que no cambia el
   * tamaño de la caja de layout) quedaban CONGELADOS en la posición anterior
   * -- círculos de puerto/asa "flotantes", visualmente desconectados de
   * cualquier bloque real, y además inservibles para crear/reconectar
   * flechas nuevas porque el puerto real ya no está donde parece.
   *
   * DECISIÓN DE DISEÑO (documentada aquí, no solo en el historial de la
   * ficha, para que quien la revierta la vea): esta función se llama SOLO al
   * CONFIRMAR el gesto -- soltar el ratón (`manejarFin`) o cada pulsación de
   * Alt+flecha (que ya es un paso discreto por sí mismo, no hay "gesto en
   * curso" que confirmar aparte) -- nunca en cada `mousemove` intermedio del
   * arrastre por puntero. `renderizarCapaInteractiva()` hace
   * `capaPuertos.replaceChildren()`, destruyendo y recreando TODOS los
   * puertos/asas de la capa entera (no solo los del bloque movido): llamarlo
   * hasta 60 veces por segundo durante un arrastre en curso sería un coste de
   * rendimiento injustificado y, sobre todo, un riesgo real de que el foco
   * cayera a `<body>` en mitad del gesto si algo de esta capa lo tuviera --
   * el mismo tipo de regresión de accesibilidad que TSK-0204 ya introdujo por
   * error una vez. El coste aceptado, documentado explícitamente: mientras el
   * arrastre por puntero está EN CURSO, las líneas SVG (`actualizarConexiones`,
   * que sí se sigue llamando en cada `mousemove`, ver `manejarMover`) se ven
   * en vivo, pero los puertos/asas de ese bloque "se asientan" en su posición
   * final solo al soltar -- una degradación menor y aceptable (nunca peor que
   * el estado final del gesto), no la regresión de esta ficha (puertos que se
   * quedaban MAL para siempre, incluso tras soltar).
   *
   * Conservación de foco (mismo patrón ya aprobado en el callback del
   * `ResizeObserver`, TSK-0204/TSK-0205, más abajo -- NO se extrae un helper
   * común más allá de esta función para no acoplar ese observer a esta
   * ficha): captura qué puerto/asa/asa de extremo tenía el foco ANTES de que
   * `renderizarCapaInteractiva()` haga `replaceChildren()`, y lo restaura
   * sobre el elemento equivalente recién pintado. En la práctica, durante un
   * arrastre por puntero o un paso de Alt+flecha el foco ya está sobre el
   * propio bloque/ancla que se mueve (`botonInicial.focus()` en
   * `manejarInicioArrastreLibre`, el mismo botón que ya tenía el foco al
   * recibir la pulsación de teclado) -- nunca sobre un puerto/asa de ESTA
   * capa -- pero se aplica la comprobación de todos modos, sin asumir ese
   * caso concreto, por si el foco estuviera en otro sitio de la capa cuando
   * se confirma el gesto (p.ej. dos gestos entrelazados).
   */
  function refrescarConexionesYCapaInteractivaTrasMovimiento() {
    const activo = /** @type {HTMLElement | null} */ (document.activeElement);
    const puertoEnfocado =
      activo && activo.classList.contains('canvas-port')
        ? { nodeId: activo.dataset.portNode, puerto: activo.dataset.portValue }
        : null;
    const asaEnfocada =
      activo && activo.classList.contains('canvas-arrow-handle') ? activo.dataset.arcId : null;
    const asaExtremoEnfocada =
      activo && activo.classList.contains('canvas-arrow-endpoint-handle')
        ? { arcId: activo.dataset.arcId, extremo: activo.dataset.arcEndpoint }
        : null;

    actualizarConexiones();
    renderizarCapaInteractiva();

    if (puertoEnfocado && puertoEnfocado.nodeId && puertoEnfocado.puerto) {
      enfocarPuertoInteractivo(puertoEnfocado.nodeId, puertoEnfocado.puerto);
    } else if (asaEnfocada) {
      enfocarAsaInteractiva(asaEnfocada);
    } else if (asaExtremoEnfocada && asaExtremoEnfocada.arcId && asaExtremoEnfocada.extremo) {
      enfocarAsaExtremoInteractiva(
        asaExtremoEnfocada.arcId,
        /** @type {'origen' | 'destino'} */ (asaExtremoEnfocada.extremo)
      );
    }
  }

  /**
   * Aplica el resultado de `calcularAccionArco` (pura) sobre
   * `grafoInteractivo`, refresca la capa y anuncia (criterios de aceptación
   * 1/3/4): usada tanto por el arrastre por puntero como por el "modo
   * conexión" de teclado (misma función, mismo resultado, ver comentario de
   * cabecera "TSK-0194").
   * @param {ExtremoPuerto} origen
   * @param {ExtremoPuerto} destino
   */
  function aplicarAccionArcoInteractivo(origen, destino) {
    const resultado = calcularAccionArco(grafoInteractivo, origen, destino);
    if (resultado.tipo === 'rechazado') {
      anunciar(`No se pudo conectar la flecha: ${resultado.motivo}.`, { aviso: true });
      return;
    }
    grafoInteractivo = resultado.grafo;
    refrescarCapaInteractivaTrasEdicion();
    // El foco va al puerto de DESTINO recién conectado (defecto de foco
    // señalado por accessibility-reviewer en TSK-0194): es el extremo que la
    // persona acaba de alcanzar con Tab/arrastre, el punto natural desde el
    // que seguiría editando (p.ej. crear otra flecha desde ahí).
    enfocarPuertoInteractivo(destino.nodeId, destino.puerto);
    const verbo = resultado.tipo === 'creado' ? 'creada' : 'reconectada';
    anunciar(
      `Flecha ${verbo}: ${etiquetaDeExtremo(origen.nodeId, origen.puerto)} — ` +
        `${etiquetaDeExtremo(destino.nodeId, destino.puerto)}. ${mensajeConteoViolaciones()}`
    );
  }

  /**
   * Borra la flecha (si hay alguna) conectada al extremo exacto `origen`
   * (criterio de aceptación 2/3: soltar sobre la papelera, o Supr/Backspace,
   * sobre un puerto concreto).
   * @param {ExtremoPuerto} origen
   */
  function eliminarArcoDeExtremoInteractivo(origen) {
    const resultado = eliminarArcoDeExtremo(grafoInteractivo, origen);
    if (!resultado) {
      anunciar('No había ninguna flecha conectada a ese puerto.', { aviso: true });
      return;
    }
    grafoInteractivo = resultado.grafo;
    refrescarCapaInteractivaTrasEdicion();
    // `origen` es un PUERTO (no un arco): sigue existiendo tras borrar la
    // flecha que colgaba de él (los puertos se derivan del tipo de nodo, no
    // de sus arcos, ver `descriptoresPuertos`), así que es el "puerto
    // restante" natural al que devolver el foco (defecto de foco señalado
    // por accessibility-reviewer en TSK-0194) -- mismo puerto que ya tenía
    // el foco antes del re-render, tanto si esta llamada viene de Supr/
    // Backspace sobre él como si viene de soltarlo sobre la papelera.
    enfocarPuertoInteractivo(origen.nodeId, origen.puerto);
    anunciar(`Flecha eliminada. ${mensajeConteoViolaciones()}`);
  }

  /**
   * Borra la flecha `arcoId` por completo (criterio de aceptación 2/3:
   * arrastrar su asa a la papelera, Supr/Backspace sobre ella, o activar la
   * papelera con ella enfocada -- ver `manejarActivarPapelera`, más abajo).
   * @param {string} arcoId
   * @returns {boolean} `true` si existía y se ha eliminado.
   */
  function eliminarArcoInteractivoPorId(arcoId) {
    const arco = grafoInteractivo.arcos.find((a) => a.id === arcoId);
    if (!arco) return false;
    grafoInteractivo = eliminarArco(grafoInteractivo, arcoId);
    if (idArcoSeleccionado === arcoId) idArcoSeleccionado = null;
    refrescarCapaInteractivaTrasEdicion();
    // El asa que tenía el foco (`.canvas-arrow-handle`) desaparece con la
    // propia flecha, a diferencia del caso "borrar desde un puerto" (arriba):
    // aquí no hay ningún "elemento equivalente" que siga existiendo sin
    // ambigüedad. Objetivo de foco más razonable (defecto señalado por
    // accessibility-reviewer en TSK-0194): el puerto de ORIGEN de la propia
    // flecha borrada, que sigue existiendo (es un puerto del nodo, no del
    // arco); si por lo que sea ya no está en el DOM (p.ej. el bloque
    // también se eliminó mientras tanto), la papelera -- misma afordancia
    // que se acaba de usar para borrar -- como último recurso siempre
    // presente, mismo patrón que `eliminarBloque` re-enfocando su botón de
    // acción siguiente (`botonDeshacer`).
    if (!enfocarPuertoInteractivo(arco.desdeNodoId, arco.desdePuerto)) {
      papelera.focus();
    }
    anunciar(`Flecha eliminada. ${mensajeConteoViolaciones()}`);
    return true;
  }

  /**
   * Arrastre por puntero desde un puerto (criterio de aceptación 1: crear
   * una flecha nueva, o reconectar la ya existente en ese puerto -- ver
   * `calcularAccionArco`): dibuja una línea temporal (`.canvas-interactive-
   * arrow-line-temporal`) que sigue al puntero, resalta con `puertoMasCercano`
   * el puerto compatible más próximo dentro de `RADIO_SNAP_PUERTO_PX`
   * (criterio de aceptación 1: snapping), y al soltar aplica esa conexión, o
   * borra la flecha de ese extremo si se suelta sobre la papelera (criterio
   * de aceptación 2, misma afordancia que un bloque).
   * @param {MouseEvent} evento
   * @param {ExtremoPuerto} origen
   */
  function manejarInicioArrastrePuerto(evento, origen) {
    if (typeof evento.button === 'number' && evento.button !== 0) return;
    evento.preventDefault();
    /** @type {HTMLButtonElement} */ (evento.currentTarget).focus();
    origenConexionTeclado = null;

    const puntoOrigen = puntoDePuerto(origen.nodeId, origen.puerto);
    if (!puntoOrigen) return;

    const lineaTemporal = document.createElementNS(SVG_NS, 'line');
    lineaTemporal.setAttribute('class', 'canvas-interactive-arrow-line canvas-interactive-arrow-line-temporal');
    lineaTemporal.setAttribute('x1', String(puntoOrigen.x));
    lineaTemporal.setAttribute('y1', String(puntoOrigen.y));
    lineaTemporal.setAttribute('x2', String(puntoOrigen.x));
    lineaTemporal.setAttribute('y2', String(puntoOrigen.y));
    // TSK-0196: misma punta de flecha que la línea definitiva, para que el
    // gesto en curso ya se perciba con la dirección final del trazo.
    lineaTemporal.setAttribute('marker-end', `url(#${idMarcadorFlechaInteractiva})`);
    capaFlechasInteractivas.append(lineaTemporal);

    const candidatos = /** @type {CandidatoPuerto[]} */ (
      descriptoresPuertos()
        .filter((candidato) => !(candidato.nodeId === origen.nodeId && candidato.puerto === origen.puerto))
        .map((candidato) => {
          const punto = puntoDePuerto(candidato.nodeId, candidato.puerto);
          return punto ? { ...candidato, x: punto.x, y: punto.y } : null;
        })
        .filter((candidato) => candidato !== null)
    );

    /** @param {string} nodeId
     *  @param {string} puerto */
    function marcarSnapActivo(nodeId, puerto) {
      for (const boton of capaPuertos.querySelectorAll('.canvas-port-snap-activo')) {
        boton.classList.remove('canvas-port-snap-activo');
      }
      if (!nodeId) return;
      const boton = capaPuertos.querySelector(`[data-port-node="${nodeId}"][data-port-value="${puerto}"]`);
      if (boton) boton.classList.add('canvas-port-snap-activo');
    }

    /** @param {MouseEvent} eventoMover */
    function manejarMover(eventoMover) {
      const base = superficie.getBoundingClientRect();
      const x = (eventoMover.clientX - base.left) / nivelZoom + superficie.scrollLeft;
      const y = (eventoMover.clientY - base.top) / nivelZoom + superficie.scrollTop;
      lineaTemporal.setAttribute('x2', String(x));
      lineaTemporal.setAttribute('y2', String(y));

      const cercano = puertoMasCercano(candidatos, { x, y }, RADIO_SNAP_PUERTO_PX);
      marcarSnapActivo(cercano?.nodeId ?? '', cercano?.puerto ?? '');
      papelera.classList.toggle('canvas-trash-armada', estaSobrePapelera(eventoMover.clientX, eventoMover.clientY));
    }

    /** @param {MouseEvent} [eventoFin] Ausente cuando `destruir()` fuerza el
     * fin de un arrastre a mitad de gesto (docs/03-frontend.md §8.3, mismo
     * patrón que `manejarInicioArrastreLibre`): se descarta el gesto sin
     * aplicar ningún cambio. */
    function manejarFin(eventoFin) {
      document.removeEventListener('mousemove', /** @type {any} */ (manejarMover));
      document.removeEventListener('mouseup', /** @type {any} */ (manejarFin));
      detenerArrastreActivo = null;
      lineaTemporal.remove();
      marcarSnapActivo('', '');
      papelera.classList.remove('canvas-trash-armada');

      if (!eventoFin) return;

      if (estaSobrePapelera(eventoFin.clientX, eventoFin.clientY)) {
        eliminarArcoDeExtremoInteractivo(origen);
        return;
      }

      const base = superficie.getBoundingClientRect();
      const x = (eventoFin.clientX - base.left) / nivelZoom + superficie.scrollLeft;
      const y = (eventoFin.clientY - base.top) / nivelZoom + superficie.scrollTop;
      const cercano = puertoMasCercano(candidatos, { x, y }, RADIO_SNAP_PUERTO_PX);
      if (!cercano) {
        anunciar('Arrastre de flecha cancelado: no hay ningún puerto compatible cerca de donde se soltó.', {
          aviso: true,
        });
        return;
      }
      aplicarAccionArcoInteractivo(origen, {
        nodeId: cercano.nodeId,
        puerto: /** @type {import('./arrow-connection-graph.js').ValorPuerto} */ (cercano.puerto),
      });
    }

    document.addEventListener('mousemove', /** @type {any} */ (manejarMover));
    document.addEventListener('mouseup', /** @type {any} */ (manejarFin));
    detenerArrastreActivo = /** @type {() => void} */ (/** @type {unknown} */ (manejarFin));
  }

  /**
   * Equivalente COMPLETO de teclado (criterio de aceptación 3, WCAG 2.5.7,
   * requisito duro) del arrastre de un puerto: Intro/Espacio marca/despeja el
   * "modo conexión" de 2 pasos (`origenConexionTeclado`); Escape, o repetir
   * el MISMO puerto, cancela sin aplicar nada; Delete/Backspace borra la
   * flecha conectada a ESTE puerto exacto (equivalente de "arrastrarla a la
   * papelera" para ese extremo).
   * @param {KeyboardEvent} evento
   * @param {ExtremoPuerto} descriptor
   */
  function manejarTeclaEnPuerto(evento, descriptor) {
    if (evento.key === 'Delete' || evento.key === 'Backspace') {
      evento.preventDefault();
      eliminarArcoDeExtremoInteractivo(descriptor);
      return;
    }

    if (evento.key === 'Escape' && origenConexionTeclado) {
      evento.preventDefault();
      origenConexionTeclado = null;
      renderizarCapaInteractiva();
      // `renderizarCapaInteractiva` reconstruye TODOS los botones desde cero
      // (`replaceChildren`): sin volver a enfocar el mismo puerto que ya lo
      // tenía (`descriptor`, el que ha recibido esta tecla), el foco cae a
      // `<body>` (defecto señalado por accessibility-reviewer en TSK-0194).
      enfocarPuertoInteractivo(descriptor.nodeId, descriptor.puerto);
      anunciar('Conexión de flecha cancelada.');
      return;
    }

    if (evento.key !== 'Enter' && evento.key !== ' ') return;
    evento.preventDefault();

    if (!origenConexionTeclado) {
      origenConexionTeclado = descriptor;
      renderizarCapaInteractiva();
      enfocarPuertoInteractivo(descriptor.nodeId, descriptor.puerto);
      anunciar(
        `Origen de flecha marcado en ${etiquetaDeExtremo(descriptor.nodeId, descriptor.puerto)}. ` +
          'Ve con Tab hasta el puerto de destino y pulsa Intro o Espacio para conectar, o Escape para cancelar.'
      );
      return;
    }

    if (origenConexionTeclado.nodeId === descriptor.nodeId && origenConexionTeclado.puerto === descriptor.puerto) {
      origenConexionTeclado = null;
      renderizarCapaInteractiva();
      enfocarPuertoInteractivo(descriptor.nodeId, descriptor.puerto);
      anunciar('Conexión de flecha cancelada.');
      return;
    }

    const origen = origenConexionTeclado;
    origenConexionTeclado = null;
    aplicarAccionArcoInteractivo(origen, descriptor);
  }

  /**
   * Supr/Backspace sobre el asa de una flecha la borra (criterio de
   * aceptación 2/3, mismo mecanismo que un bloque colocado,
   * `manejarTeclaEnBloque`).
   * @param {KeyboardEvent} evento
   * @param {string} arcoId
   */
  function manejarTeclaEnArco(evento, arcoId) {
    if (evento.key !== 'Delete' && evento.key !== 'Backspace') return;
    evento.preventDefault();
    eliminarArcoInteractivoPorId(arcoId);
  }

  /**
   * Arrastrar el asa de una flecha hasta la papelera la borra (criterio de
   * aceptación 2, misma afordancia/mismo mecanismo que arrastrar un bloque,
   * `manejarInicioArrastreLibre`/`estaSobrePapelera`, reutilizada sin
   * cambios): esta asa NO tiene ninguna posición propia que confirmar si NO
   * se suelta sobre la papelera (a diferencia de un bloque, "soltar en
   * cualquier otro punto" simplemente cancela el gesto, la flecha sigue
   * exactamente donde estaba).
   * @param {MouseEvent} evento
   * @param {string} arcoId
   */
  function manejarInicioArrastreAsaArco(evento, arcoId) {
    if (typeof evento.button === 'number' && evento.button !== 0) return;
    evento.preventDefault();
    /** @type {HTMLButtonElement} */ (evento.currentTarget).focus();

    const clientXInicio = evento.clientX;
    const clientYInicio = evento.clientY;
    let seMovio = false;

    /** @param {MouseEvent} eventoMover */
    function manejarMover(eventoMover) {
      const distancia = Math.hypot(eventoMover.clientX - clientXInicio, eventoMover.clientY - clientYInicio);
      if (!seMovio && distancia < UMBRAL_ARRASTRE_LIBRE_PX) return;
      seMovio = true;
      papelera.classList.toggle('canvas-trash-armada', estaSobrePapelera(eventoMover.clientX, eventoMover.clientY));
    }

    /** @param {MouseEvent} [eventoFin] */
    function manejarFin(eventoFin) {
      document.removeEventListener('mousemove', /** @type {any} */ (manejarMover));
      document.removeEventListener('mouseup', /** @type {any} */ (manejarFin));
      detenerArrastreActivo = null;
      papelera.classList.remove('canvas-trash-armada');
      if (!seMovio || !eventoFin) return;
      if (estaSobrePapelera(eventoFin.clientX, eventoFin.clientY)) {
        eliminarArcoInteractivoPorId(arcoId);
      }
    }

    document.addEventListener('mousemove', /** @type {any} */ (manejarMover));
    document.addEventListener('mouseup', /** @type {any} */ (manejarFin));
    detenerArrastreActivo = /** @type {() => void} */ (/** @type {unknown} */ (manejarFin));
  }

  /**
   * Calcula, a partir de una coordenada vertical de puntero (`clientY`), el
   * índice de destino dentro de la lista de "otros" bloques del contenedor
   * `contenedorId` (Secuencia raíz o cualquier rama/cuerpo anidado, TSK-0072;
   * todos los bloques de ese contenedor EXCLUYENDO `idExcluido`, si se
   * indica): el primer bloque cuyo centro vertical está por debajo de
   * `clientY` marca el punto de inserción; si ninguno lo está, el destino es
   * el final de la lista. Reutilizada tanto para el arrastre de reordenado
   * como para el `drop` desde la paleta (sin `idExcluido`, porque el bloque
   * soltado aún no está en la lista).
   * @param {number} clientY
   * @param {string} contenedorId
   * @param {string} [idExcluido]
   * @returns {number}
   */
  function calcularIndiceObjetivo(clientY, contenedorId, idExcluido) {
    const todas = sentenciasDe(contenedorId);
    const otros = idExcluido ? todas.filter((s) => s.id !== idExcluido) : todas;
    for (let i = 0; i < otros.length; i += 1) {
      const boton = botonesPorId.get(otros[i].id);
      if (!boton) continue;
      const rect = boton.getBoundingClientRect();
      const medio = rect.top + rect.height / 2;
      if (clientY < medio) return i;
    }
    return otros.length;
  }

  /**
   * `true` si el punto `(clientX, clientY)` cae dentro del rectángulo actual
   * de la papelera: usada tanto por el arrastre por puntero (soltar sobre la
   * papelera elimina en vez de reordenar) como, indirectamente, documentada
   * aquí para dejar clara la geometría que también protege el clic/teclado
   * (que no la necesitan: activan directamente sobre `idBloqueSeleccionado`).
   * @param {number} clientX
   * @param {number} clientY
   * @returns {boolean}
   */
  function estaSobrePapelera(clientX, clientY) {
    const rect = papelera.getBoundingClientRect();
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
  }

  /** Oculta y limpia el estado de "deshacer" (TSK-0061): se llama al
   * empezar cualquier acción estructural nueva (colocar/reordenar/duplicar/
   * otro borrado) para que un deshacer nunca reinserte un nodo con un
   * índice ya obsoleto, y también tras un deshacer ya consumido. */
  function invalidarDeshacer() {
    ultimoEliminado = null;
    botonDeshacer.hidden = true;
    botonDeshacer.textContent = '';
  }

  /**
   * Elimina de verdad un bloque, dondequiera que esté conectado -- Secuencia
   * raíz o cualquier rama/cuerpo anidado (TSK-0072, Supr/Backspace, papelera
   * por clic/teclado, o soltarlo con el puntero sobre la papelera): sin
   * confirmación previa de dos pasos (ver comentario de cabecera "TSK-0061",
   * decisión de deshacer inmediato en su lugar). Dueña única de la
   * eliminación: tanto el atajo de teclado como el arrastre a la papelera
   * llaman a esta misma función, nunca duplican la lógica. Localiza el
   * contenedor real del bloque con `buscarSecuenciaPadre` (`editor-model.js`)
   * ANTES de desconectar (necesario para guardar `contenedorId`/`indice` de
   * cara a `deshacerEliminacion`) y delega la propia desconexión en
   * `desconectarBloque` (`connectors.js`), que ya sabe recorrer cualquier
   * nivel de anidamiento.
   * @param {string} id
   * @returns {boolean} `true` si existía el bloque y se ha eliminado.
   */
  function eliminarBloque(id) {
    const contenedor = buscarSecuenciaPadre(programaActual, id);
    if (!contenedor) return false;
    const indice = /** @type {NodoModelo[]} */ (
      /** @type {Record<string, unknown>} */ (contenedor).sentencias
    ).findIndex((sentencia) => sentencia.id === id);
    const nodo = /** @type {NodoModelo} */ (buscarNodoPorId(programaActual, id));
    // DEC-180: sin `etiquetaDe`, el aviso y el botón decían el nombre del
    // modelo («Asignacion») de todo bloque que no colocó esta vista.
    const etiqueta = etiquetaDe(id);
    // TSK-0081: guarda la posición VISUAL actual (si la había) ANTES de
    // borrarla de `EstadoVisual`, para que `deshacerEliminacion` pueda
    // restaurarla -- ver `editor-visual-state.js#eliminarPosicion` (docstring
    // ya existente: "no dejar referencias colgantes en la capa visual").
    const posicionVisual = obtenerPosicion(estadoVisual, id);

    const resultado = desconectarBloque(programaActual, id);
    programaActual = resultado.programa;
    if (idBloqueSeleccionado === id) idBloqueSeleccionado = null;
    eliminarPosicion(estadoVisual, id);
    ultimoEliminado = { nodo, contenedorId: contenedor.id, indice, etiqueta, posicionVisual };

    renderizar();
    if (notificarCambioPrograma) notificarCambioPrograma(programaActual);

    // El botón "Deshacer" aparece YA visible y recibe el foco de inmediato
    // (criterio de aceptación: mecanismo de confirmación/protección
    // equivalente al de dos pasos que tenía el botón "Eliminar" anterior):
    // un usuario de teclado no tiene que buscarlo, está a un Intro de
    // distancia justo donde ya tenía el foco/la atención.
    botonDeshacer.hidden = false;
    botonDeshacer.textContent = `Deshacer eliminación de ${etiqueta}`;
    botonDeshacer.focus();

    anunciar(`Bloque ${etiqueta} eliminado. Activa "Deshacer eliminación de ${etiqueta}" para recuperarlo.`);
    return true;
  }

  /** Reinserta el último bloque eliminado en su índice y CONTENEDOR
   * originales (TSK-0061; `contenedorId` añadido en TSK-0072 para que un
   * bloque eliminado de dentro de una rama se restaure dentro de esa misma
   * rama, no siempre en la raíz). No hace nada si no hay ninguna eliminación
   * pendiente de deshacer (botón oculto/deshabilitado de facto, ver
   * `invalidarDeshacer`), ni si el contenedor original ya no existe (p.ej.
   * si el propio Condicional/Bucle que contenía la rama se eliminó mientras
   * tanto: caso límite documentado, no silencioso -- se avisa en vez de
   * lanzar). */
  function deshacerEliminacion() {
    if (!ultimoEliminado) return;
    const { nodo, contenedorId, indice, etiqueta, posicionVisual } = ultimoEliminado;

    try {
      programaActual = conectarBloque(programaActual, { contenedorId, nodo, indice });
    } catch (error) {
      invalidarDeshacer();
      anunciar(`No se pudo deshacer la eliminación de ${etiqueta}: ${/** @type {Error} */ (error).message}`, {
        aviso: true,
      });
      return;
    }
    etiquetasPorId.set(nodo.id, etiqueta);
    // TSK-0081: restaura también la posición VISUAL que tenía antes de
    // eliminarse (si la había) -- si nunca se había movido, no hay nada que
    // restaurar y el bloque reaparece en su posición de flujo por defecto.
    if (posicionVisual) establecerPosicion(estadoVisual, nodo.id, posicionVisual);
    invalidarDeshacer();

    renderizar();
    if (notificarCambioPrograma) notificarCambioPrograma(programaActual);

    anunciar(`Bloque ${etiqueta} restaurado en su posición original.`);
    const boton = botonesPorId.get(nodo.id);
    // `preventScroll: true` (misma clase de bug que "meter un bloque en una
    // rama sube la página"): restaurar el foco tras deshacer no debe desplazar
    // la vista para traer el bloque a pantalla.
    if (boton) boton.focus({ preventScroll: true });
  }

  /** Manejador de activación de la papelera por clic/Intro/Espacio (mismo
   * `<button>` nativo, sin manejador de teclado a medida: el navegador ya
   * dispara `click` con Intro/Espacio sobre un `<button>`). Actúa sobre lo
   * que tenga el foco más reciente: un bloque colocado (comportamiento
   * original, TSK-0061) o, desde TSK-0194, una flecha de la capa
   * interactiva (`tipoSeleccionActual`/`idArcoSeleccionado`, criterio de
   * aceptación 2: "misma afordancia que borrar un bloque"). */
  function manejarActivarPapelera() {
    if (tipoSeleccionActual === 'arco' && idArcoSeleccionado && eliminarArcoInteractivoPorId(idArcoSeleccionado)) {
      return;
    }
    if (!idBloqueSeleccionado || !eliminarBloque(idBloqueSeleccionado)) {
      anunciar(
        'Selecciona primero un bloque colocado o una flecha (clic o Tab) para poder eliminarlo con la papelera.',
        { aviso: true }
      );
    }
  }

  papelera.addEventListener('click', manejarActivarPapelera);
  botonDeshacer.addEventListener('click', deshacerEliminacion);

  // DEC-180: el controlador destruye esta vista y crea otra en cada cambio de
  // programa (`editor-controller.js#reconstruirDependientes`), una microtarea
  // DESPUÉS de que `eliminarBloque` enseñe el botón. Sin recoger aquí el
  // borrado pendiente de la vista anterior, el botón nacía oculto y el aviso
  // mandaba activar algo que ya no existía. Solo se recoge si cuadra con el
  // programa que se dibuja: el bloque ya no está y su contenedor sí. El
  // «Deshacer» de la barra restaura una copia con ids nuevos (contenedor
  // incluido) y la edición bloqueada devuelve el bloque: en los dos casos se
  // descarta, así que nunca se reinserta un bloque que ya está.
  const eliminacionHeredada = opciones.eliminacionPendiente;
  if (
    eliminacionHeredada &&
    !buscarNodoPorId(programaActual, eliminacionHeredada.nodo.id) &&
    buscarNodoPorId(programaActual, eliminacionHeredada.contenedorId)
  ) {
    ultimoEliminado = eliminacionHeredada;
    botonDeshacer.hidden = false;
    botonDeshacer.textContent = `Deshacer eliminación de ${eliminacionHeredada.etiqueta}`;
  }

  /**
   * Reordena un bloque ya colocado a `nuevoIndice` DENTRO de su propio
   * contenedor (Secuencia raíz por defecto, o cualquier rama/cuerpo anidado
   * si se indica `contenedorId`, TSK-0072; única operación, junto a colocar
   * y eliminar, que toca el modelo lógico -- ver comentario de cabecera
   * "TSK-0060"/"TSK-0072"). Delega en `moverBloqueEnContenedor`
   * (`connectors.js`), nunca reimplementada. Re-renderiza, conserva el foco
   * sobre el bloque movido y opcionalmente anuncia el resultado.
   * @param {string} id
   * @param {number} nuevoIndice
   * @param {{ contenedorId?: string, anunciarCambio?: boolean }} [opciones]
   */
  function reordenarBloque(id, nuevoIndice, { contenedorId = idContenedorRaiz(), anunciarCambio = true } = {}) {
    invalidarDeshacer();
    const total = sentenciasDe(contenedorId).length;
    programaActual = moverBloqueEnContenedor(programaActual, contenedorId, id, nuevoIndice);
    renderizar();

    const boton = botonesPorId.get(id);
    // `preventScroll: true` (misma clase de bug): reordenar y conservar el foco
    // no debe hacer scroll de la página hacia el bloque.
    if (boton) boton.focus({ preventScroll: true });

    if (notificarCambioPrograma) notificarCambioPrograma(programaActual);

    if (anunciarCambio) {
      const etiqueta = etiquetaDe(id);
      const indiceFinal = sentenciasDe(contenedorId).findIndex((s) => s.id === id);
      anunciar(`Bloque ${etiqueta} movido a la posición ${indiceFinal + 1} de ${total}.`);
    }
  }

  /**
   * @param {KeyboardEvent} evento
   * @param {string} id
   * @param {string} contenedorId Contenedor (Secuencia raíz o rama/cuerpo
   *   anidado, TSK-0072) al que pertenece el bloque `id`, fijado al
   *   renderizar el botón (ver `crearItemBloque`).
   * @param {HTMLElement} item `<li>` del bloque (TSK-0081): necesario para
   *   el movimiento VISUAL libre por teclado (`Alt+flecha`, ver más abajo).
   */
  function manejarTeclaEnBloque(evento, id, contenedorId, item) {
    // Supr/Backspace elimina el bloque con el foco (TSK-0061, criterio de
    // aceptación: "seleccionar un bloque y pulsar Supr o Backspace lo
    // elimina igual que el arrastre"), reutilizando la misma `eliminarBloque`
    // que usan la papelera y el drop sobre ella (genérica: localiza el
    // contenedor real por sí misma, TSK-0072). `preventDefault` evita que
    // Backspace navegue "atrás" en el historial del navegador fuera de un
    // campo de texto.
    if (evento.key === 'Delete' || evento.key === 'Backspace') {
      evento.preventDefault();
      eliminarBloque(id);
      return;
    }

    // TSK-0081 (WCAG 2.5.7, obligatorio): `Alt+flecha` mueve la posición
    // VISUAL libre del bloque (ver comentario de cabecera "Teclado" para la
    // justificación completa de por qué es `Alt` y no las flechas solas,
    // que ya reordenan ESTRUCTURALMENTE, ver más abajo). Comprobado ANTES
    // del reordenado sin modificador para que `Alt+ArrowUp/ArrowDown` nunca
    // caiga también en la rama de reordenado de abajo.
    if (evento.altKey) {
      const deltaXY = TECLA_A_DELTA_XY[/** @type {keyof typeof TECLA_A_DELTA_XY} */ (evento.key)];
      if (!deltaXY) return;
      evento.preventDefault();
      const previa = obtenerPosicion(estadoVisual, id) ?? { x: 0, y: 0 };
      const limitada = limitarDentroDelContenedor(item, previa.x + deltaXY.x, previa.y + deltaXY.y);
      establecerPosicion(estadoVisual, id, limitada);
      aplicarTransformPosicion(item, id);
      // TSK-0206: cada pulsación de Alt+flecha ya es un paso discreto y
      // confirmado (no un `mousemove` intermedio de un gesto en curso), así
      // que aquí SÍ se refresca la capa interactiva completa en cada paso
      // (ver el comentario de cabecera de
      // `refrescarConexionesYCapaInteractivaTrasMovimiento` para la decisión
      // de diseño completa: solo se evita en cada `mousemove` del arrastre
      // por puntero).
      refrescarConexionesYCapaInteractivaTrasMovimiento();
      const etiqueta = etiquetaDe(id);
      anunciar(
        `Bloque ${etiqueta} movido visualmente con Alt+flecha a la posición horizontal ` +
          `${Math.round(limitada.x)}px, vertical ${Math.round(limitada.y)}px. Su orden en el programa no cambia.`
      );
      return;
    }

    const delta = TECLA_A_DELTA_INDICE[/** @type {keyof typeof TECLA_A_DELTA_INDICE} */ (evento.key)];
    if (!delta) return;
    const sentencias = sentenciasDe(contenedorId);
    const indiceActual = sentencias.findIndex((s) => s.id === id);
    if (indiceActual === -1) return;
    const nuevoIndice = indiceActual + delta;
    if (nuevoIndice < 0 || nuevoIndice >= sentencias.length) return; // ya está en un extremo: sin efecto
    evento.preventDefault(); // evita el scroll de página con las flechas
    reordenarBloque(id, nuevoIndice, { contenedorId });
  }

  /**
   * Manejador de teclado de una ancla (Inicio/Fin, ver "Revisión posterior"
   * en el comentario de cabecera de `crearAncla`): reproduce SOLO la rama de
   * movimiento visual libre (`Alt+flecha`) de `manejarTeclaEnBloque` --
   * nunca Supr/Backspace (las anclas no son eliminables) ni las flechas SIN
   * modificador (que en `manejarTeclaEnBloque` reordenan `sentencias`; una
   * ancla no está en ese array, no hay índice que mover).
   * @param {KeyboardEvent} evento
   * @param {string} id
   * @param {HTMLElement} item
   * @param {string} etiqueta
   */
  function manejarTeclaEnAncla(evento, id, item, etiqueta) {
    if (!evento.altKey) return;
    const deltaXY = TECLA_A_DELTA_XY[/** @type {keyof typeof TECLA_A_DELTA_XY} */ (evento.key)];
    if (!deltaXY) return;
    evento.preventDefault();
    const previa = obtenerPosicion(estadoVisual, id) ?? { x: 0, y: 0 };
    const limitada = limitarDentroDelContenedor(item, previa.x + deltaXY.x, previa.y + deltaXY.y);
    establecerPosicion(estadoVisual, id, limitada);
    aplicarTransformPosicion(item, id);
    // TSK-0206: mismo refresco completo (líneas + capa interactiva) que la
    // rama Alt+flecha de `manejarTeclaEnBloque` -- ver el comentario de
    // cabecera de `refrescarConexionesYCapaInteractivaTrasMovimiento`.
    refrescarConexionesYCapaInteractivaTrasMovimiento();
    anunciar(
      `Bloque ${etiqueta} movido visualmente con Alt+flecha a la posición horizontal ` +
        `${Math.round(limitada.x)}px, vertical ${Math.round(limitada.y)}px. Su orden en el programa no cambia.`
    );
  }

  /**
   * Aplica al `<li>` de un bloque colocado el `transform: translate(...)`
   * correspondiente a su posición VISUAL actual en `EstadoVisual` (TSK-0081,
   * ver comentario de cabecera "Mecanismo elegido"): sin posición guardada
   * (o `{x:0,y:0}`), se limpia cualquier `transform` previo -- el bloque se
   * ve en su posición de flujo normal, "posición automática por defecto"
   * (DEC-028, síntesis de diseño). Se llama tanto al renderizar
   * (`crearItemBloque`) como en cada paso de un arrastre/movimiento por
   * teclado.
   * @param {HTMLElement} item
   * @param {string} id
   */
  function aplicarTransformPosicion(item, id) {
    const posicion = obtenerPosicion(estadoVisual, id);
    if (posicion && (posicion.x !== 0 || posicion.y !== 0)) {
      item.style.transform = `translate(${posicion.x}px, ${posicion.y}px)`;
    } else {
      item.style.removeProperty('transform');
    }
  }

  /**
   * Reserva ESPACIO REAL (no solo aritmético) hacia la derecha/abajo de
   * `contenedor` para que el margen que añade `RANGO_EXTRA_ARRASTRE_LIBRE_PX`
   * en `limitarDentroDelContenedor` sea alcanzable con scroll REAL del
   * navegador (TSK-0208, criterio de aceptación 3: "alcanzable con scroll
   * real, no solo matemáticamente permitida"), no un límite aritmético que
   * ningún scroll pudiera revelar nunca.
   *
   * Por qué hace falta (dato de CSSOM View, no una suposición): `transform`
   * es un efecto de PINTADO, nunca de layout (ver "Mecanismo elegido" en el
   * comentario de cabecera de este archivo) -- mover un `<li>`/ancla/zona con
   * `translate()` NUNCA agranda por sí solo el `scrollWidth`/`scrollHeight`
   * de su `offsetParent`, así que sin esta reserva el margen ampliado sería
   * matemáticamente correcto pero invisible/inalcanzable (ningún scrollbar
   * aparecería nunca hacia esa zona) -- exactamente la causa raíz que
   * describe el frontmatter de TSK-0208.
   *
   * Mecanismo elegido (una de las opciones que sugería el propio frontmatter
   * de la tarea: "padding dinámico en `.canvas-surface`"): se aplica aquí
   * como `min-width`/`min-height` en LÍNEA vía JS, nunca en ningún `.css`
   * (ningún archivo de estilos está en las rutas permitidas de esta tarea).
   * Un `min-width`/`min-height` mayor en `contenedor` (un elemento de flujo
   * normal, `position: relative`, nunca `absolute`/`fixed`) agranda su propia
   * caja de layout hacia abajo/derecha SIN mover ningún hijo ya existente
   * -- a diferencia de un `padding-inline-start`/`padding-block-start`, que
   * desplazaría también el origen de TODOS los hermanos ya colocados en ese
   * mismo contenedor. Por eso el margen hacia ATRÁS (izquierda/arriba, ver
   * `minX`/`minY` en `limitarDentroDelContenedor`) NO reserva espacio real
   * equivalente: hacerlo exigiría desplazar el origen visual de todo el
   * contenido ya colocado, o solapar con un margen negativo la columna
   * vecina de la paleta -- ambas alternativas peores que la corrección
   * elegida en su lugar (rechazo real de `accessibility-reviewer`: en vez de
   * reservar espacio real hacia atrás, `limitarDentroDelContenedor` ACOTA el
   * margen aritmético hacia atrás al espacio de documento NATURALMENTE
   * disponible -- `posicionAbsolutaDeDocumento`, ver su comentario de
   * cabecera y el de `limitarDentroDelContenedor` -- de modo que la posición
   * final nunca cae en una coordenada de documento negativa/inalcanzable,
   * aunque el margen práctico hacia atrás resulte menor que
   * `RANGO_EXTRA_ARRASTRE_LIBRE_PX` cerca del borde de la página). Ese
   * `min-width`/`min-height` mayor, al tratarse de un elemento en flujo
   * normal, agranda a
   * su vez la caja de SU PROPIO padre (`.diagram-editor-slot-canvas`, sin
   * scroll propio) hasta la página completa, que SÍ tiene scroll real
   * (`<body>`/`<html>` sin ningún `overflow` propio declarado, ver
   * `src/styles/base.css`/`reset.css` -- el scroll nativo del viewport) --
   * así el margen ampliado queda respaldado por scroll REAL de extremo a
   * extremo, sin tocar ningún `.css`.
   *
   * Idempotente (`Math.max` contra el valor ya fijado en línea): solo
   * AGRANDA una reserva ya hecha, nunca la encoge ni repite una escritura
   * innecesaria al DOM en cada `mousemove` del mismo arrastre.
   *
   * CORREGIDO (TSK-0214, regresión real reportada por el usuario con
   * captura): la limitación que este comentario documentaba hasta ahora --
   * "para una zona de rama anidada, un `min-width` reservado aquí mayor que
   * su propio `max-width: 26rem` (canvas.css) pintaría fuera de él" -- era en
   * realidad un síntoma de un bug más grave, no solo una limitación estética.
   * Esta función NUNCA debe recibir ya la propia `<ul class="canvas-branch-
   * surface">` de una rama/cuerpo como `contenedor`: `limitarDentroDelContenedor`
   * (más abajo) ahora reserva el espacio siempre en la RAÍZ de scroll real
   * (`encontrarRaizDeReservaDeEspacio`), nunca en el `offsetParent` INMEDIATO
   * del item si este es una rama anidada -- de otro modo, cada movimiento de
   * un bloque dentro de una rama inflaba permanentemente (hasta el próximo
   * `renderizar()` completo, ya que este `min-width`/`min-height` en línea
   * solo puede CRECER, nunca encoger) el `min-width`/`min-height` de la
   * PROPIA rama, dejándola visualmente mucho más grande que su contenido real
   * -- exactamente el reporte del usuario. La rama sigue ajustándose a su
   * contenido (sin ningún `min-width`/`min-height` en línea propio) porque ya
   * no es nunca el destino de esta función para ese caso.
   * @param {HTMLElement} contenedor
   * @param {number} naturalLeft
   * @param {number} naturalTop
   * @param {number} ancho
   * @param {number} alto
   */
  function reservarEspacioHaciaDelante(contenedor, naturalLeft, naturalTop, ancho, alto) {
    // Premisa del usuario: el canvas puede crecer HACIA ABAJO, pero NO hacia la
    // derecha. Por eso solo se reserva espacio vertical real (`min-height`),
    // para que el scroll hacia abajo sea alcanzable al colocar bloques más
    // abajo; NUNCA `min-width` -- a la derecha el arrastre topa con el borde del
    // panel (`limitarDentroDelContenedor`), así que reservar ancho solo inflaba
    // las cajas hacia la derecha sin ningún uso. `naturalLeft`/`ancho` se
    // conservan en la firma (los pasa `limitarDentroDelContenedor`) aunque ya no
    // se usen para reservar en horizontal.
    const altoMinimoNecesario = naturalTop + alto + RANGO_EXTRA_ARRASTRE_LIBRE_PX;
    const minHeightActual = Number.parseFloat(contenedor.style.minHeight) || 0;
    if (altoMinimoNecesario > minHeightActual) contenedor.style.minHeight = `${altoMinimoNecesario}px`;
  }

  /**
   * TSK-0214 (causa raíz de la regresión reportada por el usuario): sube por
   * la cadena de `offsetParent` desde `contenedorInmediato` (siempre un
   * `.canvas-surface`, TSK-0072/TSK-0081) mientras siga siendo una zona de
   * rama/cuerpo ANIDADA (`.canvas-branch-surface`), hasta encontrar el
   * `.canvas-surface` RAÍZ (la lista de nivel superior del programa, que
   * nunca lleva esa clase) -- ese es el único contenedor cuyo `min-width`/
   * `min-height` en línea (`reservarEspacioHaciaDelante`, arriba) se traduce
   * en scroll REAL de página de extremo a extremo (ver el comentario de
   * cabecera de esa función: crece en flujo normal hasta
   * `.diagram-editor-slot-canvas`, sin scroll propio, y de ahí hasta
   * `<body>`/`<html>`, que sí lo tienen de forma nativa). Una rama/cuerpo
   * anidada, en cambio, NO tiene scroll propio (`overflow-y: visible` en
   * `.canvas-branch-surface`, canvas.css) ni debe reservar espacio propio:
   * debe seguir ajustándose siempre a su contenido real.
   *
   * Si `contenedorInmediato` YA es la raíz (el caso general, un bloque/ancla/
   * zona colocado directamente en el canvas raíz), se devuelve sin ningún
   * salto adicional en la cadena -- comportamiento IDÉNTICO al de antes de
   * esta tarea para ese caso (TSK-0207/TSK-0208 sin regresión).
   * @param {HTMLElement} contenedorInmediato
   * @returns {HTMLElement}
   */
  function encontrarRaizDeReservaDeEspacio(contenedorInmediato) {
    let actual = contenedorInmediato;
    while (
      actual.classList.contains('canvas-branch-surface') &&
      actual.offsetParent instanceof window.HTMLElement
    ) {
      actual = actual.offsetParent;
    }
    return actual;
  }

  /**
   * Posición de `elemento` (normalmente `item`, el bloque/ancla/zona movido)
   * relativa al origen de contenido de `raiz` (normalmente el resultado de
   * `encontrarRaizDeReservaDeEspacio`, más arriba): suma `offsetLeft`/
   * `offsetTop` de `elemento` y de cada `offsetParent` ascendente hasta (sin
   * incluirlo) `raiz`. Mismo mecanismo que `posicionAbsolutaDeDocumento` (más
   * abajo), acotado a un ancestro intermedio en vez de subir hasta la raíz
   * del documento -- necesario porque `raiz` puede ser varios niveles de
   * anidamiento por encima del `offsetParent` INMEDIATO de `elemento`
   * (Condicional/Bucle anidado dentro de otro, TSK-0214).
   *
   * Sin ningún `transform: scale` intermedio entre `elemento` y `raiz` (el
   * único `transform` de zoom del canvas se aplica una única vez, a nivel de
   * todo el árbol, ver `nivelZoom`, nunca por cada rama anidada por
   * separado): una traslación de `elemento` de N píxeles en el sistema de
   * coordenadas de su `offsetParent` inmediato produce el mismo
   * desplazamiento de N píxeles en el sistema de coordenadas de `raiz`, así
   * que sumar `offsetLeft`/`offsetTop` sin ponderar por ninguna escala
   * acumulada da la posición correcta.
   * @param {HTMLElement} elemento
   * @param {HTMLElement} raiz
   * @returns {{ left: number, top: number }}
   */
  function posicionRelativaARaizDeReserva(elemento, raiz) {
    let left = 0;
    let top = 0;
    /** @type {HTMLElement | null} */
    let actual = elemento;
    while (actual instanceof window.HTMLElement && actual !== raiz) {
      left += actual.offsetLeft;
      top += actual.offsetTop;
      actual = /** @type {HTMLElement | null} */ (actual.offsetParent);
    }
    return { left, top };
  }

  /**
   * Clampa un desplazamiento propuesto `(xPropuesta, yPropuesta)` para que
   * la caja de FLUJO natural de `item` (invariante frente a `transform`,
   * `offsetLeft`/`offsetTop`/`offsetWidth`/`offsetHeight`, especificación
   * CSSOM View) siga cabiendo dentro del área de scroll de su propio
   * contenedor (`item.offsetParent`, siempre un `.canvas-surface` -- raíz o
   * de una rama/cuerpo anidada, TSK-0072/TSK-0081, ver comentario de
   * cabecera "Límites del área de arrastre" para la justificación completa
   * de por qué el límite es el del contenedor MÁS CERCANO, no siempre el de
   * la lista raíz).
   *
   * TSK-0208 (reporte directo del usuario tras probar TSK-0207: "se pueden
   * mover, pero mínimamente, necesito más rango"): antes de esta tarea, el
   * margen disponible era EXACTAMENTE `contenedor.scrollWidth/scrollHeight -
   * naturalLeft/Top - ancho/alto` -- es decir, el hueco de overflow que YA
   * EXISTIERA en el contenedor antes de mover nada, que normalmente es ~0
   * (`.canvas-surface`/`.canvas-branch-surface` ya NO tienen scroll propio
   * desde que se retiró su `max-height`/`overflow-y: auto`, ver el
   * comentario de cabecera de esa regla en canvas.css: crecen en flujo
   * normal con su contenido, así que su `scrollWidth`/`scrollHeight` natural
   * coincide casi siempre con el tamaño exacto de su contenido, sin ningún
   * margen). Se añade ahora `RANGO_EXTRA_ARRASTRE_LIBRE_PX` en las 4
   * direcciones:
   * - Hacia DELANTE (derecha/abajo, `maxX`/`maxY`): respaldado por espacio
   *   REAL (`reservarEspacioHaciaDelante`, más arriba) -- alcanzable con
   *   scroll real de la página, no solo matemáticamente permitido.
   * - Hacia ATRÁS (izquierda/arriba, `minX`/`minY`): SIN margen extra --
   *   CORREGIDO (regresión real reportada por el usuario: "el bloque que
   *   contiene hijos del diagrama de flujo no debería poder posicionarse
   *   fuera del canvas"). La versión anterior de este comentario documentaba
   *   aquí un margen hacia atrás acotado solo a "no caer en una coordenada de
   *   DOCUMENTO negativa" (`posicionAbsolutaDeDocumento`, ya retirada): eso
   *   evitaba la inalcanzabilidad por scroll, pero NO evitaba que el bloque se
   *   pintara fuera del panel VISUAL del canvas (`seccion`/`.canvas`, que --
   *   a diferencia de `maxX`/`maxY`, ver más abajo -- nunca reserva espacio
   *   real hacia atrás: `reservarEspacioHaciaDelante` solo puede AGRANDAR la
   *   caja en flujo normal hacia delante, nunca desplazar el origen de todo
   *   el contenido ya colocado hacia atrás, ver su comentario de cabecera).
   *   Cualquier margen hacia atrás mayor que 0 deja, pues, un hueco entre "la
   *   posición matemáticamente permitida" y "el borde real del panel", y un
   *   bloque contenedor (Condicional/Bucle, con toda su rama/cuerpo anidada
   *   dentro) que cae en ese hueco queda pintado fuera del canvas, solapando
   *   el resto de la página -- exactamente el reporte del usuario. Por eso
   *   `minX`/`minY` ya NO añaden ningún margen: el límite hacia atrás es
   *   exactamente `-naturalLeft`/`-naturalTop` (el borde natural EN FLUJO de
   *   `contenedor`, que si es la lista raíz coincide con el borde visual real
   *   de `seccion`), igual que antes de TSK-0208. El margen hacia DELANTE
   *   (`maxX`/`maxY`) no sufre este problema y se mantiene sin cambios: sí
   *   está respaldado por espacio real (`reservarEspacioHaciaDelante`), así
   *   que un bloque nunca puede escapar del canvas en esa dirección --
   *   `seccion` crece con él.
   * @param {HTMLElement} item
   * @param {number} xPropuesta
   * @param {number} yPropuesta
   * @returns {{ x: number, y: number }}
   */
  function limitarDentroDelContenedor(item, xPropuesta, yPropuesta) {
    // `window.HTMLElement` (no el identificador bare `HTMLElement`, que
    // `eslint.config.js` -- fuera de las rutas permitidas de esta tarea --
    // no declara como global): mismo patrón ya usado en este archivo para
    // otros globals de navegador no declarados a secas (ver
    // `globalThis.ResizeObserver`, comentario de cabecera "TSK-0074").
    const contenedor = item.offsetParent;
    if (!(contenedor instanceof window.HTMLElement)) return { x: xPropuesta, y: yPropuesta };

    const naturalLeft = item.offsetLeft;
    const naturalTop = item.offsetTop;
    const ancho = item.offsetWidth;
    const alto = item.offsetHeight;

    // TSK-0208: reserva espacio REAL hacia delante ANTES de calcular
    // `maxX`/`maxY` (ver comentario de cabecera de la función, más arriba) --
    // así `contenedor.scrollWidth`/`scrollHeight`, leídos justo debajo,
    // reflejan (en un navegador real) el nuevo mínimo ya reservado.
    //
    // TSK-0214 (fix de regresión real, reportada con captura): el CLAMP de
    // más abajo (`maxX`/`maxY`/`minX`/`minY`) sigue calculándose sobre
    // `contenedor`, el `offsetParent` INMEDIATO de `item`, SIN NINGÚN CAMBIO
    // -- `naturalLeft`/`naturalTop`/`scrollWidth`/`scrollHeight` solo tienen
    // sentido geométrico relativos a ese contenedor inmediato. Lo único que
    // cambia es DÓNDE se reserva el ESPACIO FÍSICO real
    // (`reservarEspacioHaciaDelante`): nunca ya en `contenedor` directamente
    // si este es una zona de rama/cuerpo anidada (`.canvas-branch-surface`),
    // sino en la raíz de scroll real que la contiene
    // (`encontrarRaizDeReservaDeEspacio`) -- de lo contrario, cada movimiento
    // de un bloque DENTRO de una rama inflaba permanentemente el tamaño de
    // esa rama misma (ver comentario de cabecera de `reservarEspacioHaciaDelante`,
    // más arriba, para el análisis completo de la causa raíz). La posición de
    // `item` relativa a esa raíz (necesaria para saber cuánto `min-width`/
    // `min-height` reservar ahí) la calcula `posicionRelativaARaizDeReserva`.
    const raizDeReserva = encontrarRaizDeReservaDeEspacio(contenedor);
    const posicionEnRaiz = posicionRelativaARaizDeReserva(item, raizDeReserva);
    reservarEspacioHaciaDelante(raizDeReserva, posicionEnRaiz.left, posicionEnRaiz.top, ancho, alto);

    // #5 (petición del usuario): permitir mover los bloques del cuerpo de un
    // bucle/condicional también hacia la IZQUIERDA, no solo a la derecha.
    // `minX = -naturalLeft` deja al bloque llegar solo al borde izquierdo de SU
    // contenedor (una rama anidada `.canvas-branch-surface` está indentada
    // hacia la derecha, así que ese margen es casi nulo). Se amplía el margen
    // izquierdo hasta el borde del PANEL visible del canvas (`seccion`, `.canvas`)
    // -- nunca más allá: eso preserva la corrección anti-regresión ("un bloque
    // no puede pintarse fuera del canvas"). Para un bloque de la lista RAÍZ, su
    // contenedor ya coincide con el panel, así que `margenIzquierdaHastaPanel`
    // es ~0 y `minX` queda EXACTAMENTE en `-naturalLeft` como antes (sin cambio
    // de comportamiento para los bloques raíz). Layout-dependiente
    // (`getBoundingClientRect`): bajo jsdom devuelve ceros -> margen 0 ->
    // comportamiento idéntico al anterior (los tests no cambian).
    let margenIzquierdaHastaPanel = 0;
    // `maxX` por defecto = margen aritmético de siempre. En jsdom / sin layout
    // real (`getBoundingClientRect` devuelve ceros) se conserva EXACTAMENTE este
    // valor -> los tests no cambian. En un navegador real se sustituye por el
    // TOPE DURO al borde derecho del panel (premisa del usuario: el canvas no
    // crece a la derecha; un bloque/rama no puede pintarse más allá del panel).
    let maxX = Math.max(contenedor.scrollWidth, naturalLeft + ancho + RANGO_EXTRA_ARRASTRE_LIBRE_PX) - naturalLeft - ancho;
    if (typeof contenedor.getBoundingClientRect === 'function' && typeof seccion.getBoundingClientRect === 'function') {
      const escala = nivelZoom || 1;
      const rectContenedor = contenedor.getBoundingClientRect();
      const rectPanel = seccion.getBoundingClientRect();
      margenIzquierdaHastaPanel = Math.max(0, (rectContenedor.left - rectPanel.left) / escala);
      // Solo con layout REAL (algún rect no nulo): el borde derecho del item
      // (naturalLeft + ancho + x) no puede superar el borde derecho del panel.
      if (rectPanel.right > 0 || rectContenedor.left > 0) {
        const bordeDerechoPanelEnContenedor = (rectPanel.right - rectContenedor.left) / escala;
        maxX = bordeDerechoPanelEnContenedor - naturalLeft - ancho;
      }
    }
    const minX = -naturalLeft - margenIzquierdaHastaPanel;
    const minY = -naturalTop;
    // `maxY` sin cambios: el canvas SÍ crece hacia abajo (scroll real reservado
    // por `reservarEspacioHaciaDelante`, ahora solo vertical).
    const maxY = Math.max(contenedor.scrollHeight, naturalTop + alto + RANGO_EXTRA_ARRASTRE_LIBRE_PX) - naturalTop - alto;

    return {
      x: maxX >= minX ? Math.min(Math.max(xPropuesta, minX), maxX) : minX,
      y: maxY >= minY ? Math.min(Math.max(yPropuesta, minY), maxY) : minY,
    };
  }

  /**
   * Arrastre LIBRE 2D con el ratón/táctil (vía eventos de ratón estándar,
   * mismo motivo de compatibilidad/determinismo en jsdom que el arrastre-de-
   * reordenado que sustituye, ver comentario de cabecera "TSK-0081" para el
   * porqué completo de este reemplazo): mueve la posición VISUAL de `item`
   * en `EstadoVisual`, NUNCA la estructura lógica del programa (no llama a
   * `reordenarBloque`/`moverBloqueEnContenedor` en ningún punto).
   * @param {MouseEvent} evento
   * @param {string} id
   * @param {HTMLElement} item
   * @param {HTMLButtonElement} botonInicial
   */
  function manejarInicioArrastreLibre(evento, id, item, botonInicial) {
    if (typeof evento.button === 'number' && evento.button !== 0) return;
    evento.preventDefault();
    // `preventScroll: true` (corrección del bug reportado por el usuario "al
    // mover un bloque se descolocan los de después"): sin él, enfocar un bloque
    // que está parcial o totalmente fuera de la vista hace que el navegador
    // haga scroll para traerlo a la vista, y ese salto de scroll desplaza
    // visualmente TODOS los demás bloques de golpe. El foco (necesario para la
    // equivalencia de teclado, Supr/Alt+flechas) se mantiene, pero SIN mover la
    // página. No cambia ninguna lógica de arrastre.
    botonInicial.focus({ preventScroll: true });

    const clientXInicio = evento.clientX;
    const clientYInicio = evento.clientY;
    const posicionPrevia = obtenerPosicion(estadoVisual, id) ?? { x: 0, y: 0 };

    let seMovio = false;
    let xFinal = posicionPrevia.x;
    let yFinal = posicionPrevia.y;

    /** @param {MouseEvent} eventoMover */
    function manejarMover(eventoMover) {
      // Corrección de zoom (ver comentario junto a `nivelZoom`): el delta de
      // `clientX/clientY` es SIEMPRE en píxeles de pantalla reales, sin
      // relación con ningún `transform` -- pero `item.style.transform =
      // translate(...)`, al aplicarse DENTRO de `superficie` (que ya tiene su
      // propio `transform: scale`), se reescala una segunda vez al pintarse.
      // Sin dividir por `nivelZoom` aquí, el bloque se movería más rápido o
      // más lento que el puntero según el zoom actual.
      const dx = (eventoMover.clientX - clientXInicio) / nivelZoom;
      const dy = (eventoMover.clientY - clientYInicio) / nivelZoom;
      if (!seMovio && Math.hypot(dx, dy) < UMBRAL_ARRASTRE_LIBRE_PX) return;
      seMovio = true;
      item.classList.add('canvas-block-item-dragging');

      // TSK-0061 (conservado sin cambios de comportamiento, ver comentario
      // de cabecera "Papelera por arrastre"): mientras el puntero sobrevuela
      // la papelera, se "arma" visualmente -- soltar aquí elimina en vez de
      // reposicionar (`manejarFin`, más abajo).
      const sobrePapelera = estaSobrePapelera(eventoMover.clientX, eventoMover.clientY);
      papelera.classList.toggle('canvas-trash-armada', sobrePapelera);

      const limitada = limitarDentroDelContenedor(item, posicionPrevia.x + dx, posicionPrevia.y + dy);
      xFinal = limitada.x;
      yFinal = limitada.y;
      item.style.transform = `translate(${xFinal}px, ${yFinal}px)`;
      // Rendimiento (ver comentario de cabecera "Rendimiento"): recálculo
      // síncrono por evento, deliberadamente sin `requestAnimationFrame`.
      actualizarConexiones();
    }

    /** @param {MouseEvent} [eventoFin] Ausente cuando `destruir()` fuerza el
     * fin de un arrastre a mitad de gesto (docs/03-frontend.md §8.3): en ese
     * caso NO hay coordenada de soltado real, así que nunca se interpreta
     * como "soltar sobre la papelera", y la posición no llega a
     * confirmarse/anunciarse (queda como estaba antes de este gesto, el
     * `transform` en curso se pierde junto con el propio elemento
     * desmontado). */
    function manejarFin(eventoFin) {
      document.removeEventListener('mousemove', /** @type {any} */ (manejarMover));
      document.removeEventListener('mouseup', /** @type {any} */ (manejarFin));
      detenerArrastreActivo = null;
      papelera.classList.remove('canvas-trash-armada');
      item.classList.remove('canvas-block-item-dragging');

      if (!seMovio) return; // clic simple: ni reposiciona ni anuncia

      // Soltar sobre la papelera tras un arrastre real elimina el bloque
      // (TSK-0061, conservado): reutiliza la misma `eliminarBloque` que
      // Supr/Backspace y la activación de la papelera por clic/teclado.
      if (eventoFin && estaSobrePapelera(eventoFin.clientX, eventoFin.clientY)) {
        eliminarBloque(id);
        return;
      }

      // TSK-0081: aquí, y SOLO aquí (al soltar, no en cada `mousemove`), se
      // confirma la posición en `EstadoVisual` -- durante el arrastre el
      // `transform` ya se actualizaba en vivo (`manejarMover`), pero
      // `establecerPosicion` no se llamaba en cada evento para no generar
      // escrituras intermedias innecesarias sobre un objeto que otros
      // módulos (`editor-persistence.js`) pueden observar/serializar en
      // cualquier momento.
      establecerPosicion(estadoVisual, id, { x: xFinal, y: yFinal });
      // TSK-0206: al SOLTAR (confirmación del gesto, no en cada `mousemove`
      // de `manejarMover` -- ver el comentario de cabecera de
      // `refrescarConexionesYCapaInteractivaTrasMovimiento` para la decisión
      // de diseño completa), se refresca también la capa de puertos/asas de
      // flecha -- hasta ahora solo se llamaba a `actualizarConexiones()`
      // (líneas SVG) en `manejarMover`, dejando los puertos/asas de este
      // bloque y de sus arcos conectados congelados en la posición ANTERIOR
      // al arrastre.
      refrescarConexionesYCapaInteractivaTrasMovimiento();
      // #6: al soltar, ceñir la altura de la superficie al bloque más bajo
      // (el arrastre libre no dispara `renderizar()`, así que se ajusta aquí).
      ajustarAlturaSuperficieRaiz();
      const etiqueta = etiquetaDe(id);
      anunciar(
        `Bloque ${etiqueta} movido visualmente a la posición horizontal ${Math.round(xFinal)}px, ` +
          `vertical ${Math.round(yFinal)}px. Su orden en el programa no cambia.`
      );
    }

    document.addEventListener('mousemove', /** @type {any} */ (manejarMover));
    document.addEventListener('mouseup', /** @type {any} */ (manejarFin));
    detenerArrastreActivo = /** @type {() => void} */ (/** @type {unknown} */ (manejarFin));
  }

  /**
   * Construye el `<li>` de un único bloque colocado en `contenedorId`
   * (Secuencia raíz o rama/cuerpo anidado, TSK-0072), incluidos sus
   * controles (mover/eliminar; TSK-0076: ya sin "duplicar", ver comentario
   * de cabecera "ACTUALIZACIÓN (TSK-0076...") y, si `nodo` es un `Condicional` o
   * un bucle, sus zonas de rama/cuerpo anidadas (`crearZonaRama`, más abajo,
   * llamada recursivamente): es esa recursión la que permite el anidamiento
   * arbitrario (un Condicional dentro de la rama de otro, etc., criterio de
   * aceptación 5 de TSK-0072). DOM seguro (docs/03-frontend.md §8.2): solo
   * `createElement`/`textContent`.
   * @param {NodoModelo} nodo
   * @param {string} contenedorId
   * @returns {HTMLLIElement}
   */
  function crearItemBloque(nodo, contenedorId) {
    const item = document.createElement('li');
    item.className = 'canvas-block-item';

    const fila = document.createElement('div');
    fila.className = 'canvas-block-row';

    const etiqueta = etiquetasPorId.get(nodo.id) ?? nodo.nodo;

    // TSK-0068: contenido real (variable/condición) cuando existe, si no
    // la etiqueta genérica de siempre (ver comentario de cabecera
    // "Alcance del contenido real").
    const contenidoReal = contenidoRealDeNodo(nodo);
    const textoPrincipalCompleto = contenidoReal ?? etiqueta;

    const boton = document.createElement('button');
    boton.type = 'button';
    boton.className = 'canvas-block';
    boton.dataset.nodeId = nodo.id;
    // TSK-0063: metadato de presentación adicional (forma/color por tipo,
    // ver comentario de cabecera). `nodo.nodo` es el propio valor de
    // `TIPO_NODO` ("Asignacion", "Condicional", "Comentario", ...): un
    // selector CSS `[data-block-type="..."]` en canvas.css lo usa para
    // aplicar `clip-path`/`background-color` sin tocar el nombre accesible
    // ni el modelo lógico.
    boton.dataset.blockType = nodo.nodo;

    // TSK-0068: contenido principal en su propio nodo de texto (DOM
    // seguro: `createElement` + `textContent`, docs/03-frontend.md §8.2).
    // El texto VISIBLE puede acortarse (`truncarTexto`); el `aria-label` de
    // más abajo lleva siempre el texto completo, truncado o no.
    const contenidoPrincipal = document.createElement('span');
    contenidoPrincipal.className = 'canvas-block-content';
    contenidoPrincipal.textContent = truncarTexto(textoPrincipalCompleto, LIMITE_CARACTERES_CONTENIDO);
    boton.append(contenidoPrincipal);

    // TSK-0068 (criterio de aceptación: "el texto no se trunca de forma
    // inaccesible... mediante title/aria-label con el valor completo"):
    // el nombre accesible del botón SIEMPRE es el texto completo,
    // independientemente de si el texto visible se acortó o no -- evita una
    // rama condicional que pudiera desincronizarse entre lo mostrado y lo
    // anunciado. TSK-0213: eliminado el sufijo "(nombre corto)" del nombre
    // accesible junto con el propio campo.
    boton.setAttribute('aria-label', textoPrincipalCompleto);
    if (textoPrincipalCompleto.length > LIMITE_CARACTERES_CONTENIDO) {
      // `title` (tooltip nativo al pasar el puntero): mismo texto completo
      // que ya lleva `aria-label`, para un usuario con puntero/vista que
      // vea el texto acortado y quiera comprobar el valor íntegro.
      boton.title = textoPrincipalCompleto;
    }

    boton.addEventListener('keydown', (evento) =>
      manejarTeclaEnBloque(/** @type {KeyboardEvent} */ (evento), nodo.id, contenedorId, item)
    );
    // TSK-0081: `mousedown` sobre un bloque colocado ya NO reordena (ver
    // comentario de cabecera "Reemplazo del arrastre por puntero anterior")
    // -- ahora inicia el arrastre LIBRE 2D, que mueve `item` (el `<li>`
    // completo, incluidas sus zonas de rama/cuerpo anidadas si las tiene),
    // no solo `boton`.
    boton.addEventListener('mousedown', (evento) =>
      manejarInicioArrastreLibre(/** @type {MouseEvent} */ (evento), nodo.id, item, boton)
    );
    // TSK-0061: "seleccionar" un bloque (para la papelera activada por
    // clic/teclado) es, igual que en el resto de este módulo, tener el
    // foco. Se actualiza en `focus`, no en `click`, para que Tab también
    // cuente como selección (equivalencia de teclado, DEC-027).
    boton.addEventListener('focus', () => {
      idBloqueSeleccionado = nodo.id;
      // TSK-0194: enfocar un bloque vuelve a dejar la papelera/Supr
      // actuando sobre bloques (ver `tipoSeleccionActual`/
      // `manejarActivarPapelera`, generalizados para también borrar una
      // flecha con el foco más reciente).
      tipoSeleccionActual = 'bloque';
    });

    // TSK-0076: el botón "Duplicar {etiqueta}" (introducido en TSK-0061) se
    // retira de la UI por petición directa del usuario ("no quiero que
    // aparezca duplicar al lado del bloque"). No existía ningún atajo de
    // teclado independiente del propio `<button>` visible (`duplicarBloque`
    // solo se invocaba desde el `click` de ese botón, verificado antes de
    // decidir: ver también la función `duplicarBloque`, retirada junto con
    // este bloque por quedar sin ningún llamador, código muerto). No queda
    // ninguna vía de duplicado en este módulo; `block-operations.js`
    // (módulo NO montado por `editor-controller.js` desde TSK-0061, ver
    // comentario de cabecera "TSK-0061" más arriba) conserva su propia
    // implementación de "duplicar" sin relación con esta.
    fila.append(boton);
    item.append(fila);
    botonesPorId.set(nodo.id, boton);
    // TSK-0081: aplica la posición VISUAL ya guardada (si la hay) apenas se
    // crea `item`, antes de insertarlo en el DOM real -- ver
    // `aplicarTransformPosicion` para por qué es seguro llamarla ya (no
    // depende de layout calculado, solo lee `EstadoVisual`).
    aplicarTransformPosicion(item, nodo.id);

    // TSK-0072: zonas de rama/cuerpo anidadas, SOLO para Condicional/Bucle.
    // Alcance visual deliberadamente simple (ver comentario de cabecera):
    // una lista indentada por zona, apilada verticalmente si hay dos.
    if (esNodoConRamas(nodo)) {
      const zonas = document.createElement('div');
      zonas.className = 'canvas-block-branches';

      if (nodo.nodo === TIPO_NODO.CONDICIONAL) {
        const ramaSi = /** @type {NodoModelo} */ (nodo.rama_si);
        zonas.append(crearZonaRama(ramaSi, `Rama verdadero de Condicional ${nodo.id}`, 'Si se cumple', 'verdadero'));
        const ramaSino = /** @type {NodoModelo | null} */ (nodo.rama_sino);
        if (ramaSino) {
          zonas.append(crearZonaRama(ramaSino, `Rama falso de Condicional ${nodo.id}`, 'Si no se cumple', 'falso'));
        }
      } else {
        const cuerpo = /** @type {NodoModelo} */ (nodo.cuerpo);
        zonas.append(crearZonaRama(cuerpo, `Cuerpo del bucle ${nodo.id}`, 'Cuerpo del bucle', 'cuerpo'));
      }

      item.append(zonas);
    }

    return item;
  }

  /**
   * TSK-0207: Alt+flecha para mover una ZONA de rama/cuerpo COMPLETA (la caja
   * entera "Si se cumple"/"Si no se cumple"/"Cuerpo del bucle", no solo los
   * bloques de su interior) -- mismo mecanismo GENÉRICO de posición visual
   * libre (`estadoVisual`/`obtenerPosicion`/`establecerPosicion`/
   * `aplicarTransformPosicion`/`limitarDentroDelContenedor`, DEC-028 Frente
   * B) que ya usan un bloque (`manejarTeclaEnBloque`) o un ancla
   * (`manejarTeclaEnAncla`, calcado casi al detalle), sin reimplementar nada
   * de eso. Las únicas diferencias: `item` es el propio
   * `<section class="canvas-branch">` (así el `transform` desplaza también
   * TODOS los bloques de su interior, que viven dentro de ese `<section>` --
   * un `transform` en un ancestro se compone visualmente con el de cada
   * descendiente en cualquier navegador real) y el texto anunciado describe
   * una "zona", no un "bloque" suelto.
   * @param {KeyboardEvent} evento
   * @param {string} id Igual a `secuencia.id` (ver el comentario de cabecera
   *   de `configurarArrastreZonaRama` sobre por qué reutilizar ese mismo id
   *   como clave de posición visual es seguro).
   * @param {HTMLElement} item El `<section class="canvas-branch">` completo.
   * @param {string} etiquetaZona Texto descriptivo para el anuncio.
   */
  function manejarTeclaEnZonaRama(evento, id, item, etiquetaZona) {
    if (!evento.altKey) return;
    const deltaXY = TECLA_A_DELTA_XY[/** @type {keyof typeof TECLA_A_DELTA_XY} */ (evento.key)];
    if (!deltaXY) return;
    evento.preventDefault();
    const previa = obtenerPosicion(estadoVisual, id) ?? { x: 0, y: 0 };
    const limitada = limitarDentroDelContenedor(item, previa.x + deltaXY.x, previa.y + deltaXY.y);
    establecerPosicion(estadoVisual, id, limitada);
    aplicarTransformPosicion(item, id);
    // TSK-0206/TSK-0207: mismo refresco completo (líneas + capa interactiva,
    // con conservación de foco) que las otras dos variantes de Alt+flecha ya
    // existentes -- ver el comentario de cabecera de
    // `refrescarConexionesYCapaInteractivaTrasMovimiento`.
    refrescarConexionesYCapaInteractivaTrasMovimiento();
    anunciar(
      `Zona ${etiquetaZona} movida visualmente con Alt+flecha a la posición horizontal ` +
        `${Math.round(limitada.x)}px, vertical ${Math.round(limitada.y)}px. Los bloques de su interior se mueven ` +
        'con ella; su orden y pertenencia en el programa no cambian.'
    );
  }

  /**
   * TSK-0207: engancha a una zona de rama/cuerpo (`.canvas-branch`) el mismo
   * arrastre LIBRE 2D por puntero (`manejarInicioArrastreLibre`) y Alt+flecha
   * por teclado (`manejarTeclaEnZonaRama`, arriba) que ya tienen los bloques
   * colocados y las anclas Inicio/Fin -- mismo patrón exacto que
   * `configurarArrastreAncla` (ver su comentario de cabecera), incluida la
   * restauración de cualquier posición visual ya guardada al montar
   * (`aplicarTransformPosicion`, para que una zona movida sobreviva a un
   * re-render/autoguardado).
   *
   * Sobre la clave de `estadoVisual`: `id` es `secuencia.id` -- el MISMO id
   * que `lista.dataset.contenedorId` ya usa como identificador de CONTENIDO
   * ("a qué array de sentencias pertenecen los bloques de esta zona", ver
   * `crearZonaRama` más abajo). Reutilizarlo TAMBIÉN como clave de POSICIÓN
   * VISUAL en el mismo `Map` de `estadoVisual` es seguro: `generarId()`
   * (`editor-model.js`) reparte un ÚNICO contador global entre TODOS los
   * tipos de nodo (bloques Y `Secuencia` por igual, ver su comentario de
   * cabecera) -- dos nodos cualesquiera, sean del tipo que sean, NUNCA
   * comparten el mismo string de id, así que no hay colisión posible entre
   * "posición de un bloque" y "posición de una zona" aunque convivan en el
   * mismo `Map<string, {x,y}>`. Se registra además una etiqueta legible en
   * `etiquetasPorId` (igual que Inicio/Fin en la cabecera de este módulo):
   * sin ella, el mensaje que anuncia `manejarInicioArrastreLibre` al soltar
   * el arrastre por puntero (`etiquetasPorId.get(id) ?? 'bloque'`) diría
   * "Bloque bloque movido..." en vez de identificar la zona.
   * @param {HTMLButtonElement} boton El botón "Mover rama"/"Mover cuerpo" dedicado.
   * @param {HTMLElement} zona El `<section class="canvas-branch">` completo.
   * @param {string} id
   * @param {string} etiquetaZona
   */
  function configurarArrastreZonaRama(boton, zona, id, etiquetaZona) {
    etiquetasPorId.set(id, etiquetaZona);
    boton.addEventListener('mousedown', (evento) =>
      manejarInicioArrastreLibre(/** @type {MouseEvent} */ (evento), id, zona, boton)
    );
    boton.addEventListener('keydown', (evento) =>
      manejarTeclaEnZonaRama(/** @type {KeyboardEvent} */ (evento), id, zona, etiquetaZona)
    );
    aplicarTransformPosicion(zona, id);
  }

  /**
   * Construye una única zona de rama/cuerpo (`<section aria-labelledby>` con
   * su propio encabezado visible y su propia lista `<ul>` de bloques
   * colocados, TSK-0072): mismo mecanismo de colocar (drag&drop propio,
   * `contenedorId = secuencia.id`)/reordenar/eliminar que la lista raíz,
   * nunca una reimplementación distinta. Recursiva vía `crearItemBloque`
   * (cada bloque de la lista puede a su vez tener sus propias zonas).
   * @param {NodoModelo} secuencia Nodo `Secuencia` de la rama/cuerpo
   *   (`rama_si`/`rama_sino`/`cuerpo`), ya con `id` propio desde que se
   *   colocó el Condicional/Bucle (ver `palette-view.js`).
   * @param {string} etiquetaAccesible Nombre accesible único de la zona
   *   (incluye el id del bloque contenedor para garantizar unicidad si hay
   *   varios Condicionales/Bucles hermanos, mismo patrón que
   *   `connectors.js#construirListaSecuencia`).
   * @param {string} tituloVisible Texto corto mostrado como encabezado de la
   *   zona (p.ej. "Si se cumple"/"Cuerpo del bucle", ver criterio de
   *   aceptación de la ficha).
   * @param {'verdadero' | 'falso' | 'cuerpo'} tipoRama TSK-0196 (pulido
   *   visual, DEC-069 fase 3): volcado a `dataset.branchKind` para que
   *   `canvas.css` pinte columnas divergentes reales (verdadero/falso lado a
   *   lado) con un acento de borde propio por tipo -- refuerzo puramente
   *   visual, nunca la única señal (el propio `tituloVisible` ya identifica
   *   la zona sin él, WCAG 2.2 SC 1.4.1).
   * @returns {HTMLElement}
   */
  function crearZonaRama(secuencia, etiquetaAccesible, tituloVisible, tipoRama) {
    contadorZonasRama += 1;
    const idTituloZona = `canvas-branch-heading-${contadorInstanciasCanvas}-${contadorZonasRama}`;

    const zona = document.createElement('section');
    zona.className = 'canvas-branch';
    zona.dataset.branchKind = tipoRama;
    zona.setAttribute('aria-labelledby', idTituloZona);

    const titulo = document.createElement('p');
    titulo.id = idTituloZona;
    titulo.className = 'canvas-branch-heading';

    // Texto VISIBLE corto (frontmatter de la tarea, literal: "Si se
    // cumple"/"Si no se cumple"/"Cuerpo del bucle"), en su propio nodo de
    // texto seguro (docs/03-frontend.md §8.2).
    const tituloVisibleSpan = document.createElement('span');
    tituloVisibleSpan.textContent = tituloVisible;

    // Detalle disambiguador SOLO para el nombre accesible (`.canvas-branch-
    // heading-detalle`, visualmente oculto pero leído por un lector de
    // pantalla, canvas.css): sin él, dos Condicionales HERMANOS colocados en
    // el mismo contenedor producirían dos regiones "Si se cumple" con el
    // MISMO nombre accesible -- ambiguo para un usuario de lector de
    // pantalla que navegue por regiones (y, en la práctica, el tipo de
    // duplicado que axe-core suele señalar). Se añade como texto adicional
    // DENTRO del propio `titulo` (nunca un `aria-label` aparte que pudiera
    // desincronizarse de `aria-labelledby`): el nombre accesible resultante
    // concatena ambos ("Si se cumple Rama verdadero de Condicional
    // <id-real-único>"), cumpliendo a la vez el texto visible corto exigido
    // por el frontmatter y el ejemplo de nombre distintivo del criterio de
    // aceptación ("Rama verdadero de Condicional", "Cuerpo del bucle").
    const detalleSpan = document.createElement('span');
    detalleSpan.className = 'canvas-branch-heading-detalle';
    detalleSpan.textContent = ` ${etiquetaAccesible}`;

    titulo.append(tituloVisibleSpan, detalleSpan);

    // TSK-0207: control dedicado para mover la ZONA COMPLETA (no solo los
    // bloques de su interior, que ya podían moverse desde antes). Vive como
    // HERMANO de `titulo` (nunca DENTRO de él): `titulo` es el elemento
    // referenciado por el `aria-labelledby` de `zona` de arriba, y el
    // algoritmo de cómputo del nombre accesible a partir de
    // `aria-labelledby` incorpora el nombre accesible de cualquier control
    // interactivo embebido en el contenido referenciado -- si este botón
    // viviera dentro de `titulo`, su propio `aria-label` contaminaría/
    // duplicaría el nombre de región ya existente, justo lo que prohíbe el
    // criterio de aceptación ("el nombre de región... NO se ve alterado por
    // el nuevo control"). Por eso se agrupan visualmente en `cabecera` (un
    // `<div>` sin semántica propia), no dentro de `titulo`.
    const etiquetaMoverZona = `${tituloVisible} (${etiquetaAccesible})`;
    const botonMoverZona = document.createElement('button');
    botonMoverZona.type = 'button';
    botonMoverZona.className = 'canvas-branch-move';
    const iconoMoverZona = document.createElement('span');
    iconoMoverZona.setAttribute('aria-hidden', 'true');
    iconoMoverZona.textContent = '✥';
    botonMoverZona.append(iconoMoverZona, document.createTextNode(' Mover'));
    // Nombre accesible PROPIO y DISTINTO del nombre de región ya existente
    // (ese es "Si se cumple Rama verdadero de Condicional <id>", vía
    // `aria-labelledby` sobre `titulo`): este describe una ACCIÓN ("mover"),
    // no qué es la zona.
    botonMoverZona.setAttribute('aria-label', `Mover rama: ${etiquetaMoverZona}`);

    const cabecera = document.createElement('div');
    cabecera.className = 'canvas-branch-header';
    cabecera.append(titulo, botonMoverZona);

    const lista = document.createElement('ul');
    lista.className = 'canvas-surface canvas-branch-surface';
    lista.dataset.contenedorId = secuencia.id;

    const sentencias = /** @type {NodoModelo[]} */ (
      /** @type {Record<string, unknown>} */ (secuencia).sentencias
    );
    sentencias.forEach((nodoHijo) => {
      lista.append(crearItemBloque(nodoHijo, secuencia.id));
    });

    lista.addEventListener('dragover', /** @type {any} */ (manejarDragOver));
    lista.addEventListener(
      'drop',
      /** @type {any} */ ((/** @type {DragEvent} */ evento) => manejarDrop(evento, secuencia.id))
    );

    zona.append(cabecera, lista);

    // TSK-0073 (criterio de aceptación: "el primer bloque de cada rama, o
    // hacia un indicador de rama vacía si no tiene contenido"): un `<p>`
    // decorativo, solo presente cuando la rama/cuerpo no tiene NINGÚN bloque
    // colocado todavía -- registrado en `indicadoresVaciosPorContenedor` para
    // que `actualizarConexiones` pueda dibujar la flecha V/F (o de entrada al
    // cuerpo de un Bucle) hacia él en vez de hacia un bloque inexistente. No
    // reemplaza la información ya accesible del encabezado de la zona (que
    // ya identifica "Si se cumple"/"Si no se cumple"/"Cuerpo del bucle"): es
    // un refuerzo visual, no la única fuente de esa información (WCAG 2.2 SC
    // 1.4.1).
    if (sentencias.length === 0) {
      const indicadorVacio = document.createElement('p');
      indicadorVacio.className = 'canvas-branch-empty';
      indicadorVacio.textContent = '(rama vacía)';
      zona.append(indicadorVacio);
      indicadoresVaciosPorContenedor.set(secuencia.id, indicadorVacio);
    }

    // TSK-0207: engancha el arrastre libre 2D por puntero + Alt+flecha por
    // teclado a la zona ENTERA (mismo mecanismo ya usado por bloques/anclas,
    // ver el comentario de cabecera de `configurarArrastreZonaRama`) y
    // restaura cualquier posición visual ya guardada al montar. Se hace al
    // FINAL, con `zona` ya completamente construida (título + botón +
    // lista + posible indicador de rama vacía): `aplicarTransformPosicion`
    // solo lee `estadoVisual` y escribe `zona.style.transform`, no depende de
    // que el resto de hijos ya estén insertados, pero mantener esta llamada
    // como el último paso evita cualquier duda sobre orden de construcción.
    configurarArrastreZonaRama(botonMoverZona, zona, secuencia.id, etiquetaMoverZona);

    return zona;
  }

  /** Reconstruye la lista de bloques colocados a partir de
   * `programaActual.cuerpo.sentencias`, en flujo normal de documento (sin
   * `left`/`top`): el ORDEN del array ES la posición visual. Recursivo desde
   * TSK-0072 (`crearItemBloque`/`crearZonaRama`): cada bloque de tipo
   * Condicional/Bucle renderiza además sus propias zonas de rama/cuerpo
   * anidadas, con el mismo mecanismo de colocar/reordenar/eliminar. DOM
   * seguro (docs/03-frontend.md §8.2): solo `createElement`/`textContent`.
   * TSK-0073: `superficie` reconstruye también `capaConexiones` y las anclas
   * Inicio/Fin en cada pasada (siempre las MISMAS referencias de nodo DOM,
   * ver `liAnclaInicio`/`liAnclaFin`/`capaConexiones`, nunca recreadas) --
   * ver comentario de cabecera "TSK-0073" sobre por qué ahora viven dentro de
   * `superficie`. */
  /**
   * Ajusta la altura de la superficie RAÍZ para que termine "un poco más
   * abajo" del bloque colocado más bajo (petición del usuario, #6). Mide el
   * borde inferior de cada hijo directo colocado con `getBoundingClientRect`
   * (que ya refleja tanto el `transform: translate` del arrastre libre como el
   * `transform: scale` del zoom) y fija `min-height` a ese máximo + un margen
   * pequeño. Sustituye el margen de reserva de arrastre (hasta
   * `RANGO_EXTRA_ARRASTRE_LIBRE_PX` = 480px por debajo) por un ajuste ceñido
   * tras cada render/soltado, sin recortar ningún bloque (el máximo SIEMPRE
   * incluye el borde inferior real del bloque más bajo). Solo la superficie
   * raíz; las ramas anidadas se ajustan solas a su contenido. Bajo jsdom
   * (`getBoundingClientRect` devuelve ceros) no tiene efecto: no rompe tests.
   */
  function ajustarAlturaSuperficieRaiz() {
    if (typeof superficie.getBoundingClientRect !== 'function') return;
    const rectSup = superficie.getBoundingClientRect();
    const escala = nivelZoom || 1;
    let maxBottom = 0;
    for (const hijo of superficie.children) {
      // El SVG de flechas (`capaConexiones`) es `SVGElement`, no `HTMLElement`,
      // así que ya queda excluido por este `instanceof`. La capa interactiva de
      // controles (`liCapaInteractiva`) sí es un `<li>`: se excluye aparte para
      // que no defina el "bloque más bajo".
      if (!(hijo instanceof globalThis.HTMLElement)) continue;
      if (hijo === liCapaInteractiva) continue;
      const rect = hijo.getBoundingClientRect();
      const bottom = (rect.bottom - rectSup.top) / escala;
      if (bottom > maxBottom) maxBottom = bottom;
    }
    const MARGEN_INFERIOR_PX = 40;
    if (maxBottom > 0) {
      superficie.style.minHeight = `${Math.ceil(maxBottom + MARGEN_INFERIOR_PX)}px`;
    }
  }

  function renderizar() {
    superficie.replaceChildren();
    botonesPorId.clear();
    indicadoresVaciosPorContenedor.clear();
    contadorZonasRama = 0;

    const sentencias = sentenciasActuales();
    const idRaiz = idContenedorRaiz();

    const itemsBloques = sentencias.map((nodo) => crearItemBloque(nodo, idRaiz));
    // TSK-0194: `liCapaInteractiva` se añade DESPUÉS de `liAnclaFin` -- fuera
    // de la secuencia Inicio-bloques-Fin en sí (que sigue siendo,
    // exactamente igual que antes de esta tarea, "primer/último botón
    // real = Inicio/Fin"), como una capa de controles de FINO detalle sobre
    // el diagrama ya colocado (ver comentario de cabecera "TSK-0194").
    superficie.append(capaConexiones, liAnclaInicio, ...itemsBloques, liAnclaFin, liCapaInteractiva);

    // Redibuja las flechas a partir del nuevo orden/conjunto de botones de
    // TODOS los contenedores (raíz + ramas/cuerpo anidados, TSK-0073): debe
    // ser el ÚLTIMO paso de `renderizar()`, después de que `botonesPorId`/
    // `indicadoresVaciosPorContenedor` reflejen ya el DOM recién creado.
    actualizarConexiones();

    // TSK-0194: re-deriva y repinta la capa interactiva de flechas a partir
    // del `programaActual` YA actualizado (después de `actualizarConexiones`,
    // que a su vez depende de que `botonesPorId` ya esté poblado -- ambas
    // capas leen la misma referencia de nodo DOM de cada bloque).
    recalcularGrafoInteractivo();
    renderizarCapaInteractiva();

    // #6 (petición del usuario): ceñir la altura de la superficie al bloque
    // colocado más bajo. Se ejecuta al final, con el DOM ya poblado, para
    // medir posiciones reales. Diferido a la siguiente vuelta de animación
    // para que el navegador haya aplicado ya el layout/transform recién
    // escritos (bajo jsdom/tests no hay `requestAnimationFrame` fiable, así que
    // también se llama de forma directa: la síncrona no hace nada útil sin
    // layout, la diferida es la que cuenta en el navegador real).
    ajustarAlturaSuperficieRaiz();
    if (typeof globalThis.requestAnimationFrame === 'function') {
      globalThis.requestAnimationFrame(() => ajustarAlturaSuperficieRaiz());
    }
  }

  /**
   * Coloca `nodo` dentro del contenedor `contenedorId` (Secuencia raíz o
   * cualquier rama/cuerpo anidado, TSK-0072): implementación GENÉRICA
   * compartida por `colocarBloque` (API pública, siempre sobre la raíz) y
   * por `manejarDrop` de cualquier zona de rama/cuerpo. Delega en
   * `conectarBloque` (`connectors.js`), nunca reimplementada.
   *
   * Si `nodo` es `null` (Inicio/Fin/Conector, o cualquier otro bloque futuro
   * sin nodo IR propio) NO se modifica el modelo lógico: se limita a dar el
   * feedback perceptible explícito exigido por el criterio de aceptación.
   * @param {NodoModelo | null} nodo
   * @param {DefinicionBloqueMinima} definicion
   * @param {number | undefined} indiceSugerido
   * @param {string} contenedorId
   * @returns {NodoModelo | null}
   */
  function colocarEnContenedor(nodo, definicion, indiceSugerido, contenedorId) {
    const etiqueta =
      typeof definicion.etiqueta === 'string' && definicion.etiqueta.length > 0
        ? definicion.etiqueta
        : (nodo && nodo.nodo) || 'bloque';

    if (nodo === null) {
      anunciar(
        `Aviso: "${etiqueta}" no añade ningún bloque nuevo al programa (ver paleta); ` +
          'no se ha colocado nada en el área de trabajo.',
        { aviso: true }
      );
      return null;
    }

    if (!esNodo(nodo)) {
      throw new TypeError('colocarBloque: "nodo" debe ser un NodoModelo válido o null');
    }

    try {
      programaActual = conectarBloque(programaActual, { contenedorId, nodo, indice: indiceSugerido });
    } catch (error) {
      anunciar(`No se pudo colocar "${etiqueta}": ${/** @type {Error} */ (error).message}`, { aviso: true });
      return null;
    }
    etiquetasPorId.set(nodo.id, etiqueta);
    invalidarDeshacer();

    renderizar();
    anunciar(`Bloque ${etiqueta} colocado en el área de trabajo.`);
    if (notificarCambioPrograma) notificarCambioPrograma(programaActual);
    return nodo;
  }

  /**
   * Punto de entrada único para colocar un bloque en la Secuencia RAÍZ, sea
   * por arrastre (lo invoca `manejarDrop` tras parsear `dataTransfer`) o
   * "por teclado"/API directa (ver nota de ambigüedad en la cabecera del
   * archivo). Para colocar dentro de una rama/cuerpo anidado, cada zona
   * (`crearZonaRama`, TSK-0072) tiene su PROPIO `dragover`/`drop` que llama
   * a `colocarEnContenedor` directamente con el `contenedorId` de esa zona.
   * @param {NodoModelo | null} nodo
   * @param {DefinicionBloqueMinima} [definicion]
   * @param {number} [indiceSugerido] Índice de inserción en la Secuencia
   *   raíz; si se omite, se añade al final (mismo comportamiento que antes
   *   de esta tarea para la colocación "por teclado"/API directa).
   * @returns {NodoModelo | null}
   */
  function colocarBloque(nodo, definicion = {}, indiceSugerido) {
    return colocarEnContenedor(nodo, definicion, indiceSugerido, idContenedorRaiz());
  }

  /** @param {DragEvent} evento */
  function manejarDragOver(evento) {
    // preventDefault es obligatorio: el comportamiento por defecto del
    // navegador rechaza cualquier "drop" si no se cancela "dragover".
    evento.preventDefault();
    if (evento.dataTransfer) evento.dataTransfer.dropEffect = 'copy';
  }

  /**
   * @param {DragEvent} evento
   * @param {string} [contenedorId] Contenedor destino (TSK-0072); por
   *   defecto la Secuencia raíz (mismo comportamiento que antes de esta
   *   tarea para el `drop` sobre `superficie`).
   */
  function manejarDrop(evento, contenedorId = idContenedorRaiz()) {
    evento.preventDefault();
    const dt = evento.dataTransfer;
    if (!dt) return;

    const idBloque = dt.getData(MIME_BLOQUE_ID);
    if (!idBloque) return; // no procede de un origen que siga este contrato: se ignora

    const etiqueta = dt.getData('text/plain') || idBloque;
    const json = dt.getData(MIME_BLOQUE);
    /** @type {NodoModelo | null} */
    let nodo = null;
    if (json) {
      try {
        const candidato = JSON.parse(json);
        nodo = esNodo(candidato) ? candidato : null;
      } catch {
        nodo = null;
      }
    }

    // Índice de inserción a partir de la coordenada vertical del cursor: si
    // se suelta por encima del centro de un bloque ya colocado, el nuevo
    // bloque se inserta justo antes de ese bloque; si no, al final.
    const indiceDestino = calcularIndiceObjetivo(evento.clientY, contenedorId);
    colocarEnContenedor(nodo, { id: idBloque, etiqueta }, indiceDestino, contenedorId);
  }

  superficie.addEventListener('dragover', /** @type {any} */ (manejarDragOver));
  superficie.addEventListener('drop', /** @type {any} */ (manejarDrop));
  // TSK-0073 (ver comentario de cabecera): a diferencia de TSK-0060/DEC-027,
  // YA NO hace falta un listener de `scroll` que recalcule las flechas --
  // `capaConexiones` vive dentro de `superficie` y sus coordenadas son
  // estables frente al scroll (`puntoCentral`), así que el propio navegador
  // las desplaza de forma nativa junto con el resto del contenido.

  // --------------------------------------------------------------------------
  // TSK-0074: recalcular tras la conexión REAL de `superficie` al DOM.
  // --------------------------------------------------------------------------
  // Causa raíz confirmada (frontmatter de la tarea): `renderizar()` (más
  // abajo) se invoca de forma SÍNCRONA aquí mismo, durante la construcción de
  // `crearVistaCanvas()` -- es decir, ANTES de que `seccion` (el elemento
  // devuelto por esta función) se inserte en el `document` real
  // (`editor-controller.js` lo hace DESPUÉS, fuera de este módulo). En ese
  // instante, `superficie` es un subárbol DESCONECTADO: en un navegador real
  // (a diferencia de jsdom, que YA devuelve ceros con o sin conexión, ver
  // comentario junto a `puntoCentral`/cabecera "TSK-0073") `getBoundingClientRect`/
  // `scrollWidth`/`scrollHeight` devuelven siempre 0 sobre un elemento sin
  // layout calculado, así que la primera pasada de `actualizarConexiones()`
  // dibuja todas las líneas en (0,0)-(0,0) -- invisibles -- y nunca se
  // repetía el cálculo tras la conexión real al DOM.
  //
  // Solución autocontenida (sin acoplar este módulo a que
  // `editor-controller.js` llame a nada explícito tras insertar `seccion`,
  // que ampliaría `rutas_permitidas` innecesariamente): un `ResizeObserver`
  // sobre `superficie` dispara `actualizarConexiones()` en CADA cambio de su
  // tamaño de contenido observado -- incluida la notificación inicial que la
  // propia API dispara al llamar a `observe()` (spec CSSOM View: "the user
  // agent must … queue a resize observation" incluso si el tamaño inicial es
  // 0x0), y de nuevo en cuanto el navegador calcula layout real tras la
  // inserción en el DOM (el tamaño deja de ser 0x0), y en cualquier resize
  // posterior (p.ej. el usuario cambia el tamaño de la ventana, o el
  // contenido crece/decrece) -- exactamente el criterio de aceptación de esta
  // tarea. Es un mecanismo DISTINTO del listener de `scroll` que TSK-0073
  // eliminó deliberadamente (ese recalculaba por DESPLAZAMIENTO del
  // contenido, un problema ya resuelto de raíz por `puntoCentral`; este
  // recalcula por CAMBIO DE TAMAÑO/layout, un problema distinto): no lo
  // reintroduce ni lo sustituye.
  //
  // Detección de característica explícita (`globalThis.ResizeObserver`, en
  // vez del identificador global bare -- `globalThis` es el único global
  // "genérico" ya declarado en `eslint.config.js`, fuera de las rutas
  // permitidas de esta tarea, así que se evita depender de que `ResizeObserver`
  // esté también registrado ahí): jsdom (entorno de test, ver
  // canvas-connections.spec.js) no implementa `ResizeObserver` -- sin esta
  // guarda, instanciarlo lanzaría en cada test. Degradar en silencio a "sin
  // observer" en ese entorno es seguro porque jsdom nunca calcula layout real
  // de todos modos (ver comentario de cabecera "TSK-0073":
  // `getBoundingClientRect` ya devuelve ceros de forma constante ahí, con o
  // sin `ResizeObserver`); los tests existentes ya fuerzan el recálculo
  // explícitamente vía `forzarRecalculo` (reordenar dos veces), que sigue
  // funcionando exactamente igual.
  // TSK-0204 (defecto de QA manual post-TSK-0191..0196): la MISMA carrera de
  // layout que TSK-0074 describe para `actualizarConexiones()` afecta también
  // a la capa interactiva de flechas (`renderizarCapaInteractiva()`, puertos y
  // asas de `.canvas-port`/`.canvas-arrow-handle`). Confirmado con
  // instrumentación en navegador real (Playwright/Chromium, no jsdom):
  // `puntoDePuerto` -> `elemento.getBoundingClientRect()` devuelve TODO CEROS
  // (rect/base/local = 0, `isConnected === false`) en la ÚNICA pasada que
  // `renderizarCapaInteractiva()` recibía -- la síncrona de `renderizar()`
  // (más abajo), ejecutada mientras `superficie` es un subárbol DESCONECTADO,
  // igual que las líneas SVG. Pero, a diferencia de `actualizarConexiones()`,
  // la capa interactiva NO se volvía a pintar tras la conexión real al DOM:
  // este `ResizeObserver` solo repetía `actualizarConexiones()`, dejando TODOS
  // los puertos/asas congelados en `left:0px; top:0px` (inutilizando arrastrar/
  // reconectar/borrar flechas). La aritmética de `puntoDePuerto` es correcta
  // (idéntica a `puntoCentral`, ya validada); el fallo era el disparador que
  // faltaba. Se añade `renderizarCapaInteractiva()` al MISMO ciclo de
  // revalidación que ya usa `actualizarConexiones()` -- reposiciona los puertos
  // leyendo el `grafoInteractivo`/`nodosInteractivos` vigentes (NO recompila el
  // grafo ni toca `origenConexionTeclado`, así que no interrumpe una conexión
  // por teclado en curso). No hay riesgo de bucle: el observer mira el
  // border-box de `superficie`, y los puertos son hijos posicionados en
  // absoluto que no alteran ese tamaño (mismo razonamiento por el que
  // `dimensionarCapaConexiones` tampoco lo dispara).
  const ResizeObserverGlobal = /** @type {any} */ (globalThis).ResizeObserver;
  const observadorTamanoSuperficie =
    typeof ResizeObserverGlobal === 'undefined'
      ? null
      : new ResizeObserverGlobal(() => {
          // TSK-0204 (foco, defecto real señalado por accessibility-reviewer):
          // `renderizarCapaInteractiva()` hace `capaPuertos.replaceChildren()`,
          // destruyendo y recreando TODOS los `.canvas-port`/`.canvas-arrow-handle`.
          // Si el observer dispara (resize/zoom/reflow) mientras uno de ellos
          // tiene el foco, éste caería a `<body>`, violando el invariante
          // "nunca `<body>`" de este módulo. Se replica el patrón ya aprobado en
          // el resto del archivo (`manejarTeclaEnPuerto`/
          // `eliminarArcoDeExtremoInteractivo`): capturar el extremo/asa
          // enfocado ANTES y restaurarlo sobre el elemento equivalente recién
          // pintado DESPUÉS. Este re-render NO borra puertos ni arcos (solo los
          // reposiciona por el nuevo layout, sin tocar `grafoInteractivo`), así
          // que el elemento equivalente siempre existe.
          const activo = /** @type {HTMLElement | null} */ (document.activeElement);
          const puertoEnfocado =
            activo && activo.classList.contains('canvas-port')
              ? { nodeId: activo.dataset.portNode, puerto: activo.dataset.portValue }
              : null;
          const asaEnfocada =
            activo && activo.classList.contains('canvas-arrow-handle') ? activo.dataset.arcId : null;
          // TSK-0205: tercer tipo focable de esta capa (asa de EXTREMO), con
          // identidad `arcId` + `arcEndpoint` -- se captura y restaura igual
          // que el puerto/asa central para no dejar caer el foco a `<body>`.
          const asaExtremoEnfocada =
            activo && activo.classList.contains('canvas-arrow-endpoint-handle')
              ? { arcId: activo.dataset.arcId, extremo: activo.dataset.arcEndpoint }
              : null;

          actualizarConexiones();
          renderizarCapaInteractiva();

          if (puertoEnfocado && puertoEnfocado.nodeId && puertoEnfocado.puerto) {
            enfocarPuertoInteractivo(puertoEnfocado.nodeId, puertoEnfocado.puerto);
          } else if (asaEnfocada) {
            enfocarAsaInteractiva(asaEnfocada);
          } else if (asaExtremoEnfocada && asaExtremoEnfocada.arcId && asaExtremoEnfocada.extremo) {
            enfocarAsaExtremoInteractiva(
              asaExtremoEnfocada.arcId,
              /** @type {'origen' | 'destino'} */ (asaExtremoEnfocada.extremo)
            );
          }
        });
  if (observadorTamanoSuperficie) observadorTamanoSuperficie.observe(superficie);

  renderizar();

  function destruir() {
    superficie.removeEventListener('dragover', /** @type {any} */ (manejarDragOver));
    superficie.removeEventListener('drop', /** @type {any} */ (manejarDrop));
    // TSK-0073: ya no hay listener de `scroll` que desmontar (ver comentario
    // junto a `renderizar()`/cabecera del archivo).
    // TSK-0074: se desconecta el `ResizeObserver` para no dejar un observer
    // huérfano referenciando un elemento potencialmente ya desmontado (mismo
    // patrón de limpieza que el resto de listeners de esta función).
    if (observadorTamanoSuperficie) observadorTamanoSuperficie.disconnect();
    papelera.removeEventListener('click', manejarActivarPapelera);
    botonDeshacer.removeEventListener('click', deshacerEliminacion);
    if (detenerArrastreActivo) {
      detenerArrastreActivo();
    }
    // Los listeners de cada bloque (keydown/mousedown/focus) se
    // descartan junto al propio nodo DOM al reemplazarse con
    // `replaceChildren`/al desmontar `elemento`: no hay referencia colgante
    // que limpiar aparte.
  }

  return {
    elemento: seccion,
    colocarBloque,
    obtenerPrograma: () => programaActual,
    // TSK-0195 (DEC-069 punto 6/DEC-070): la IR "autoritativa" compilada
    // desde el grafo interactivo EN ESTE INSTANTE (no una copia obsoleta):
    // `editor-controller.js` la usa para decidir si "Ejecutar"/"Corrección"
    // pueden llamar al backend (gate = éxito de `compilarGrafoAIr`, nunca
    // `validarGrafoArcos`, ver DEC-070).
    obtenerResultadoCompilacionGrafo: () => resultadoCompilacionGrafo,
    // Texto vigente de la live region, para que `editor-controller.js` pueda
    // conservarlo a través de la destrucción/reconstrucción de esta vista
    // (ver el comentario junto a `estado.textContent`, más arriba).
    mensajeEstado: () => estado.textContent ?? '',
    // DEC-180: el borrado pendiente y su foco, para que el controlador los
    // pase a la vista que reconstruye (ver `eliminacionHeredada`, más arriba).
    eliminacionPendiente: () => ultimoEliminado,
    deshacerEliminacionTieneFoco: () => !botonDeshacer.hidden && document.activeElement === botonDeshacer,
    enfocarDeshacerEliminacion: () => {
      if (botonDeshacer.hidden) return false;
      botonDeshacer.focus({ preventScroll: true });
      return true;
    },
    destruir,
  };
}
